// components/dealerCommands.ts
import { expansionChartYears } from '#lib/leasing/expansionChartYears';
import { isStudioProjectType } from '#lib/studio-project-view';
import {
  type ServerSourcingMandate,
  type CNCCSector,
  CNCC_SECTORS, UiCommandDefinition, ChatSession, PlanningTracePayload, InventoryMutationOperation, KernelHandle, WorkingMemory, ContextAnchor, LeasingActionType, EntityType, MUTATION_ENTITY_TYPES, ActiveEntityFocus, GeneratedDocumentType, CommandTargetEntityType, AgentObservedError, InventoryEntityType, SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH } from '#types/types';
import { createWebSearchHandler } from '#lib/webSearchCommand';
import type { RefObject } from 'react';
import { isInternalAssetUrl } from '#lib/azure-shared';
import { mergeSourcingCategoryFilters, readFlag } from '#lib/leasing/sourcingStrategyParams';
import { parseAspectRatio, runStudioGeneration } from '#lib/studio/runStudioGeneration';
import { toast } from 'sonner';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { ENRICH_DIMS, buildEnrichNote, type EnrichBrandLike } from '#lib/leasing/enrichNote';
import { markEnriching } from '#lib/leasing/enrichingDims';
import {
  applySourcingPresetToShortlist,
  parseSourcingPreset,
  serializeSourcingPreset,
  SOURCING_PRESET_DOCTRINE_KEY,
  DEFAULT_PARAMS_SET_NAME,
} from '#lib/leasing/sourcingPreset';
import { isSourceAgentSession } from '#lib/leasing/sourceAgentMode';
import { userAskedForContacts, userAskedToLaunch } from '#lib/leasing/unprompted-turns';
import { commitToMandate, createMandate, MANDATE_DEFAULT_TARGET_SCORE, mandateAllows } from '#lib/leasing/sourcingMandate';
import { creditsForBrands } from '#lib/leasing/sourceBudget';
import { resolveOpenCampaignScope } from '#lib/leasing/source-view-guards';
import { leadBelongsToCampaign } from '#lib/leasing/campaignMembership';
import {
  areSubjectFocusesEquivalent,
  buildSessionTableFocusEntityId,
  isEntityFocusSubject,
  normalizeSubjectFocus,
  resolveSessionTableFocusView,
  SESSION_TABLE_FOCUS_VIEW_TO_ENTITY_TYPE,
} from '#lib/leasing/session-focus';
import {
  deriveAssetBaseline,
  deriveDefaultParams,
  deriveScenarioOutcomes,
  mergeRentRollEdits,
  fmtEuroM,
  type ScenarioParams,
  type TenantMix,
  type Sustainability,
  type RentRollEdit,
} from './asset-management/scenario-engine';
import {
  getAssetModelParams,
  patchAssetModelParams,
  setAssetModelParams,
  upsertAssetCustomSheet,
  removeAssetCustomSheet,
  writeAssetCustomSheetCells,
  getAssetRentRollEdits,
  applyAssetRentRollEdits,
} from './asset-management/assetModelStore';
import { useTablesStore, canEditTable, isStructuralSheet, type CellStyle } from './tables/tablesStore';
import { loadSharedTables, saveSharedTable } from './tables/tablesShared';
import { canAccessAnalyst } from '#lib/access/analystAccess';
import { computeWorkbook } from './asset-management/workbookEngine';
import { keyToA1, parseA1Range, parseCellPatch } from './tables/cellRef';
import { computeTableSlice, loadSsf } from './tables/analystTableSlice';
import { buildSourceSlice, type SourceCardKind, type SourceCardScope } from './source/sourceTableSlice';
import { resolveOpenSourceShortlist } from './source/sourceShortlistContext';
import { resolveSourceFocusedBrandId } from '#lib/leasing/sourceFocusedBrand';
import { AGENT_UNCREATABLE_ENTITY_TYPES } from '#lib/user-activities';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';

// --- Analyst (standalone Tables tool) helpers -------------------------------
// Analyst commands act on the table the user has OPEN in the Analyst tool,
// resolved from the client-side tables store (no entity focus needed).
const assetManagementPermissionLock = (
  kernelHandle: KernelHandle | null | undefined,
): { isLocked: boolean; unlockHint: string } | null => {
  const canAccess = kernelHandle?.getUserContext?.()?.permissions?.canAccessAssetManagement;
  if (canAccess) return null;
  return {
    isLocked: true,
    unlockHint: 'This tool requires asset management access for your account.',
  };
};

const analystPermissionLock = (kernelHandle: KernelHandle | null | undefined) =>
  canAccessAnalyst(kernelHandle?.getUserContext?.()) ? null : {
    isLocked: true, unlockHint: 'This tool requires Analyst or leasing access for your account.',
  };

const analystLock = (kernelHandle: KernelHandle | null | undefined): { isLocked: boolean; unlockHint: string } => {
  const permissionLock = analystPermissionLock(kernelHandle);
  if (permissionLock) return permissionLock;

  const st = useTablesStore.getState();
  if (!st.activeDocId || !st.docs[st.activeDocId]) {
    return { isLocked: true, unlockHint: 'Open a table in the Analyst tool first (or use ANALYST_CREATE_TABLE to start one).' };
  }
  return { isLocked: false, unlockHint: '' };
};
const getActiveTable = (): { id: string; name: string } | null => {
  const st = useTablesStore.getState();
  const id = st.activeDocId;
  if (!id) return null;
  const doc = st.docs[id];
  return doc ? { id, name: doc.name } : null;
};
// If this command is running inside a MASTER turn, return that master session id,
// else null. A master session builds its model headlessly and must target its OWN
// bound table, not the single global activeDocId that every other master session
// would share.
const masterTurnSessionId = (
  kernelHandle: KernelHandle | null | undefined,
  options: { sessionId?: string } | undefined,
): string | null => {
  const sid = typeof options?.sessionId === 'string' ? options.sessionId : '';
  if (!sid) return null;
  const isMaster = kernelHandle?.getInventory?.().ChatSession?.registry?.[sid]?.creationModule === 'master';
  return isMaster ? sid : null;
};
// Resolve the table an ANALYST_* command should target. On a master turn: the doc
// BOUND to that session (or null if it has not built one yet). Everywhere else (the
// Analyst tool / dealer): byte-identical to getActiveTable(), the open doc.
const resolveAgentTable = (
  kernelHandle: KernelHandle | null | undefined,
  options: { sessionId?: string } | undefined,
): { id: string; name: string } | null => {
  const masterSid = masterTurnSessionId(kernelHandle, options);
  if (!masterSid) return getActiveTable();
  const st = useTablesStore.getState();
  const id = st.masterSessionDocs[masterSid];
  if (!id) return null;
  const doc = st.docs[id];
  return doc ? { id, name: doc.name } : null;
};
// On a master turn, bind the doc the agent just created/opened to that session, so
// its later edit commands resolve to it and two master chats never collide.
const bindMasterDocIfMasterTurn = (
  kernelHandle: KernelHandle | null | undefined,
  options: { sessionId?: string } | undefined,
  docId: string,
): void => {
  const masterSid = masterTurnSessionId(kernelHandle, options);
  if (masterSid) useTablesStore.getState().bindMasterSessionDoc(masterSid, docId);
};
const toRows2D = (raw: unknown): string[][] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    if (Array.isArray(row)) return row.map((c) => (c === null || c === undefined ? '' : String(c)));
    if (row === null || row === undefined) return [];
    if (typeof row === 'object') return Object.values(row as Record<string, unknown>).map((c) => (c === null || c === undefined ? '' : String(c)));
    return [String(row)];
  });
};
/**
 * Names the cell entries a write command could not read, appended to the error the runner
 * hands back to the agent. The valid cells of the same patch are already written when this
 * fires, so it says so: the agent is meant to resend these addresses, not the whole patch.
 */
const describeRejectedCells = (rejected: string[]): string => {
  if (rejected.length === 0) return '';
  return ` Unreadable cell address${rejected.length === 1 ? '' : 'es'}, not written: ${rejected.join(', ')}. Resend just those as a single-cell A1 ref (e.g. "B3"); a range or a "Sheet!B3" ref is not a valid target.`;
};

export const dealerCommandTimeEstimations: { [key: string]: number } = {

};

/**
 * "Agents live in projects": when the MASTER builds a Studio project, mirror the exchange into
 * the project's own scoped conversation, the user's originating ask (once, when the
 * conversation is created) and the master's generation prompt to Studio (each step), so
 * opening the project shows its real chat history instead of an empty conversation.
 *
 * Reuses the project's studio-scoped session when one exists (multiple RUN_STUDIO_STEPs share
 * one project); creates it WITHOUT activating it (the operator stays in the master run).
 * Messages are written as durable COMMUNICATION TimelineEvents directly (the pattern of
 * createMessageTimelineEvent) because updateActiveSessionMessages no-ops on sessions absent
 * from the local registry. Best-effort by contract: a mirroring failure never fails the build.
 */
async function mirrorMasterPromptsToProjectConversation(
  k: KernelHandle,
  projectId: string,
  masterSessionId: string,
  masterPrompt: string,
  generation: { modelId: string; workspaceId: string },
): Promise<void> {
  // 1) The project's scoped conversation, if it already exists (same lookup focusEntityUI uses).
  let sessionId = '';
  let isNewSession = false;
  try {
    const res = await fetch(
      `/api/inventory/sessions?creationModule=studio&creationEntityType=Project&creationEntityId=${encodeURIComponent(projectId)}&limit=10`,
      { credentials: 'include' },
    );
    if (res.ok) {
      const json = (await res.json().catch(() => ({}))) as { chatSessions?: Array<{ id?: unknown }> };
      const first = Array.isArray(json?.chatSessions) ? json.chatSessions[0] : undefined;
      if (typeof first?.id === 'string' && first.id.trim()) sessionId = first.id.trim();
    }
  } catch { /* lookup is best-effort; fall through to create */ }

  // 2) Create it if missing, NOT activated, so the operator's view never moves. The
  //    originating master session is stamped into workingMemory so both sessions stay
  //    durably linked (powers the "Open on Master" header link + agent command).
  if (!sessionId) {
    const projectName = (k.getInventory().Project.registry[projectId] as { name?: string } | undefined)?.name;
    const created = await k.createEntity('ChatSession', {
      name: projectName || 'Project',
      creationModule: 'studio',
      creationEntityType: 'Project',
      creationEntityId: projectId,
      workingMemory: { anchors: [], originMasterSessionId: masterSessionId },
    } as never);
    sessionId = typeof (created as { id?: unknown })?.id === 'string' ? ((created as { id: string }).id) : '';
    isNewSession = true;
  } else {
    // Reused session: stamp the link if absent (merge, never clobber existing anchors).
    try {
      await k.syncEntity?.('ChatSession', sessionId);
      const session = k.getInventory().ChatSession.registry[sessionId] as
        | { workingMemory?: { anchors?: unknown[]; originMasterSessionId?: string | null } }
        | undefined;
      if (!session?.workingMemory?.originMasterSessionId) {
        await k.mutateInventory([
          {
            action: 'UPDATE',
            entity: 'ChatSession',
            id: sessionId,
            payload: {
              workingMemory: {
                anchors: Array.isArray(session?.workingMemory?.anchors) ? session.workingMemory.anchors : [],
                originMasterSessionId: masterSessionId,
              },
            },
          } as InventoryMutationOperation,
        ], `Link project session ${sessionId} to master ${masterSessionId}`);
      }
    } catch { /* linking is best-effort */ }
  }
  if (!sessionId) return;

  const operations: InventoryMutationOperation[] = [];

  // 3) The user's originating ask = the last user-role message of the master session.
  //    Written once, when the project conversation is first created.
  if (isNewSession) {
    const masterMessages = k.getChatSessionMessages(masterSessionId);
    const lastUser = [...masterMessages].reverse().find((m) => m.role === 'user');
    if (lastUser?.content) {
      operations.push({
        action: 'CREATE',
        entity: 'TimelineEvent',
        tempId: `timeline-event-${crypto.randomUUID()}`,
        payload: {
          chatSessionId: sessionId,
          category: 'COMMUNICATION',
          eventType: 'USER_MESSAGE',
          targetEntityType: 'Project',
          targetEntityId: projectId,
          payload: {
            role: 'user',
            content: lastUser.content,
            // A second before the telemetry so the exchange reads in order.
            timestamp: new Date(Date.now() - 1000).toISOString(),
            targetEntityType: 'Project',
            targetEntityId: projectId,
          },
        },
      } as InventoryMutationOperation);
    }
  }

  // 4) CLASSIC Studio telemetry for the step, the same STUDIO_GENERATION_TASK_SPAWNED
  //    event the director emits, so the project chat renders "<Model> launched on
  //    workspace <n>" with the prompt in the standard prompt container (useSessionMessages
  //    formatGenerationSpawnContent), not a bare assistant message.
  const workspaceName = generation.workspaceId
    ? ((k.getWorkspace?.(generation.workspaceId) as { name?: string } | undefined)?.name ?? null)
    : null;
  operations.push({
    action: 'CREATE',
    entity: 'TimelineEvent',
    tempId: `timeline-event-generation-spawn-${crypto.randomUUID()}`,
    payload: {
      chatSessionId: sessionId,
      targetEntityType: 'Project',
      targetEntityId: projectId,
      category: 'MUTATION',
      eventType: 'STUDIO_GENERATION_TASK_SPAWNED',
      payload: {
        projectId,
        workspaceId: generation.workspaceId || null,
        workspaceName,
        taskType: 'diffuse',
        model: generation.modelId,
        prompt: masterPrompt,
        createdAt: new Date().toISOString(),
      },
    },
  } as InventoryMutationOperation);

  await k.mutateInventory(operations, `Mirror master prompts to project ${projectId} conversation`);
}

const LEASING_MUTATION_SUPPORTED_ENTITIES = new Set<string>(MUTATION_ENTITY_TYPES);

// SAID WHERE IT APPLIES, NOT ONLY IN THE DOCTRINE. Every command that produces or re-orders a
// shortlist invalidates the verdict that came before it, and the moment a list moves is the moment
// the agent is holding the result and can act. The standing rule lives in the Dealer chapter
// (leasingKnowledgeBase); this is the reminder at the point of action, the same way
// EXTEND_SHORTLIST already hands back a tellTheUser line rather than trusting recall.
const SHORTLIST_VERDICT_NEXT_STEP =
  'The ranking has changed, so any earlier verdict on this list is stale. Read `verdictRows` below - it IS this campaign\'s list, read from the records a moment ago, and it is the only list you may name from. Say in your reply which rows are worth pursuing and name ONE winner with the reason it wins - a sentence, not a write: setting a lead INTERESTING is the user\'s call (/interesting), never yours. The winner is a judgement and is often not row 1: the score does not know about cannibalisation, failing accounts, a stalled expansion or an unreachable tenant. If you pick against the order, say what disqualifies the row above it. Quote scores exactly as they appear in `verdictRows`, never from memory, and never name a brand that is not in it.';

/**
 * The verdict instruction, handed the campaign's ACTUAL rows.
 *
 * WHY THE ROWS TRAVEL WITH THE INSTRUCTION. "Read the top rows" sent the agent to its own context,
 * which by the end of a run is minutes stale and holds leads from other campaigns. Measured on
 * 2026-08-15: a run closed by naming Devred 1902 as a remaining candidate, a lead belonging to a
 * DIFFERENT campaign that the agent had touched two minutes earlier, and quoted scores of 70 and 63
 * for brands the records had at 60 and 65. Both campaigns were on one screen, disagreeing.
 *
 * Membership, not ownership: a brand re-scored by a later run still belongs to this campaign's
 * receipt (lib/leasing/campaignMembership.ts), so it must still appear in its verdict.
 *
 * Deliberately NOT a fetch. The kernel registry is what the table on screen is drawn from, so the
 * agent and the user read the same numbers by construction; going to the network here would let the
 * two drift apart again, which is the whole defect.
 */
const shortlistVerdictResult = (
  kernel: KernelHandle | null | undefined,
  campaignId: string,
): { nextStep: string; verdictRows?: Array<{ leadId: string; brand: string; score: number | null; status: string }> } => {
  const inventory = kernel?.getInventory?.();
  if (!inventory || !campaignId) return { nextStep: SHORTLIST_VERDICT_NEXT_STEP };

  type Row = { id?: string; brandId?: string; brandName?: string; status?: string };
  const leads = (inventory.Lead?.registry ?? {}) as Record<
    string,
    Row & { fitScore?: number | null; lastCampaignId?: string; campaignIds?: string[] }
  >;
  // A PORTFOLIO run's rows are PortfolioFit, not Lead, and they carry a plain `campaignId` column
  // rather than the membership list. Reading only the Lead registry here returned an empty list on
  // every portfolio verdict, which is not a crash and would never have been noticed: the agent
  // would simply have gone back to naming brands from memory, which is the defect being fixed.
  const fits = (inventory.PortfolioFit?.registry ?? {}) as Record<
    string,
    Row & { score?: number | null; campaignId?: string }
  >;

  const brands = inventory.Brand?.registry as Record<string, { name?: string }> | undefined;
  const nameOf = (row: Row) => row.brandName || (row.brandId ? brands?.[row.brandId]?.name : '') || 'unnamed';
  // null means never scored, and it has to stay null: coercing it to 0 would invite the agent to
  // describe an unscored brand as a bad one.
  const scoreOf = (value: unknown) => (typeof value === 'number' ? value : null);

  const rows = [
    ...Object.values(leads)
      .filter((lead) => leadBelongsToCampaign(lead, campaignId))
      .map((lead) => ({ leadId: String(lead.id ?? ''), brand: nameOf(lead), score: scoreOf(lead.fitScore), status: String(lead.status ?? '') })),
    ...Object.values(fits)
      .filter((fit) => fit.campaignId === campaignId)
      .map((fit) => ({ leadId: String(fit.id ?? ''), brand: nameOf(fit), score: scoreOf(fit.score), status: String(fit.status ?? '') })),
  ]
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
    .slice(0, 15);

  return rows.length > 0 ? { nextStep: SHORTLIST_VERDICT_NEXT_STEP, verdictRows: rows } : { nextStep: SHORTLIST_VERDICT_NEXT_STEP };
};
const LEASING_MUTATION_SUPPORTED_ACTIONS = new Set(['CREATE', 'UPDATE', 'DELETE']);
type EvaluateLockContext = Parameters<NonNullable<UiCommandDefinition['evaluateLockState']>>[0];
type DealerCommandDefinition = UiCommandDefinition & { targetEntityType: CommandTargetEntityType };
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const coerceGeneratedDocumentType = (value: unknown): GeneratedDocumentType | undefined => {
  if (value === 'INCENTIVE' || value === 'LOI') return value;
  return undefined;
};

const resolveDocumentTypeForGeneration = (
  kernelRef: { current: KernelHandle | null },
  documentType: unknown,
  templateId: unknown
): GeneratedDocumentType | undefined => {
  const directType = coerceGeneratedDocumentType(documentType);
  if (directType) return directType;

  if (typeof templateId !== 'string' || templateId.trim().length === 0) return undefined;
  const inventory = kernelRef.current?.getInventory?.() as any;
  return coerceGeneratedDocumentType(inventory?.DocumentTemplate?.registry?.[templateId]?.documentType);
};

const resolveDocumentActionType = (documentType?: GeneratedDocumentType | null): LeasingActionType | undefined => {
  if (documentType === 'INCENTIVE') return 'REDACT_INCENTIVE_LETTER';
  if (documentType === 'LOI') return 'REDACT_LOI';
  return undefined;
};

const resolveAnchorEntityTypeFromQueryEntity = (entity: unknown): string | null => {
  if (typeof entity !== 'string') return null;
  const normalized = entity.trim().toUpperCase().replace(/\s+/g, '_');
  switch (normalized) {
    case 'PROPERTY':
      return 'Property';
    case 'UNIT':
      return 'Unit';
    case 'BRAND':
      return 'Brand';
    case 'LEAD':
      return 'Lead';
    case 'SOURCING_CAMPAIGN':
      return 'SourcingCampaign';
    case 'MISSION':
      return 'Mission';
    case 'DEAL':
      return 'Deal';
    case 'DOCUMENT_TEMPLATE':
      return 'DocumentTemplate';
    case 'GENERATED_DOCUMENT':
      return 'GeneratedDocument';
    case 'MARKET_SIGNAL':
      return 'MarketSignal';
    case 'PORTFOLIO':
      return 'Portfolio';
    case 'LEGAL_ENTITY':
      return 'LegalEntity';
    case 'PROJECT':
      return 'Project';
    default:
      return null;
  }
};

const resolvePropertyIdFromActiveSubject = (
  kernelHandle: KernelHandle,
  activeSubject: ActiveEntityFocus | null | undefined
): string | null => {
  if (!isEntityFocusSubject(activeSubject)) return null;

  if (activeSubject.entityType === 'Property') {
    return activeSubject.entityId;
  }

  if (activeSubject.entityType === 'Unit') {
    return kernelHandle.getInventory().Unit.registry[activeSubject.entityId]?.propertyId || null;
  }

  if (activeSubject.entityType === 'Deal') {
    const unitId = kernelHandle.getInventory().Deal.registry[activeSubject.entityId]?.unitId;
    if (!unitId) return null;
    return kernelHandle.getInventory().Unit.registry[unitId]?.propertyId || null;
  }

  if (activeSubject.entityType === 'Lead') {
    return kernelHandle.getInventory().Lead.registry[activeSubject.entityId]?.propertyId || null;
  }

  if (activeSubject.entityType === 'SourcingCampaign' || activeSubject.entityType === 'Campaign') {
    // Both names reach the same row, and the unified one carries its coverage as an array.
    return resolveCampaignPropertyId(readCampaignRow(kernelHandle.getInventory(), activeSubject.entityId)) || null;
  }

  return null;
};

/** The unit the session is focused on, when its subject is a unit; null otherwise. */
const resolveUnitIdFromSessionContext = (
  kernelRef: { current: KernelHandle | null },
  sessionId: string | null | undefined
): string | null => {
  if (!sessionId || !kernelRef.current) return null;
  const subject = kernelRef.current.getInventory().ChatSession.registry[sessionId]?.activeSubject ?? null;
  return isEntityFocusSubject(subject) && subject.entityType === 'Unit' && typeof subject.entityId === 'string' && subject.entityId.trim()
    ? subject.entityId.trim()
    : null;
};

const resolvePropertyIdFromSessionContext = (
  kernelRef: { current: KernelHandle | null },
  sessionId: string | null | undefined
): string | null => {
  if (!sessionId) return null;
  const k = kernelRef.current;
  if (!k) return null;
  const session = k.getInventory().ChatSession.registry[sessionId];
  const fromSubject = resolvePropertyIdFromActiveSubject(k, session?.activeSubject ?? null);
  if (fromSubject) return fromSubject;
  // Same "open shortlist wins" resolution the turn assembler applies in kernel/useAgents.ts. The
  // session subject stays on the org's default portfolio while the user reads one asset's list, so
  // without this an unlocked command would still fail at execution on "propertyId is required" the
  // moment the agent trusted its focus instead of repeating the id. Guarded on a Source session
  // because `sourceSelectedCampaignId` is global kernel state that outlives leaving Source.
  // The session's own campaign binding outranks the globally open one, mirroring the turn
  // assembler: the prompt was built from the bound campaign's shortlist, so the command must
  // execute against the same one even if the user has since opened another on screen.
  const boundCampaignId =
    isEntityFocusSubject(session?.activeSubject) &&
    typeof session?.activeSubject?.campaignId === 'string'
      ? session.activeSubject.campaignId
      : null;
  return isSourceAgentSession(session)
    ? resolveOpenSourceShortlist(k, boundCampaignId)?.propertyId ?? null
    : null;
};

/**
 * The portfolio a portfolio-wide command should act on.
 *
 * A Portfolio subject is the direct answer; an asset-shaped subject (Property / Unit / Deal / Lead)
 * still belongs to a portfolio, and in Source that is the one on screen, so it resolves through the
 * asset. Without this the agent had to be handed an id it was already looking at.
 */
const resolvePortfolioIdFromSessionContext = (
  kernelRef: { current: KernelHandle | null },
  sessionId: string | null | undefined
): string | null => {
  const k = kernelRef.current;
  if (!k) return null;
  const session = sessionId ? k.getInventory().ChatSession.registry[sessionId] : null;
  const subject = session?.activeSubject ?? null;
  if (isEntityFocusSubject(subject) && subject.entityType === 'Portfolio') {
    return subject.entityId;
  }
  const propertyId = resolvePropertyIdFromSessionContext(kernelRef, sessionId);
  if (propertyId) {
    const portfolioId = k.getInventory().Property.registry[propertyId]?.portfolioId;
    if (typeof portfolioId === 'string' && portfolioId.length > 0) return portfolioId;
  }
  // Nothing narrower to go on. The Source dashboard focuses the Portfolio itself as the session
  // subject, so the first branch already covers "the user is looking at a portfolio"; anything
  // left here genuinely needs the id spelled out rather than guessed.
  return null;
};

const resolveSessionProjectContext = (
  kernelRef: { current: KernelHandle | null },
  preferredSessionId?: string | null
): { sessionId: string | null; session: ChatSession | null; projectId: string | null } => {
  const k = kernelRef.current;
  if (!k) {
    return { sessionId: null, session: null, projectId: null };
  }

  const normalizedPreferredSessionId =
    typeof preferredSessionId === 'string' && preferredSessionId.trim().length > 0
      ? preferredSessionId.trim()
      : null;
  const activeSessionId =
    typeof k.getActiveChatSessionId === 'function'
      ? k.getActiveChatSessionId()
      : null;
  const normalizedActiveSessionId =
    typeof activeSessionId === 'string' && activeSessionId.trim().length > 0
      ? activeSessionId.trim()
      : null;

  const sessionId = normalizedPreferredSessionId ?? normalizedActiveSessionId;
  if (!sessionId) {
    return { sessionId: null, session: null, projectId: null };
  }

  const session = k.getInventory().ChatSession.registry[sessionId] ?? null;
  const projectIdFromKernel =
    typeof k.getLastProjectId === 'function'
      ? k.getLastProjectId(sessionId)
      : null;
  const normalizedProjectIdFromKernel =
    typeof projectIdFromKernel === 'string' && projectIdFromKernel.trim().length > 0
      ? projectIdFromKernel.trim()
      : null;
  const projectIdFromSession = session ? resolveProjectIdFromChatSessionContext(session) : null;

  return {
    sessionId,
    session,
    projectId: normalizedProjectIdFromKernel ?? projectIdFromSession ?? null,
  };
};

const resolveFocusedPropertyId = (
  context: EvaluateLockContext
): { propertyId: string | null; lockHint: string | null } => {
  const k = context.kernel.current;
  if (!k) return { propertyId: null, lockHint: 'Inventory is not available yet.' };

  const activePropertyId = resolvePropertyIdFromActiveSubject(k, context.activeSubject ?? null);
  if (activePropertyId) {
    return { propertyId: activePropertyId, lockHint: null };
  }

  const propertyAnchor = context.anchors.find(
    (a) => a.entityType === 'Property' && typeof a.entityId === 'string' && a.entityId.trim().length > 0
  );
  if (propertyAnchor) {
    return { propertyId: propertyAnchor.entityId, lockHint: null };
  }

  for (const anchor of context.anchors) {
    if (typeof anchor.entityId !== 'string' || anchor.entityId.trim().length === 0) {
      continue;
    }
    const anchorPropertyId = resolvePropertyIdFromActiveSubject(k, {
      entityType: anchor.entityType as EntityType,
      entityId: anchor.entityId,
    });
    if (anchorPropertyId) {
      return { propertyId: anchorPropertyId, lockHint: null };
    }
  }

  return { propertyId: null, lockHint: 'Focus a Property, Unit, Deal, Lead, or Campaign first.' };
};

// --- Cognitive Desk Helpers ---

const updateSessionAnchors = (
  kernel: KernelHandle,
  sessionId: string,
  newAnchors: ContextAnchor[]
) => {
  const session = kernel.getInventory().ChatSession.registry[sessionId];
  if (!session) return;

  const currentMemory: WorkingMemory = session.workingMemory || { anchors: [] };
  
  // Merge logic: Update existing, append new
  const anchorMap = new Map(currentMemory.anchors.map(a => [`${a.entityType}:${a.entityId}`, a]));
  
  newAnchors.forEach(newAnchor => {
    const key = `${newAnchor.entityType}:${newAnchor.entityId}`;
    const existing = anchorMap.get(key);
    
    if (existing) {
      const existingMass = existing.mass;
      const newMass = newAnchor.mass;
      if (newMass <= 0) {
        anchorMap.delete(key);
        return;
      }
      const mergedMass = Math.max(existingMass, newMass);
      
      anchorMap.set(key, {
        ...existing,
        mass: mergedMass,
        provenance: newAnchor.provenance ?? existing.provenance,
        ephemeralData: newAnchor.ephemeralData ?? existing.ephemeralData,
        lastAccessedAt: Date.now()
      });
    } else {
      if (newAnchor.mass <= 0) {
        return;
      }
      anchorMap.set(key, newAnchor);
    }
  });

  const updatedAnchors = Array.from(anchorMap.values());
  const newMemory: WorkingMemory = { ...currentMemory, anchors: updatedAnchors };

  kernel.mergeEntityKernelState('ChatSession', sessionId, { workingMemory: newMemory });
  kernel.markChatSessionDirty?.(sessionId);
};


/** Names models reach for when they mean "payload". See the auto-fix in the normalizer below. */
const PAYLOAD_ALIAS_KEYS = ['data', 'fields', 'values', 'set', 'updates', 'changes', 'attributes'] as const;

function normalizeLeasingMutationOperations(rawOperations: unknown): InventoryMutationOperation[] {
  if (!Array.isArray(rawOperations) || rawOperations.length === 0) {
    throw new Error('MUTATE: "operations" must be a non-empty array of mutation definitions.');
  }

  return rawOperations.map((operation, index) => {
    if (!isRecord(operation)) {
      throw new Error(`MUTATE: Operation at index ${index} must be an object.`);
    }

    const candidate = operation;
    const rawAction = typeof candidate.action === 'string' ? candidate.action.trim() : '';
    if (!rawAction) {
      throw new Error(`MUTATE: Operation at index ${index} is missing an "action".`);
    }
    const normalizedAction = rawAction.toUpperCase() as InventoryMutationOperation['action'];
    if (!LEASING_MUTATION_SUPPORTED_ACTIONS.has(normalizedAction)) {
      throw new Error(
        `MUTATE: Unsupported action "${candidate.action}" at index ${index}. Use CREATE, UPDATE, or DELETE.`,
      );
    }

    const rawEntity = typeof candidate.entity === 'string' ? candidate.entity.trim() : '';
    if (!rawEntity) {
      throw new Error(`MUTATE: Operation at index ${index} is missing an "entity".`);
    }
    const normalizedEntityString =
      rawEntity.length === 0
        ? rawEntity
        : `${rawEntity.charAt(0).toUpperCase()}${rawEntity.slice(1).toLowerCase()}`;
    const normalizedEntity = normalizedEntityString as InventoryMutationOperation['entity'];
    if (!LEASING_MUTATION_SUPPORTED_ENTITIES.has(normalizedEntity)) {
      throw new Error(
        `MUTATE: Unsupported entity "${candidate.entity}" at index ${index}.`,
      );
    }

    // NOBODY ASKED FOR A DEAL. On 2026-08-16, after scoring a shortlist, the agent created a draft
    // deal, opened its page, then went looking for a Letter of Intent template to render for it.
    // Withholding commands could not stop that: MUTATE is generic, so the entity is a parameter and
    // "create a Deal" needs no deal-specific command to exist. The refusal is here, at the one place
    // every agent write passes through, and it is worded for the user, because the agent reads it
    // back to them.
    if (normalizedAction === 'CREATE' && AGENT_UNCREATABLE_ENTITY_TYPES.has(normalizedEntity)) {
      throw new Error(
        `MUTATE: refused. Creating a ${normalizedEntity} is a commercial decision, not a step in a ` +
          'sourcing pass. Deliver the shortlist verdict and stop. If the user wants a deal, they ' +
          'open one themselves.',
      );
    }

    if (candidate.tempId !== undefined && typeof candidate.tempId !== 'string') {
      throw new Error(`MUTATE: tempId must be a string when provided (index ${index}).`);
    }

    // --- AUTO-FIX: Hoist ID from payload if missing at root ---
    // Agents often mistake { id: "..." } as part of the payload for updates.
    // We detect this and move it to the root to prevent validation errors.
    let finalId = candidate.id;
    // Same auto-fix, one level up: models routinely name the payload after the ORM they know
    // ("data" for Prisma, "fields", "set", "values", "updates", "changes", "attributes") instead of
    // "payload". The operation was otherwise perfectly well formed, so rejecting it with "UPDATE
    // operations require a payload object" cost the user a whole failed turn plus a retry before
    // the first proposal could even be drawn. Accept the alias and carry on; the payload itself is
    // still validated downstream exactly as before, so nothing is loosened.
    const finalPayload = isRecord(candidate.payload)
      ? candidate.payload
      : PAYLOAD_ALIAS_KEYS.map((key) => candidate[key]).find(isRecord);
    const payloadTempId =
      finalPayload && typeof finalPayload.tempId === 'string' && finalPayload.tempId.trim().length > 0
        ? finalPayload.tempId.trim()
        : undefined;
    const finalTempId =
      typeof candidate.tempId === 'string' && candidate.tempId.trim().length > 0
        ? candidate.tempId.trim()
        : payloadTempId;

    if (finalId === undefined && finalPayload && typeof finalPayload === 'object' && 'id' in finalPayload) {
       finalId = finalPayload.id;
    }
    // ---------------------------------------------------------

    if (normalizedAction === 'CREATE') {
      if (!finalPayload || typeof finalPayload !== 'object') {
        throw new Error(`MUTATE: CREATE operations require a payload object (index ${index}).`);
      }
    } else {
      // UPDATE or DELETE
      const isValidReference =
        typeof finalId === 'string' ||
        (isRecord(finalId) && typeof finalId.$ref === 'string');
      
      if (!isValidReference) {
        throw new Error(`MUTATE: ${normalizedAction} operations require an "id" field (index ${index}). It was not found at the root nor in the payload.`);
      }

      if (normalizedAction === 'UPDATE') {
        if (!finalPayload || typeof finalPayload !== 'object') {
          throw new Error(`MUTATE: UPDATE operations require a payload object (index ${index}).`);
        }
      }
    }

    return {
      action: normalizedAction,
      entity: normalizedEntity,
      id: finalId, // Use the resolved/hoisted ID
      payload: finalPayload,
      tempId: finalTempId
    } as InventoryMutationOperation;
  });
}

/**
 * The shortlist a per-campaign command acts on. An explicit `campaignId` wins; otherwise it is the
 * shortlist the user has OPEN in Source, which is the only one they can be pointing at when they
 * say "take store size into account more". A portfolio-fit run is not a shortlist in this sense
 * and is refused out loud, because a command that silently no-ops is one the agent reports as done.
 */
function resolveShortlistTarget(
  params: Record<string, any>,
  kernel: RefObject<KernelHandle | null>,
  commandName: string,
): { campaignId: string } {
  const state = kernel.current?.getState?.();
  const explicit = typeof params?.campaignId === 'string' ? params.campaignId.trim() : '';
  const open = typeof state?.sourceSelectedCampaignId === 'string' ? state.sourceSelectedCampaignId.trim() : '';
  const campaignId = explicit || open;
  // Same question, same answer, whichever id it is: the campaign's own row says what it covered.
  // An id in no registry reads as `asset`, which is what an explicit id from outside Source is.
  const scope = campaignId
    ? resolveOpenCampaignScope({ sourceSelectedCampaignId: campaignId, inventory: state?.inventory })
    : 'portfolio';
  if (!campaignId || scope === 'portfolio') {
    throw new Error(
      `${commandName}: no shortlist in scope. Pass "campaignId" (a sourcing campaign id), or ask the user to open the shortlist first.`,
    );
  }
  return { campaignId };
}

/**
 * Contextual lock for every command that acts on ONE shortlist (its doctrine, or the free re-rank),
 * in the same spirit as the Analyst lock: without a shortlist there is nothing to write a doctrine
 * for and nothing to re-rank, and a campaign id is not something the agent can invent. An explicit
 * `campaignId` still works once the command is offered.
 */
const openShortlistLock = (
  kernel: RefObject<KernelHandle | null>,
): { isLocked: boolean; unlockHint: string } => {
  const state = kernel.current?.getState?.();
  const open = typeof state?.sourceSelectedCampaignId === 'string' ? state.sourceSelectedCampaignId.trim() : '';
  const scope = open ? resolveOpenCampaignScope(state) : 'portfolio';
  if (open && scope === 'asset') return { isLocked: false, unlockHint: '' };
  return {
    isLocked: true,
    unlockHint:
      'Open a shortlist in Source first. This command acts on one campaign, and only a run that covered an asset has one.',
  };
};

/**
 * Bring a brand's CONTACTS into view, and say we are working on them.
 *
 * Every command that answers a contacts question calls this FIRST, before its own await. The user
 * asks "get me the contacts for X" and the contacts tab of that brand has to come to the front
 * immediately: the work takes tens of seconds, and without this the screen does not move at all,
 * so the honest reading is that nothing happened. It shipped in DISCOVER only, and the agent
 * usually answers by VERIFYING or reading instead - which is exactly the case the user hit.
 *
 * `showWorking` raises the flag ContactsTable's existing progress bar reads. Callers that finish
 * in a moment leave it false rather than flashing a bar nobody can see.
 */
const openBrandContactsView = (
  kernelRef: { current: KernelHandle | null },
  brandId: string,
  showWorking = false,
): void => {
  const k = kernelRef.current;
  if (!k || !brandId) return;

  // EXPANDING THE SHORTLIST ROW IS WHAT "OPEN THE DETAIL" MEANS HERE, and it is keyed on the FIT,
  // not the brand. The detail under a shortlist row is a LeadPage rendering BrandShortlistDetail,
  // and the only thing that opens it is `sourceFocusedFitId`. This called focusEntityUI('Brand')
  // instead, which navigates to the Brand page - a different surface entirely - so on the
  // shortlist the user saw nothing move, which is exactly the report.
  const state = k.getState?.();
  const leadRegistry = k.getInventory?.()?.Lead?.registry ?? {};
  const openCampaignId =
    typeof state?.sourceSelectedCampaignId === 'string' ? state.sourceSelectedCampaignId : '';
  const fits = Object.values(leadRegistry) as Array<{
    id?: string;
    brandId?: string;
    lastCampaignId?: string;
    campaignIds?: string[];
  }>;
  // Prefer a row from the campaign that is actually on screen; a brand can appear in several.
  const fit =
    fits.find(
      (row) => row?.brandId === brandId && openCampaignId && leadBelongsToCampaign(row, openCampaignId),
    ) ?? fits.find((row) => row?.brandId === brandId);

  if (fit?.id) {
    k.setState({ sourceFocusedFitId: fit.id });
  } else {
    // No shortlist row for this brand (asked from outside a campaign): the Brand page is then the
    // right place to land, and it renders the same detail with the same tabs.
    k.focusEntityUI?.('Brand', brandId);
  }

  k.setState({
    sourceBrandDetailTab: 'contacts',
    ...(showWorking ? { sourceContactDiscoveryBrandId: brandId } : {}),
  });
};

/** The language the preset document is written in, so a French user does not get an English page. */
/** The user's preset document, or '' when they have none. Shared by the two params commands. */
const fetchSourcingPresetContent = async (): Promise<string> => {
  const response = await fetch(`/api/doctrine?key=${encodeURIComponent(SOURCING_PRESET_DOCTRINE_KEY)}`);
  if (!response.ok) return '';
  const payload = await response.json().catch(() => null);
  return typeof payload?.document?.content === 'string' ? payload.document.content : '';
};

const resolveAgentLanguage = (kernel: RefObject<KernelHandle | null>): 'en' | 'fr' =>
  kernel.current?.getState?.()?.language === 'fr' ? 'fr' : 'en';

/** Read the shortlist's instructions half (the CAMPAIGN doctrine document). */
async function fetchShortlistInstructions(
  campaignId: string,
  commandName: string,
  signal?: AbortSignal,
): Promise<string> {
  const key = `campaign:${campaignId}`;
  const response = await fetch(`/api/doctrine?key=${encodeURIComponent(key)}`, { cache: 'no-store', signal });
  if (!response.ok) {
    throw new Error(
      response.status === 403 || response.status === 404
        ? `${commandName}: no readable doctrine for shortlist "${campaignId}".`
        : `${commandName}: reading the shortlist instructions failed (${response.status}).`,
    );
  }
  const payload = await response.json().catch(() => null);
  return typeof payload?.document?.content === 'string' ? payload.document.content : '';
}

/**
 * DID THE USER ACTUALLY ASK FOR A BILLED RUN THIS TURN?
 *
 * `lastUserTurnPrompt` is the words the user typed for the turn now running. `_executeDealerTurn`
 * rewrites it on every turn and fills it only where `askAssistant` declares that a person typed into
 * the composer, so empty means nobody asked and no run starts.
 *
 * The rule itself lives with the rest of "who is behind this turn", in
 * lib/leasing/unprompted-turns.ts, which also explains why the depth signal cannot replace it.
 */
const assertUserAskedToLaunch = (kernel: RefObject<KernelHandle | null>, commandName: string): void => {
  if (userAskedToLaunch(kernel.current?.getState?.()?.lastUserTurnPrompt)) return;
  throw new Error(
    `${commandName}: refused. Nothing in the user's message this turn asked for a run, and a run ` +
      'spends credits according to the analysis cost, which varies by brand. Set the parameters, show them, and ASK - "shall I launch it?" ' +
      'Launch only in a turn where they said so.',
  );
};

/**
 * A BILLED STEP PAID FOR BY THE USER'S MANDATE (lib/leasing/sourcingMandate.ts), or refused by the
 * guard it stands in for. Committed before the step runs: a budget counts once it is started.
 */
const payFromMandateOr = (
  kernel: RefObject<KernelHandle | null>,
  sessionId: string | null,
  step: { propertyId: string | null; credits: number },
  guard: () => void,
): void => {
  const k = kernel.current;
  const mandate = sessionId ? k?.getState().sourceMandates?.[sessionId] : undefined;
  const verdict = mandateAllows(mandate, step);
  if (!k || !sessionId || !mandate || !verdict.ok) {
    try {
      guard();
    } catch (error) {
      // Say why the mandate did not cover it, so the agent reports the real limit.
      if (mandate && !verdict.ok) throw new Error(`${(error as Error).message} The mandate does not cover it: ${verdict.reason}.`);
      throw error;
    }
    return;
  }
  k.setState({ sourceMandates: { ...k.getState().sourceMandates, [sessionId]: commitToMandate(mandate, step.credits) } });
};

/**
 * DID THE USER ACTUALLY ASK FOR CONTACTS THIS TURN?
 *
 * The twin of the launch guard, and it exists for the same reason: doctrine forbade unprompted
 * contact discovery in capitals and the agent did it anyway, three times, always as the tidy-up
 * step after work that WAS asked for. The words live in lib/leasing/unprompted-turns.ts.
 *
 * The UI's own discover buttons (ContactsTable, DealPage) call the kernel directly rather than
 * this command, so a click is never affected by this.
 */
const assertUserAskedForContacts = (kernel: RefObject<KernelHandle | null>, commandName: string): void => {
  const state = kernel.current?.getState?.();
  if (userAskedForContacts(state?.lastUserTurnPrompt) || userAskedForContacts(state?.lastUserChainPrompt)) return;
  throw new Error(
    `${commandName}: refused. Nothing in the user's message this turn asked for contacts, and a ` +
      'discovery bills per brand. Say in one sentence that you can find the people, and let them ask.',
  );
};

/**
 * NOTHING PAID RUNS IN A TURN NOBODY TYPED. Extend, rerun, enrich and store additions bill real
 * provider and model cost (lib/billing/meteredCharge.ts); the landing verdict and other app-started
 * turns carry no words of the user's, so they are refused here and the agent offers instead
 * (owner, 2026-09-27). A chain the user started keeps its licence across its continuations.
 */
const assertUserStartedThisChain = (kernel: RefObject<KernelHandle | null>, commandName: string): void => {
  const state = kernel.current?.getState?.();
  const typed = [state?.lastUserTurnPrompt, state?.lastUserChainPrompt].some((prompt) => typeof prompt === 'string' && prompt.trim().length > 0);
  if (typed) return;
  throw new Error(
    `${commandName}: refused. This turn was not started by the user, and this command spends credits. ` +
      'Say in one sentence what you would run and why, and let them ask for it.',
  );
};

export const DEALER_COMMAND_DEFINITIONS: Record<string, DealerCommandDefinition> = {
  ASSISTANT_ALERT: {
    action_type: 'ASSISTANT_ALERT',
    traceLabels: { active: 'sending', done: 'sent', unit: 'alert' },
    targetEntityType: 'ABSOLUTE',
    description: `Plays a short, synthesized audio message directly to the user, interrupting any ongoing queued speech.
    Use this when you requires user action/confirmation (e.g., "Please cut the reference image logo").
    This is intended to grab the user attention. The message should be concise.
    `,
    parameters: [
      {
        name: 'text',
        type: 'string',
        description: 'The short text message to be synthesized and played as an alert. Keep it under 300 characters for best effect.',
        required: true,
        example: "Attention required. Please review the current selection."
      }
    ],
    availableInModes: ['director', 'commander'], 
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      const alertText = params.text as string;
      const MAX_ALERT_LENGTH = 500; // Define the limit here

      if (!alertText || typeof alertText !== 'string' || alertText.trim() === "") {
        throw new Error('ASSISTANT_ALERT: "text" parameter is required and must be a non-empty string.');
      }
      if (alertText.length > MAX_ALERT_LENGTH) {
        throw new Error(`ASSISTANT_ALERT: "text" parameter exceeds the character limit of ${MAX_ALERT_LENGTH}. Please provide a more concise message.`);
      }
      if (!kernel.current || typeof kernel.current.playAdhocAlert !== 'function') {
        throw new Error('ASSISTANT_ALERT: The function "playAdhocAlert" is not available on the kernel handle. Ensure it is correctly exposed from TTSManager through Kernel and KernelHandle.');
      }
      try {
        await kernel.current.playAdhocAlert(alertText);
        
      } catch (error: any) {
        console.error('[CommandRunner] ASSISTANT_ALERT: Error calling kernel.playAdhocAlert -', error);
        throw error;
      }
    },
  },

  QUICKSTART_DESIGN_PROJECT: {
    action_type: 'QUICKSTART_DESIGN_PROJECT',
    traceLabels: { active: 'creating', done: 'created', unit: 'project' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Creates a brand-new Design project and immediately opens it in the editor with a prompt (and optional reference images) preloaded into the quickstart flow. Use this to transition from intelligence gathering to visual execution without manual setup.',
    parameters: [
      {
        name: 'prompt',
        type: 'string',
        description: 'The creative brief or instructions that should seed the design quickstart flow.',
        required: true,
      },
      {
        name: 'imageUrls',
        type: 'string[]',
        description:
          'Optional list of already-uploaded internal asset URLs (e.g., /api/assets/<orgId>/<file>) that should be passed into the quickstart flow as reference images.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      if (options?.isHeadless) {
        throw new Error('QUICKSTART_DESIGN_PROJECT: This command is not supported in headless mode.');
      }

      if (!kernel.current?.createProjectAndQuickstart) {
        throw new Error('QUICKSTART_DESIGN_PROJECT: Quickstart functionality is not available on the kernel handle.');
      }

      const promptRaw = typeof params?.prompt === 'string' ? params.prompt : '';
      const prompt = promptRaw.trim();
      if (!prompt) {
        throw new Error('QUICKSTART_DESIGN_PROJECT: "prompt" parameter is required and must be a non-empty string.');
      }

      const rawImageUrls = params?.imageUrls;
      let sanitizedImageUrls: string[] | undefined;

      if (rawImageUrls !== undefined) {
        if (!Array.isArray(rawImageUrls)) {
          throw new Error('QUICKSTART_DESIGN_PROJECT: "imageUrls", if provided, must be an array of strings.');
        }

        const filteredUrls = rawImageUrls
          .map((url) => (typeof url === 'string' ? url.trim() : ''))
          .filter((url): url is string => !!url);

        for (const url of filteredUrls) {
          if (!isInternalAssetUrl(url)) {
            throw new Error(
              `QUICKSTART_DESIGN_PROJECT: Invalid image URL "${url}". Only internal asset URLs ("/api/assets/...") are supported.`,
            );
          }
        }

        if (filteredUrls.length > 0) {
          sanitizedImageUrls = Array.from(new Set(filteredUrls));
        }
      }

      let userId: string | undefined;
      const projectRegistry = kernel.current.getInventory().Project.registry;
      const anyProject = Object.values(projectRegistry).find((project) =>
        typeof project?.userId === 'string' && project.userId.trim().length > 0
      );

      if (anyProject?.userId) {
        userId = anyProject.userId;
      } else {
        const { projectId: activeProjectId } = resolveSessionProjectContext(kernel, options?.sessionId ?? null);
        if (activeProjectId) {
          const activeProjectRow = projectRegistry[activeProjectId];
          if (activeProjectRow?.userId) {
            userId = activeProjectRow.userId;
          }
        }
      }

      if (!userId) {
        const userContextId = kernel.current.getUserContext().dbUserId;
        if (typeof userContextId === 'string' && userContextId.trim().length > 0) {
          userId = userContextId;
        }
      }

      if (!userId) {
        throw new Error('QUICKSTART_DESIGN_PROJECT: Unable to determine current user ID. Cannot create project.');
      }

      try {
        await kernel.current.createProjectAndQuickstart(userId, prompt, sanitizedImageUrls);
        kernel.current?.logUserAction?.('Dealer Quickstarted Design Project', {
          hasReferences: Boolean(sanitizedImageUrls?.length),
          referenceCount: sanitizedImageUrls?.length ?? 0,
        });
      } catch (error) {
        console.error('[CommandRunner] QUICKSTART_DESIGN_PROJECT failed:', error);
        throw error instanceof Error ? error : new Error('Failed to quickstart design project.');
      }
    },
  },

  // MASTER-ONLY orchestration tool. The Master is a SEPARATE construct from the
  // regular in-project chats: it does not do design work itself and it NEVER
  // navigates its own (operator) view. It delegates by creating a project and
  // opening it in a STANDALONE worker tab where the Designer executes, while the
  // operator tab stays on the master run/telemetry view.
  KICKOFF_PROJECT_SESSION: {
    action_type: 'KICKOFF_PROJECT_SESSION',
    traceLabels: { active: 'kicking off', done: 'launched', unit: 'project' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Master orchestration: create a new Studio design project and open it in a STANDALONE worker tab, seeded with a brief (and optional reference images), where the Designer executes it. Your operator view is NOT navigated, you stay here and narrate the run. Returns the projectId and the worker tab URL.',
    parameters: [
      {
        name: 'prompt',
        type: 'string',
        description: 'The creative brief that seeds the project in the worker tab.',
        required: true,
      },
      {
        name: 'projectType',
        type: 'string',
        description: "Optional: 'LIST' for a flat batch of outputs, otherwise a Canvas project (default).",
        required: false,
      },
      {
        name: 'imageUrls',
        type: 'string[]',
        description: 'Optional internal asset URLs (/api/assets/...) passed into the worker tab as reference images.',
        required: false,
      },
    ],
    // Disabled: the Master executes Studio work PROGRAMMATICALLY in-session via
    // RUN_STUDIO_STEP (no worker tab, no navigation). This browser-driving path is
    // retired from the Master's toolset; kept defined only as a reference/escape
    // hatch (available to no mode).
    availableInModes: [],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      if (options?.isHeadless) {
        throw new Error('KICKOFF_PROJECT_SESSION: This command is not supported in headless mode.');
      }
      if (typeof window === 'undefined') {
        throw new Error('KICKOFF_PROJECT_SESSION: a browser window is required to open the worker tab.');
      }
      if (!kernel.current?.createEntity) {
        throw new Error('KICKOFF_PROJECT_SESSION: project creation is not available on the kernel handle.');
      }

      const promptRaw = typeof params?.prompt === 'string' ? params.prompt : '';
      const prompt = promptRaw.trim();
      if (!prompt) {
        throw new Error('KICKOFF_PROJECT_SESSION: "prompt" parameter is required and must be a non-empty string.');
      }

      const projectType = isStudioProjectType(params?.projectType) && params.projectType !== 'FREEFORM' ? params.projectType : undefined;

      const rawImageUrls = params?.imageUrls;
      let sanitizedImageUrls: string[] = [];
      if (Array.isArray(rawImageUrls)) {
        const filteredUrls = rawImageUrls
          .map((url) => (typeof url === 'string' ? url.trim() : ''))
          .filter((url): url is string => !!url);
        for (const url of filteredUrls) {
          if (!isInternalAssetUrl(url)) {
            throw new Error(
              `KICKOFF_PROJECT_SESSION: Invalid image URL "${url}". Only internal asset URLs ("/api/assets/...") are supported.`,
            );
          }
        }
        sanitizedImageUrls = Array.from(new Set(filteredUrls));
      }

      const derivedName = prompt.split(/\s+/).slice(0, 6).join(' ').slice(0, 60);

      // Create ONLY the project (no session activation here, that would navigate
      // the operator tab). The worker tab creates the session + quickstarts.
      const created = await kernel.current.createEntity('Project', {
        name: derivedName,
        ...(projectType ? { projectType } : {}),
      });
      const projectId = created?.id;
      if (!projectId) {
        throw new Error('KICKOFF_PROJECT_SESSION: project creation failed (missing id).');
      }
      await kernel.current.syncEntity?.('Project', projectId).catch(() => undefined);

      // Create the project's studio session server-side (with the Project subject)
      // so the worker tab opens the project editor via the proven ?c activation ,
      // WITHOUT activating anything in the operator tab. The master view is untouched.
      const sessionCreated = await kernel.current.createEntity('ChatSession', {
        name: derivedName,
        creationModule: 'studio',
        creationEntityType: 'Project',
        creationEntityId: projectId,
        activeSubject: { entityType: 'Project', entityId: projectId },
      });
      const workerSessionId = sessionCreated?.id;

      // Open the worker tab on that session. ?mq carries the brief (+ ?mp the
      // project, ?qi refs) so the worker tab runs the first Designer turn to
      // generate, once the session is active.
      const url = new URL(window.location.origin + window.location.pathname);
      if (workerSessionId) {
        url.searchParams.set('c', workerSessionId);
      } else {
        url.searchParams.set('p', projectId);
      }
      url.searchParams.set('mq', prompt);
      url.searchParams.set('mp', projectId);
      if (sanitizedImageUrls.length > 0) {
        url.searchParams.set('qi', sanitizedImageUrls.join('|'));
      }
      // Direct open: this command runs async (after the LLM turn), so the popup
      // blocker may refuse it — openedWorkerTab reports that to the caller.
      const openedWorkerTab = Boolean(window.open(url.toString(), '_blank'));

      kernel.current?.logUserAction?.('Master Kicked Off Design Project', {
        projectId,
        hasReferences: sanitizedImageUrls.length > 0,
        openedWorkerTab,
      });

      params.result = {
        projectId,
        workerTabUrl: url.toString(),
        openedWorkerTab,
        note: openedWorkerTab
          ? 'Worker tab opened; the Designer is executing there.'
          : 'Could not open the worker tab (popup blocked). The project was created, open it from the worker tab URL.',
      };
    },
  },

  // MASTER-ONLY execution tool: a GENERIC Studio image generator/editor that runs
  // headlessly FROM the Master's own session. It creates a project + workspace,
  // optionally loads a source image as a layer, selects the image model with the
  // given prompt, and runs the generation, tagging the resulting downloadable
  // generation card to the MASTER session's timeline (that card IS the telemetry).
  // It NEVER opens a worker tab or navigates the operator into the Studio editor.
  // "Agents live in projects": each step ALSO mirrors the exchange into the
  // project's own scoped conversation (the user's ask + the master's Studio
  // prompt), so opening the project shows its real history, never an empty chat.
  RUN_STUDIO_STEP: {
    action_type: 'RUN_STUDIO_STEP',
    traceLabels: { active: 'generating', done: 'generated', unit: 'image' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Master execution: generate or edit an image from your own session. Always provide a `prompt`. Optionally provide a `sourceImageUrl` (an internal /api/assets URL) to edit/transform that image; omit it to generate a brand-new image from the prompt alone (text-to-image). The result renders as a real, downloadable image card in this run\'s timeline, no worker tab, no navigation. Returns the projectId; pass it back on later calls to reuse one project across the steps of a single run.',
    parameters: [
      {
        name: 'sourceImageUrl',
        type: 'string',
        description: 'Optional. An image to edit/transform, as an internal asset URL (e.g., /api/assets/<orgId>/<file>), loaded as the base layer. OMIT it to generate a brand-new image from the prompt alone (text-to-image).',
        required: false,
      },
      {
        name: 'prompt',
        type: 'string',
        description: 'The exact instruction for the image (English): what to generate, or, when a source image is provided, how to transform it.',
        required: true,
      },
      {
        name: 'projectId',
        type: 'string',
        description: 'Optional. The projectId returned by a previous RUN_STUDIO_STEP in this run. Pass it to reuse the same project (a new workspace is added per step) instead of creating a fresh project.',
        required: false,
      },
      {
        name: 'aspectRatio',
        type: 'string',
        description: 'Optional. Output framing, e.g. "1:1", "16:9", "9:16", "4:3", "3:4", or "auto". Controls the generated image aspect ratio.',
        required: false,
      },
      {
        name: 'model',
        type: 'string',
        description: 'Optional. The image model id to use (from the documented available models). Defaults to the flagship image editor when omitted or unknown.',
        required: false,
      },
    ],
    availableInModes: ['master'],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      if (!k) {
        throw new Error('RUN_STUDIO_STEP: Kernel not available.');
      }

      const masterSessionId =
        typeof options?.sessionId === 'string' && options.sessionId.trim().length > 0
          ? options.sessionId.trim()
          : '';
      if (!masterSessionId) {
        throw new Error('RUN_STUDIO_STEP: a master session id is required to tag the generation.');
      }

      // Source image is OPTIONAL: provide it to edit/rebrand an existing image;
      // omit it for a pure text-to-image generation from the prompt alone.
      const sourceImageUrlRaw = typeof params?.sourceImageUrl === 'string' ? params.sourceImageUrl.trim() : '';
      if (sourceImageUrlRaw && !isInternalAssetUrl(sourceImageUrlRaw)) {
        throw new Error(
          `RUN_STUDIO_STEP: Invalid source image URL "${sourceImageUrlRaw}". Only internal asset URLs ("/api/assets/...") are supported.`,
        );
      }

      const promptRaw = typeof params?.prompt === 'string' ? params.prompt.trim() : '';
      if (!promptRaw) {
        throw new Error('RUN_STUDIO_STEP: "prompt" is required and must be a non-empty string.');
      }

      // Steps 1-5 (project, workspace, source layer, model + prompt, framing, run,
      // output resolution) are the shared Studio recipe: lib/studio/runStudioGeneration.
      // Slides runs the same sequence for its counter-proposal, and the traps this
      // command discovered - the server-provisioned workspace, the in-place Powers -
      // are documented there once instead of in each caller.
      //
      // Master keeps only what is Master's: the run tag that puts the card in THIS
      // session's timeline, the prompt mirroring and the delivered message. The prompt mirroring needs the model id, which the
      // shared step resolves, so it now runs after the generation rather than
      // before it - the mirror is a record of the step, not a precondition for it.
      const aspectRatioRaw = typeof params?.aspectRatio === 'string' ? params.aspectRatio.trim() : '';

      let generation;
      k.setMasterRunChatSessionId(masterSessionId);
      try {
        generation = await runStudioGeneration(k, {
          projectId: typeof params?.projectId === 'string' ? params.projectId : null,
          projectName: promptRaw.split(/\s+/).slice(0, 6).join(' ').slice(0, 60) || 'Master run',
          sourceImageUrl: sourceImageUrlRaw || null,
          prompt: promptRaw,
          model: typeof params?.model === 'string' ? params.model : null,
          aspectRatio: aspectRatioRaw || null,
          abortSignal: options?.abortSignal,
        });
      } finally {
        k.setMasterRunChatSessionId(null);
      }
      const { projectId, workspaceId, outputImageUrl, model: modelId } = generation;

      // Mirror the exchange into the project's own conversation: the user's ask (once)
      //     + this step as CLASSIC Studio telemetry (model + workspace + prompt container).
      //     Best-effort, never fails the build.
      try {
        await mirrorMasterPromptsToProjectConversation(k, projectId, masterSessionId, promptRaw, {
          modelId,
          workspaceId,
        });
      } catch (mirrorError) {
        console.warn('[RUN_STUDIO_STEP] prompt mirroring to the project conversation failed (non-fatal).', mirrorError);
      }

      // Deliver the finished image straight into the run's timeline as a message ,
      // it renders through the existing chat markdown as a ChatGPT-style result card
      // (rounded, with built-in download + edit controls), so the deliverable is
      // guaranteed to appear even if no follow-up LLM turn happens. This is the
      // run's downloadable output.
      if (outputImageUrl) {
        const resultContent = `Here's the result:\n\n![Result](${outputImageUrl})`;
        await k.addPersistedAssistantMessage?.(resultContent, 'Master', masterSessionId);
      }

      k.logUserAction?.('Master Ran Studio Step', { projectId, model: modelId, hasOutput: !!outputImageUrl });

      // Return projectId (reuse across the run's steps) + the downloadable output URL.
      return JSON.stringify({ projectId, outputImageUrl });
    },
  },

  QUERY: {
    action_type: 'QUERY',
    traceLabels: { active: 'querying', done: 'queried', unit: 'entity', pluralUnit: 'entities' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Fetch records in parallel. Found entities are silently anchored to your Cognitive Desk. Omit the "select" parameter to explore an entity fully. Use the "select" array ONLY if you need to extract specific fields for a report.',
    parameters: [
      {
        name: 'queries',
        type: 'object[]',
        description:
          'Array of query descriptors. Each descriptor must include "entity", which must be one of: PROPERTY, UNIT, BRAND, CONTACT, LEAD, DEAL, PORTFOLIO, PROJECT, LEGAL_ENTITY, MARKET_SIGNAL, DOCUMENT_TEMPLATE, GENERATED_DOCUMENT, SOURCING_CAMPAIGN (an asset shortlist run, carries "propertyId" and "status"), BRAND_LOCATION (the store rows of a brand, filter on "brandId" and "country"). Anything else is rejected. Optional keys: "filters" (object mapping allowed fields to either literal equality values or operator objects such as { "$eq": value }, { "$in": [values...] }, { "$contains": "text", "$mode": "insensitive" }, { "$startsWith": "text", "$mode": "insensitive" }, or { "$endsWith": "text" }), "limit" (number of records to return, default 250, max 999), and "select" (array of fields to include in each result).',
        required: true,
      },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      if (!kernel.current) {
        throw new Error('QUERY: Kernel is not available.');
      }

      const queries = params?.queries;
      if (!Array.isArray(queries) || queries.length === 0) {
        throw new Error('QUERY: "queries" must be a non-empty array of query descriptors.');
      }

      const response = await fetch('/api/inventory/commander/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queries }),
        signal: options?.abortSignal
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        const errorMessage =
          typeof errorPayload?.message === 'string' && errorPayload.message.length > 0
            ? errorPayload.message
            : 'Failed to execute leasing data query.';
        throw new Error(errorMessage);
      }

      const { queryResults, executedQueries } = await response.json();

      // --- Implicit Anchoring ---
      const anchorsToUpsert: ContextAnchor[] = [];
      if (Array.isArray(queryResults)) {
        queryResults.forEach((resultSet: any, index: number) => {
          const queryDef = queries[index];
          const entityFromApi = typeof resultSet?.entity === 'string' ? resultSet.entity : '';
          const entityFromQuery = typeof queryDef?.entity === 'string' ? queryDef.entity : '';
          const resolvedEntityType =
            resolveAnchorEntityTypeFromQueryEntity(entityFromApi) ??
            resolveAnchorEntityTypeFromQueryEntity(entityFromQuery);
          if (!resolvedEntityType) return;
          
          const items = resultSet?.results;
          if (Array.isArray(items)) {
            const mass = 1;
            const querySelect = Array.isArray(queryDef?.select) ? queryDef.select : [];
            const hasExplicitSelect = querySelect.length > 0;
            const provenance = `QUERY Result`;

            items.forEach(item => {
              if (item && typeof item.id === 'string') {
                anchorsToUpsert.push({
                  entityType: resolvedEntityType as EntityType,
                  entityId: item.id,
                  mass,
                  provenance,
                  ...(hasExplicitSelect ? { ephemeralData: item } : {}),
                  lastAccessedAt: Date.now(),
                });
              }
            });
          }
        });
      }

      if (anchorsToUpsert.length > 0) {
        updateSessionAnchors(kernel.current, options.sessionId, anchorsToUpsert);
      }

      const totalAnchoredEntities = Array.isArray(queryResults)
        ? queryResults.reduce((acc: number, resultSet: any) => acc + Number(resultSet?.count ?? 0), 0)
        : 0;
      // WHAT WAS FOUND, named here and not only on the desk. The desk splits one budget across every
      // anchor of the session: at 183 anchors a fresh result got ~136 tokens, shrank to a bare id,
      // and the agent re-ran the same lookup turn after turn without ever adding the brand the user
      // asked for (2026-09-29). Ten rows per query, id and name, is enough to act on.
      const foundLines = Array.isArray(queryResults)
        ? queryResults.flatMap((resultSet: any, index: number) => {
            const items: any[] = Array.isArray(resultSet?.results) ? resultSet.results : [];
            if (items.length === 0) return [];
            const entity = String(resultSet?.entity ?? queries[index]?.entity ?? '');
            const shown = items.slice(0, 10).map((item) => {
              const label = item?.name ?? item?.title ?? item?.unitIdentifier ?? item?.brand?.name ?? '';
              return `${item?.id}${label ? ` (${label})` : ''}`;
            });
            const more = Number(resultSet?.count ?? items.length) - shown.length;
            return [`${entity}: ${shown.join(', ')}${more > 0 ? ` and ${more} more` : ''}`];
          })
        : [];
      params.executionSummary = `Found ${totalAnchoredEntities} entities${foundLines.length ? `: ${foundLines.join('; ')}` : ''}. They have been anchored to your Cognitive Desk.`;
      if (executedQueries) {
        params.executedQueries = executedQueries;
      }

      kernel.current.logUserAction?.('Dealer Leasing Data Queried', {
        query_count: Array.isArray(queryResults) ? queryResults.length : 0,
      });
    },
  },

  MUTATE: {
    action_type: 'MUTATE',
    traceLabels: { active: 'writing', done: 'wrote', unit: 'entity', pluralUnit: 'entities' },
    targetEntityType: 'ABSOLUTE',
    description:
      `Your official Drafting and Proposal Tool. Emitting this command is EXACTLY how you present your choices to the user.
  Do not ask for permission to draft a proposal. Do not explain your proposal in text first.
  Just shoot the MUTATE command. Create, update, or delete almost any database table entity with the user level of permission.
  Batch related operations using tempIds. This command pauses for inline human approval, then the script can resume.
  Chainability Rule: if you create an entity with a tempId in MUTATE, you can reference it in subsequent commands in the same script with { "$ref": "your_temp_id" }.`,
    parameters: [
      {
        name: 'operations',
        type: 'object[]',
        description:
          'Array of InventoryMutationOperation objects. Each entry must include "action" (CREATE, UPDATE, DELETE), "entity", and the matching payload/id fields.',
        required: true,
      },
      {
        name: 'reason',
        type: 'string',
        description:
          'A clear, user-facing question describing what this mutation will do. This text is shown directly to the user as the confirmation prompt. Write it as a concise question they can approve or reject, in the SAME LANGUAGE as the user. Example FR: "Créer un deal pour Burostore sur l\'unité 0-11 ?" Example EN: "Update the property filters for Espace St Quentin?"',
        required: true,
      },
      {
        name: 'approveLabel',
        type: 'string',
        description:
          'Short label for the approve button, in the SAME LANGUAGE as the user. Example FR: "Oui, créer le deal" Example EN: "Yes, create the deal"',
        required: false,
      },
      {
        name: 'rejectLabel',
        type: 'string',
        description:
          'Short label for the reject button, in the SAME LANGUAGE as the user. Example FR: "Non, je vais expliquer" Example EN: "No, let me explain why"',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const operations = normalizeLeasingMutationOperations(params?.operations);
      params.operations = operations;

      const reasonRaw = typeof params?.reason === 'string' ? params.reason.trim() : '';
      if (!reasonRaw) {
        throw new Error('MUTATE: "reason" must be a non-empty string.');
      }
      params.reason = reasonRaw;

      // The write. Every MUTATE used to route through an approval card unless the session mode
      // allowed it through; the card is gone, so this is the one and only write path.
      if (!kernel.current) throw new Error('MUTATE: Kernel not available.');
      const results = await kernel.current.mutateInventory(operations, reasonRaw);
      const idResolutionMap = (Array.isArray(results) ? results : []).reduce<Record<string, string>>(
        (acc, result: { tempId?: string; id?: string }) => {
          if (result?.tempId && result?.id) acc[result.tempId] = result.id;
          return acc;
        },
        {},
      );
      params._idResolutionMap = idResolutionMap;
      params.result = { applied: operations.length };
    },
  },

  MANAGE_CONTEXT: {
    action_type: 'MANAGE_CONTEXT',
    traceLabels: { active: 'updating', done: 'updated', unit: 'context' },
    targetEntityType: 'ABSOLUTE',
    description:
      "Adjust the weight (mass) of entities in your Cognitive Desk. Setting an entity's mass to 0 removes it entirely. Use this to free up token budget.",
    parameters: [
      {
        name: 'operations',
        type: 'object[]',
        description:
          'List of declarative operations. Each op is flat: { id?, type?, mass_lt?, mass_gt?, mass }. The "mass" field is required.',
        required: true
      }
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      if (!kernel.current) throw new Error('Kernel unavailable');

      const rawOperations = params.operations;
      if (!Array.isArray(rawOperations) || rawOperations.length === 0) {
        throw new Error('MANAGE_CONTEXT: "operations" must be a non-empty array.');
      }

      const session = kernel.current.getInventory().ChatSession.registry[options.sessionId];
      if (!session) throw new Error('Session not found');

      let currentAnchors: ContextAnchor[] = session.workingMemory?.anchors ? [...session.workingMemory.anchors] : [];

      for (let index = 0; index < rawOperations.length; index += 1) {
        const op = rawOperations[index];
        if (!isRecord(op)) {
          throw new Error(`MANAGE_CONTEXT: operation at index ${index} must be an object.`);
        }

        const candidate = op;
        const nextMass = Number(candidate.mass);
        if (!Number.isFinite(nextMass)) {
          throw new Error(`MANAGE_CONTEXT: operation at index ${index} requires a numeric "mass".`);
        }

        const idFilter =
          typeof candidate.id === 'string' && candidate.id.trim().length > 0
            ? candidate.id.trim()
            : null;
        const typeFilter =
          typeof candidate.type === 'string' && candidate.type.trim().length > 0
            ? candidate.type.trim()
            : null;
        const massLt =
          typeof candidate.mass_lt === 'number' && Number.isFinite(candidate.mass_lt)
            ? candidate.mass_lt
            : null;
        const massGt =
          typeof candidate.mass_gt === 'number' && Number.isFinite(candidate.mass_gt)
            ? candidate.mass_gt
            : null;

        const targetIndices = new Set(
          currentAnchors
            .map((anchor, anchorIndex) => {
              const anchorMass = anchor.mass;
              if (idFilter && anchor.entityId !== idFilter) return -1;
              if (typeFilter && anchor.entityType !== typeFilter) return -1;
              if (massLt !== null && (typeof anchorMass !== 'number' || anchorMass >= massLt)) return -1;
              if (massGt !== null && (typeof anchorMass !== 'number' || anchorMass <= massGt)) return -1;
              return anchorIndex;
            })
            .filter(anchorIndex => anchorIndex !== -1)
        );

        if (nextMass <= 0) {
          currentAnchors = currentAnchors.filter((_, anchorIndex) => !targetIndices.has(anchorIndex));
          continue;
        }

        currentAnchors = currentAnchors.map((anchor, anchorIndex) =>
          targetIndices.has(anchorIndex) ? { ...anchor, mass: nextMass } : anchor
        );
      }

      const newMemory: WorkingMemory = { ...session.workingMemory, anchors: currentAnchors };

      kernel.current.mergeEntityKernelState('ChatSession', options.sessionId, { workingMemory: newMemory });
      kernel.current.markChatSessionDirty?.(options.sessionId);
    }
  },

  WEB_SEARCH: {
      action_type: 'WEB_SEARCH',
      traceLabels: { active: 'searching', done: 'searched', unit: 'query', pluralUnit: 'queries' },
      targetEntityType: 'ABSOLUTE',
      description:
        'Browse the live web for up-to-date, public information, news, market data, comparables, a company or brand website, financials, or any real-world fact not already in the workspace. Use it PROACTIVELY: whenever the user asks you to look online, or the answer needs external or current information, call WEB_SEARCH instead of declining, never tell the user you cannot search the web. Each result now includes the full page text (not just a snippet), READ it. Results arrive in the next turn; if they do not fully answer the question, refine your query and search again before concluding, then answer by citing each claim INLINE as a markdown link to its source URL, never bare [n] numbers and never a "Sources" list at the end.',
      parameters: [
        { name: 'query', type: 'string', description: 'The search query.', required: true },
        {
          name: 'freshness',
          type: 'string',
          description: 'Optional. Time-scopes results.',
          required: false,
          options: ['oneDay', 'oneWeek', 'oneMonth', 'oneYear'],
        },
        {
          name: 'summary',
          type: 'boolean',
          description: 'Optional. If true, requests a summary of the search results. Defaults to false.',
          required: false,
        },
      ],
      availableInModes: ['commander', 'director', 'analyst', 'master', 'pulse'],
      headlessCompatible: true,
      handler: createWebSearchHandler('WEB_SEARCH', 'Dealer', { unavailableOn503: true }),
    },

  LIST_DOCTRINE: {
    action_type: 'LIST_DOCTRINE',
    traceLabels: { active: 'listing doctrine', done: 'listed doctrine', unit: 'page', pluralUnit: 'pages' },
    targetEntityType: 'ABSOLUTE',
    description:
      "List every doctrine page you can read or edit, with its EXACT key, title and the org/scope it belongs to. Call this FIRST to discover the real keys (which are org/team-specific) before READ_DOCTRINE or UPDATE_DOCTRINE, use `scope` (the org name, e.g. 'URW') to pick the right page when several share a title. The list lands on params.result.pages as [{ key, title, scope, kind, agent, editable, isSubpage }].",
    parameters: [],
    availableInModes: ['doctrine', 'master'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const response = await fetch('/api/doctrine', { cache: 'no-store', signal: options?.abortSignal });
      if (!response.ok) throw new Error(`LIST_DOCTRINE: request failed (${response.status}).`);
      const payload = await response.json().catch(() => null);
      type Sec = { key: string; title: string; parentKey?: string | null; container?: boolean; kind: string; agent: string; editable: boolean; isSubpage?: boolean };
      const sections: Sec[] = Array.isArray(payload?.sections) ? payload.sections : [];
      const byKey = new Map(sections.map((s) => [s.key, s]));
      // The top-level container's title is the org name ("URW") or "My doctrine".
      const scopeName = (s: Sec): string | null => {
        let cur: Sec | undefined = s;
        for (let i = 0; i < 12 && cur?.parentKey; i++) cur = byKey.get(cur.parentKey) ?? undefined;
        return cur?.title ?? null;
      };
      params.result = {
        pages: sections
          .filter((s) => !s.container)
          .map((s) => ({
            key: s.key,
            title: s.title,
            scope: scopeName(s),
            kind: s.kind,
            agent: s.agent,
            editable: s.editable,
            isSubpage: Boolean(s.isSubpage),
          })),
      };
    },
  },

  CREATE_DOCTRINE_SUBPAGE: {
    action_type: 'CREATE_DOCTRINE_SUBPAGE',
    traceLabels: { active: 'creating subpage', done: 'created subpage', unit: 'page' },
    targetEntityType: 'ABSOLUTE',
    description:
      "Create a subpage under an existing doctrine page, use this to STRUCTURE a doctrine (e.g. split a scoring rubric onto its own 'Barème' page, or add a themed section). Provide the parent's exact key (from LIST_DOCTRINE) and a title. Set feedsAgent=true when the subpage's content should be injected into the agent (like a barème); leave it false for reference-only notes. Returns the new page key on params.result.key, then UPDATE_DOCTRINE that key with the content.",
    parameters: [
      { name: 'parentKey', type: 'string', description: 'The parent doctrine page key (from LIST_DOCTRINE).', required: true },
      { name: 'title', type: 'string', description: 'The subpage title (e.g. "Barème").', required: true },
      { name: 'feedsAgent', type: 'boolean', description: 'Whether the subpage content is injected into the agent (contributes to context). Default false.', required: false },
    ],
    availableInModes: ['doctrine', 'master'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const parentKey = typeof params?.parentKey === 'string' ? params.parentKey.trim() : '';
      const title = typeof params?.title === 'string' ? params.title.trim() : '';
      if (!parentKey || !title) throw new Error('CREATE_DOCTRINE_SUBPAGE: "parentKey" and "title" are required.');
      const res = await fetch('/api/doctrine/pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentKey, title }),
        signal: options?.abortSignal,
      });
      if (!res.ok) throw new Error(`CREATE_DOCTRINE_SUBPAGE: request failed (${res.status}).`);
      const payload = await res.json().catch(() => null);
      const key = payload?.section?.key ?? null;
      if (key && params?.feedsAgent === true) {
        await fetch('/api/doctrine/pages', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, contributesToContext: true }),
          signal: options?.abortSignal,
        }).catch(() => {});
      }
      params.result = { key, title, feedsAgent: params?.feedsAgent === true };
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('doctrine:updated', { detail: { key: parentKey } }));
    },
  },

  ANALYZE_USAGE: {
    action_type: 'ANALYZE_USAGE',
    traceLabels: { active: 'analysing usage', done: 'analysed usage', unit: 'brief' },
    targetEntityType: 'ABSOLUTE',
    description:
      "Read the ACTUAL usage behind a doctrine, the real Studio projects, prompts, models, aspect ratios and outcomes of an organisation, a team, or a named person. This is how you KNOW the users and their usage: use it to fill or refine doctrine from evidence instead of asking the user to paste examples. Provide `scopeKey` (an org or team doctrine key from LIST_DOCTRINE, e.g. 'org:<id>:tool:STUDIO' or 'team:<id>:tool:STUDIO') and, optionally, `person` (a name or email to narrow to one user, e.g. 'Aurélie'). The grounded brief lands on params.result.brief (with .total and .matched users). If matched is 0, no one by that name is in scope, say so.",
    parameters: [
      { name: 'scopeKey', type: 'string', description: 'An org or team doctrine key (from LIST_DOCTRINE) that sets the organisation/team to analyse.', required: true },
      { name: 'person', type: 'string', description: 'Optional: a name or email to narrow the usage to one user.', required: false },
      { name: 'windowDays', type: 'number', description: 'Optional look-back window in days (default 120).', required: false },
    ],
    availableInModes: ['doctrine', 'master'],
    headlessCompatible: true,
    handler: async (params, _kernel, options) => {
      const scopeKey = typeof params?.scopeKey === 'string' ? params.scopeKey.trim() : '';
      if (!scopeKey) throw new Error('ANALYZE_USAGE: "scopeKey" is required.');
      const q = new URLSearchParams({ scopeKey });
      if (typeof params?.person === 'string' && params.person.trim()) q.set('person', params.person.trim());
      if (typeof params?.windowDays === 'number') q.set('windowDays', String(params.windowDays));
      const res = await fetch(`/api/doctrine/usage?${q.toString()}`, { cache: 'no-store', signal: options?.abortSignal });
      if (!res.ok) {
        throw new Error(
          res.status === 403
            ? 'ANALYZE_USAGE: you cannot analyse usage for that organisation.'
            : `ANALYZE_USAGE: request failed (${res.status}).`,
        );
      }
      const payload = await res.json().catch(() => null);
      params.result = {
        brief: payload?.brief ?? '',
        total: payload?.total ?? 0,
        matched: payload?.matched ?? 0,
        users: payload?.users ?? [],
        note: payload?.note ?? null,
      };
    },
  },

  READ_DOCTRINE: {
    action_type: 'READ_DOCTRINE',
    traceLabels: { active: 'reading doctrine', done: 'read doctrine', unit: 'file' },
    targetEntityType: 'ABSOLUTE',
    description:
      "Read the current content of one editable doctrine page. ALWAYS read before you UPDATE_DOCTRINE, so you refine what exists instead of blindly overwriting. Get the exact key from LIST_DOCTRINE first. Keys look like 'org:<orgId>:GLOBAL' (organisation-wide), 'org:<orgId>:tool:SOURCE' / ':tool:ANALYST' / ':tool:STUDIO' (per-agent), 'team:<teamId>:tool:STUDIO' (per-team), 'user:instructions' (this user's standing instructions), or 'doc:<id>' (a subpage, e.g. a Barème). The content lands on params.result.",
    parameters: [
      {
        name: 'key',
        type: 'string',
        description: 'The exact doctrine key (from LIST_DOCTRINE).',
        required: true,
      },
    ],
    availableInModes: ['doctrine', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const key = typeof params?.key === 'string' ? params.key.trim() : '';
      if (!key) throw new Error('READ_DOCTRINE: "key" must be a non-empty string.');
      const response = await fetch(`/api/doctrine?key=${encodeURIComponent(key)}`, {
        cache: 'no-store',
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        throw new Error(
          response.status === 403 || response.status === 404
            ? `READ_DOCTRINE: you do not have access to "${key}" (the user's role does not include this layer).`
            : `READ_DOCTRINE: request failed (${response.status}).`,
        );
      }
      const payload = await response.json().catch(() => null);
      params.result = {
        key,
        content: payload?.document?.content ?? '',
        mandatory: payload?.document?.mandatory ?? false,
        isEmpty: !(payload?.document?.content ?? '').trim(),
      };
    },
  },

  UPDATE_DOCTRINE: {
    action_type: 'UPDATE_DOCTRINE',
    traceLabels: { active: 'writing doctrine', done: 'wrote doctrine', unit: 'file' },
    targetEntityType: 'ABSOLUTE',
    description:
      "Write one editable doctrine page, this is how you fill in or refine doctrine when the user asks. The content REPLACES the whole page and rides into every future prompt of the affected agents, so write clean, final, well-structured markdown in the page's house style (short sections, imperative rules, tables for rubrics), in the organisation's language, not conversation. Protocol: (1) LIST_DOCTRINE to get the exact key; (2) READ_DOCTRINE first and fold the existing content into your rewrite, never silently drop what is there; (3) write ONE page per call; (4) confirm what you wrote and where. Keys: 'org:<orgId>:GLOBAL' (org-wide, admins only), 'org:<orgId>:tool:SOURCE|ANALYST|STUDIO' (per-agent, admins only), 'team:<teamId>:tool:STUDIO' (per-team), 'doc:<id>' (a subpage), 'user:instructions' (always writable). If the server refuses a key, tell the user their role cannot edit that layer instead of retrying.",
    parameters: [
      {
        name: 'key',
        type: 'string',
        description: 'The exact doctrine key to write (from LIST_DOCTRINE).',
        required: true,
      },
      {
        name: 'content',
        type: 'string',
        description: 'The full new file content (markdown). Replaces the existing content entirely.',
        required: true,
      },
      {
        name: 'mandatory',
        type: 'boolean',
        description: 'Optional, org/tool layers only: mark the layer as non-overridable by lower-precedence layers. Only set when the user explicitly asks for it.',
        required: false,
      },
    ],
    availableInModes: ['doctrine', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const key = typeof params?.key === 'string' ? params.key.trim() : '';
      const content = typeof params?.content === 'string' ? params.content : '';
      if (!key) throw new Error('UPDATE_DOCTRINE: "key" must be a non-empty string.');
      if (!content.trim()) throw new Error('UPDATE_DOCTRINE: "content" must be a non-empty string.');
      const body: Record<string, unknown> = { key, content };
      if (typeof params?.mandatory === 'boolean') body.mandatory = params.mandatory;
      const response = await fetch('/api/doctrine', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        throw new Error(
          response.status === 403
            ? `UPDATE_DOCTRINE: the user's role cannot edit "${key}". Do not retry; tell the user.`
            : `UPDATE_DOCTRINE: request failed (${response.status}).`,
        );
      }
      const payload = await response.json().catch(() => null);
      params.result = { key, version: payload?.document?.version ?? null, saved: true };
      // Let an open Doctrine view refresh the file the agent just wrote.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('doctrine:updated', { detail: { key } }));
      }
      kernel.current?.logUserAction?.('Agent Doctrine Updated', { key });
    },
  },

  // A shortlist judges on the one scoring grid (lib/leasing/briefCriteria.ts), weighed per run
  // (SET_RUN_WEIGHTS), and on its INSTRUCTIONS, the prose these two commands read and write. They are
  // separate from READ/UPDATE_DOCTRINE because that pair asks the agent to discover a key through
  // LIST_DOCTRINE, and a shortlist's key is not listed anywhere: it is derived from the open campaign.
  READ_SHORTLIST_DOCTRINE: {
    action_type: 'READ_SHORTLIST_DOCTRINE',
    traceLabels: { active: 'reading the doctrine', done: 'read the doctrine', unit: 'file' },
    targetEntityType: 'SourcingCampaign',
    description:
      "Read the scoring INSTRUCTIONS of a sourcing shortlist: the free-prose mandate that tells the Strategist and the Judge what this list is really looking for, and that the user sees in the shortlist's doctrine workspace. ALWAYS read before UPDATE_SHORTLIST_DOCTRINE so you extend what is there instead of overwriting it. Defaults to the shortlist the user currently has open in Source; pass 'campaignId' to target another one. The text lands on params.result.content. These instructions are only HALF the doctrine: the points are the one scoring grid, whose weights for this run are in the SOURCE - OPEN SHORTLIST context and change with SET_RUN_WEIGHTS. Read BOTH halves before answering anything about what this list is judged on, and before turning a user's remark into an edit.",
    parameters: [
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional sourcing campaign id. Defaults to the shortlist open in Source.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const { campaignId } = resolveShortlistTarget(params, kernel, 'READ_SHORTLIST_DOCTRINE');
      const key = `campaign:${campaignId}`;
      const response = await fetch(`/api/doctrine?key=${encodeURIComponent(key)}`, {
        cache: 'no-store',
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        throw new Error(
          response.status === 403 || response.status === 404
            ? `READ_SHORTLIST_DOCTRINE: no readable doctrine for shortlist "${campaignId}".`
            : `READ_SHORTLIST_DOCTRINE: request failed (${response.status}).`,
        );
      }
      const payload = await response.json().catch(() => null);
      const content = payload?.document?.content ?? '';
      params.result = { campaignId, content, isEmpty: !content.trim() };
    },
  },

  // The critic pass behind /refine. It does NOT invent a new critic: buildCoachReport already
  // makes five shortlist-level critiques for free (no model, no DB, no network) and the coach
  // route already applies one and re-ranks. Both shipped and had no caller anywhere in the app ,
  // this is the caller.
  //
  // One command, two moves, because a critic that only reports is what we already had and nobody
  // used: with no proposalId it READS the critique, with one it APPLIES that proposal. The agent
  // loops the two until the report comes back empty, which is what makes /refine keep going until
  // the list holds up rather than stopping after one opinion.
  REFINE_SHORTLIST: {
    action_type: 'REFINE_SHORTLIST',
    traceLabels: { active: 'reviewing the shortlist', done: 'reviewed the shortlist', unit: 'finding' },
    targetEntityType: 'SourcingCampaign',
    description:
      "Critique the OPEN shortlist the way a demanding leasing manager would, and fix what it finds. Call with NO proposalId to get the critique: it returns a ranked list of concrete problems with the list, a brief dimension the rubric never scores, a criterion nothing found evidence for, a criterion that does not separate anyone, claims the audit could not support, a ranking so flat it decides nothing, each with an id and an applyMode ('rescore' = free and instant, 'reresearch' = costs credits and is refused here). Then call it AGAIN with that proposalId to apply the fix: it rewrites the scoring rubric and re-ranks every brand. KEEP GOING: after applying one, ask for the critique again, because fixing one problem changes the ranking and can expose the next. Stop when it comes back with no proposals, that is the list holding up to scrutiny. Refuses while a pass is running, since re-ranking mid-run would race it. IT NEVER ADDS BRANDS: it re-judges the rows already on the list. A request for MORE brands - even one framed as making the list more realistic or more complete - is EXTEND_SHORTLIST, not this.",
    parameters: [
      {
        name: 'proposalId',
        type: 'string',
        description: "Apply this proposal from a previous call. Omit to read the critique first.",
        required: false,
      },
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional sourcing campaign id. Defaults to the shortlist open in Source.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const campaignId = resolveShortlistTarget(params, kernel, 'REFINE_SHORTLIST').campaignId;
      const proposalId = typeof params.proposalId === 'string' ? params.proposalId.trim() : '';
      const base = `/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/coach`;

      if (!proposalId) {
        const response = await fetch(base, { cache: 'no-store', signal: options?.abortSignal });
        if (!response.ok) {
          throw new Error(
            response.status === 409
              ? 'REFINE_SHORTLIST: a pass is already running on this shortlist; wait for it to finish.'
              : `REFINE_SHORTLIST: could not read the critique (${response.status}).`,
          );
        }
        const payload = await response.json().catch(() => null);
        const proposals = Array.isArray(payload?.proposals) ? payload.proposals : [];
        params.result = {
          campaignId,
          summary: payload?.summary ?? '',
          proposals,
          // The stop condition, said plainly so the agent does not have to infer it.
          satisfied: proposals.length === 0,
        };
        return;
      }

      const response = await fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposalId }),
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(
          `REFINE_SHORTLIST: could not apply "${proposalId}" (${response.status}${detail?.message ? `: ${detail.message}` : ''}).`,
        );
      }
      const payload = await response.json().catch(() => null);
      params.result = { campaignId, applied: proposalId, ...(payload ?? {}), ...shortlistVerdictResult(kernel.current, campaignId) };
    },
  },

  // The right bar's "Check the data" button, as a command. The route and the model pass already
  // existed (runBrandDataCheck: deterministic + web-grounded, caches on the brand's runtime
  // overlay so a repeat click never re-bills); it was click-only, so the agent could not run the
  // one check that tells it whether a brand's record is trustworthy.
  CHECK_BRAND_DATA: {
    action_type: 'CHECK_BRAND_DATA',
    traceLabels: { active: 'checking the data', done: 'checked the data', unit: 'finding' },
    targetEntityType: 'Brand',
    description:
      "Audit ONE brand's stored record against the live web and say what is actually wrong with it: a website that does not belong to the brand, a name that is not its consumer-facing one, a company that has stopped trading, figures nothing supports. Returns findings with a severity ('wrong' | 'suspect' | 'gap'). Defaults to the brand open in the Source editor; pass 'brandId' to target another. Use this BEFORE trusting a brand's fields in an argument, and whenever the user doubts a value. THE CHECK ITSELF ONLY READS, so it is on you to REPAIR what it finds, in the same turn: MUTATE the fields our own data already proves wrong (a network size below the number of locations we hold, a country missing while we hold stores there), and ENRICH_BRAND the ones that need outside research (a stale year, a doubtful website). Never MUTATE a value you merely remember. Then say what you changed, old -> new, and what you deliberately left.",
    parameters: [
      { name: 'brandId', type: 'string', description: 'Optional brand id. Defaults to the brand open in Source.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const explicit = typeof params.brandId === 'string' ? params.brandId.trim() : '';
      const brandId = explicit || resolveSourceFocusedBrandId(kernel.current?.getState?.());
      if (!brandId) {
        throw new Error('CHECK_BRAND_DATA: "brandId" is required unless a brand is open in the Source editor.');
      }
      const lang = kernel.current?.getState?.()?.language === 'fr' ? 'fr' : 'en';
      const check = await kernel.current?.checkBrandData(brandId, lang);
      if (!check) throw new Error('CHECK_BRAND_DATA: the data check returned nothing.');
      params.result = { brandId, ...check };
      void options;
    },
  },

  // Where a figure we DISPLAY actually came from.
  //
  // We hold 254k verified facts, 99.98% of them carrying the URL they were read from, and the
  // agent could not reach a single one: UniversalFact hangs off its parent polymorphically
  // (entityType + entityId, no foreign key), so MPRACE's relational traversal walks straight past
  // it and nothing else fetched it. The proof of everything we show was one table away and
  // invisible, which is why "prove it" could only ever become a fresh web search - corroborating
  // our own registry-sourced revenue with whatever a blog says is WEAKER proof, not stronger.
  //
  // Read-only, and free: it queries what is already stored, bills nothing and changes nothing.
  GATHER_EVIDENCE: {
    action_type: 'GATHER_EVIDENCE',
    // The trace counts EXECUTIONS, not what came back, so the unit names what one run covers.
    // 'source' read as "we found one source" while forty facts sat in the result.
    traceLabels: { active: 'gathering the evidence', done: 'gathered the evidence', unit: 'brand' },
    targetEntityType: 'Brand',
    description:
      "Retrieve the STORED PROOF behind what we display about one brand or asset: every fact we hold on it, each with the source URL it was read from and the date it was verified, the Expansion chart's bars year by year (expansion.chartByYear), plus the provenance of the revenue figure (whose accounts, which country, which period, filed or modelled). This is where OUR numbers came from, so it is the FIRST thing to reach for when the user asks you to prove, source or justify anything on screen - stronger than a web search, because it is the actual origin of the value they are looking at. Defaults to the brand open in Source; pass 'entityId' (with 'entityType') to target another. Free, read-only, changes nothing. Follow it with WEB_SEARCH to corroborate externally, or when it comes back thin.",
    parameters: [
      { name: 'entityId', type: 'string', description: 'Optional entity id. Defaults to the brand open in Source.', required: false },
      { name: 'entityType', type: 'string', description: "'Brand' (default) or 'Property'.", required: false },
      { name: 'limit', type: 'number', description: 'Max facts to return. Default 40.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel) => {
      const k = kernel.current;
      if (!k) throw new Error('GATHER_EVIDENCE: kernel unavailable.');
      const rawType = typeof params.entityType === 'string' ? params.entityType.trim().toLowerCase() : '';
      const entityType: 'Brand' | 'Property' = rawType === 'property' || rawType === 'asset' ? 'Property' : 'Brand';
      const explicit = typeof params.entityId === 'string' ? params.entityId.trim() : '';
      const entityId = explicit
        || (entityType === 'Brand' ? resolveSourceFocusedBrandId(k.getState?.()) : '');
      if (!entityId) {
        throw new Error('GATHER_EVIDENCE: "entityId" is required unless a brand is open in the Source editor.');
      }
      const limit = Math.max(1, Math.min(100, Number(params.limit) || 40));

      const res = await k.queryInventory('UniversalFact', {
        filters: { entityType, entityId },
        limit,
        force: true,
        ttl: 0,
      });
      const registry = k.getInventory().UniversalFact?.registry ?? {};
      const facts = res.ids.flatMap((id) => {
        const f = registry[id];
        return f
          ? [{ category: f.category, content: f.content, sourceUrl: f.sourceUrl, verifiedAt: f.verifiedAt }]
          : [];
      });

      // The provenance of the headline figure, which is a different KIND of proof from a fact:
      // it says whose accounts the number is. A revenue with no entity behind it is a model, and
      // saying so IS the honest answer to "prove it".
      let revenue: Record<string, unknown> | null = null;
      let name: string | null = null;
      if (entityType === 'Brand') {
        if (!k.getInventory().Brand?.registry?.[entityId]) await k.syncEntity?.('Brand', entityId);
        const b = k.getInventory().Brand?.registry?.[entityId] as Record<string, unknown> | undefined;
        if (b) {
          name = (b.name as string) ?? null;
          // The MODELLED figure is reported even when there is no proved one - especially then.
          // `sortableTotalRevenue` is reserved for figures a verifier proved (financialsProtocol
          // denormalises only VERIFIED records), so a brand can hold a perfectly usable estimate
          // and read as "revenue: not populated". Measured on Motivi: a modelled EUR 60m sat in
          // the record while the agent answered "we do not hold a usable revenue figure".
          const est = b.estimatedRevenue as
            | { revenue?: number | null; year?: number | null; method?: string | null; inputs?: unknown }
            | null
            | undefined;
          const proved = b.sortableTotalRevenue ?? null;
          revenue = {
            proved,
            isEstimate: b.revenueIsEstimate ?? null,
            filedBy: b.revenueEntityName ?? null,
            country: b.revenueEntityCountry ?? null,
            period: b.revenuePeriod ?? null,
            confidence: b.revenueConfidence ?? null,
            source: proved == null
              ? 'no PROVED figure: nothing has been verified against a named company yet'
              : b.revenueIsEstimate
                ? 'MODELLED by us, no source states it'
                : 'filed accounts, national company registry',
            modelled: est?.revenue
              ? {
                  value: est.revenue,
                  year: est.year ?? null,
                  method: est.method ?? null,
                  inputs: est.inputs ?? null,
                  readAs: 'MODELLED, not filed. Quote it only with the method, and never as a filing.',
                }
              : null,
          };
        }
      }

      // The Expansion tab's chart, bar for bar: without it "does this match our graph?" was
      // answered from the summary status alone (2026-09-26).
      let expansion: Record<string, unknown> | null = null;
      if (entityType === 'Brand') {
        const signal = await k.loadBrandExpansionSignal(entityId).catch(() => null);
        if (signal) {
          expansion = {
            status: signal.status ?? null,
            confidence: signal.confidence ?? null,
            openings3y: signal.openings3y ?? null,
            closures3y: signal.closures3y ?? null,
            netOpenings3y: signal.netOpenings3y ?? null,
            lastOpeningDate: signal.lastOpeningDate ?? null,
            source: signal.source ?? null,
            chartByYear: expansionChartYears(signal.openingsByYear, signal.closuresByYear, k.readEntityRuntimeState('Brand', entityId).expansionPeriod),
            readAs: 'chartByYear is exactly what the Expansion tab draws: openings above the axis, closures below. Answer anything about "the graph" from these numbers, year by year.',
          };
        }
      }

      params.result = {
        entityType,
        entityId,
        name,
        expansion,
        factCount: facts.length,
        totalHeld: res.pagination?.total ?? facts.length,
        facts,
        revenue,
        nextStep:
          facts.length === 0
            ? 'Nothing stored for this entity. Say so plainly, then WEB_SEARCH: an empty record is a reason to search, never an answer.'
            : 'Quote these back WITH their sourceUrl. Corroborate externally with WEB_SEARCH where it matters.',
      };
    },
  },

  // The stop condition of a goal-driven loop.
  //
  // Source already looped, it shares the Analyst's auto-recall path, up to 30 passes per user
  // message, but the only thing that ended a loop was the model deciding it was finished. That
  // is not a goal. This asks a SEPARATE agent, with a demanding-leasing-manager standard and hard
  // evidence about the shortlist, whether the goal has actually been met, and returns the
  // remaining gaps as instructions when it has not.
  //
  // Generic on purpose: it judges whatever goal it is handed. /refine is one caller with one
  // goal, not the only shape this supports.
  CHECK_GOAL: {
    action_type: 'CHECK_GOAL',
    traceLabels: { active: 'checking the goal', done: 'checked the goal', unit: 'gap' },
    targetEntityType: 'ABSOLUTE',
    description:
      "Ask an INDEPENDENT reviewer whether a goal you were set has actually been met. Pass the goal in your own words. Returns { satisfied, gaps[], reason, confidence }: `satisfied` false means keep working, and each entry in `gaps` is a concrete instruction for your next pass. Use this as the STOP CONDITION of any long task the user framed as a goal ('until X', 'work until Y', 'keep going until Z'): do the work, call CHECK_GOAL, and if it is not satisfied act on the gaps and call it again. Do NOT declare a goal met on your own judgement, you are the one doing the work, so you are the worst judge of whether it is done. When the goal concerns a shortlist, pass campaignId and the reviewer is handed the rubric, the ranking and the automated findings as evidence. The reviewer's reasoning is NOT shown to the user; only tell them what you changed.",
    parameters: [
      { name: 'goal', type: 'string', description: 'The goal being judged, stated plainly.', required: true },
      { name: 'campaignId', type: 'string', description: 'Optional shortlist to judge against. Defaults to the one open in Source.', required: false },
      { name: 'evidence', type: 'string', description: 'Optional extra context the reviewer should weigh.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const goal = typeof params.goal === 'string' ? params.goal.trim() : '';
      if (!goal) throw new Error('CHECK_GOAL: "goal" must be a non-empty string.');

      // The shortlist is optional: a goal that is not about a shortlist is still judgeable, so a
      // missing one leaves the campaign out rather than refusing the command.
      let campaignId = typeof params.campaignId === 'string' ? params.campaignId.trim() : '';
      if (!campaignId) {
        try {
          campaignId = resolveShortlistTarget(params, kernel, 'CHECK_GOAL').campaignId;
        } catch {
          campaignId = '';
        }
      }

      const response = await fetch('/api/ai/goal-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal,
          ...(campaignId ? { campaignId } : {}),
          ...(typeof params.evidence === 'string' && params.evidence.trim() ? { evidence: params.evidence.trim() } : {}),
          lang: kernel.current?.getState?.()?.language === 'fr' ? 'fr' : 'en',
        }),
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        throw new Error(`CHECK_GOAL: the reviewer could not be reached (${response.status}).`);
      }
      const verdict = await response.json();
      params.result = { goal, ...(campaignId ? { campaignId } : {}), ...verdict };
    },
  },

  // Contacts have the same homonym problem as stores, with a person on the other end of it.
  VERIFY_BRAND_CONTACTS: {
    action_type: 'VERIFY_BRAND_CONTACTS',
    traceLabels: { active: 'verifying the contacts', done: 'verified the contacts', unit: 'contact' },
    targetEntityType: 'Brand',
    description:
      "Check that the people held for a brand ACTUALLY work there, and remove the ones who provably do not. Contact providers match on a company NAME, so a brand whose name is shared by other businesses collects strangers, the same collision that files an art gallery as a retailer's shop, except here it is a real person a leasing manager might email. Each contact is judged on the employer recorded at discovery and on their LinkedIn profile. Someone whose evidence is merely thin is KEPT and flagged, never removed. Returns each contact with a verdict and whether it was removed. Run this before quoting a contact to the user, and whenever a brand's name is generic or unfamiliar. Defaults to the brand open in Source.",
    parameters: [
      { name: 'brandId', type: 'string', description: 'Optional brand id. Defaults to the brand open in Source.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const explicit = typeof params.brandId === 'string' ? params.brandId.trim() : '';
      const brandId = explicit || resolveSourceFocusedBrandId(kernel.current?.getState?.());
      if (!brandId) {
        throw new Error('VERIFY_BRAND_CONTACTS: "brandId" is required unless a brand is open in the Source editor.');
      }
      // Show the list being judged, then show the verdict. Verification REMOVES contacts, so a
      // run that reports "12 unsuitable candidates removed" over a tab the user is not on, and a
      // list that still shows the old rows, is the same invisible-work problem as discovery.
      openBrandContactsView(kernel, brandId, true);
      try {
        const response = await fetch(`/api/inventory/brands/${encodeURIComponent(brandId)}/contacts/verify`, {
          method: 'POST',
          signal: options?.abortSignal,
        });
        if (!response.ok) {
          throw new Error(`VERIFY_BRAND_CONTACTS: the check failed (${response.status}).`);
        }
        params.result = await response.json();
        await kernel.current?.syncEntity('Brand', brandId);
        await kernel.current?.refreshInventory(
          'Contact',
          { parentId: brandId, parentType: 'brand' },
          undefined,
          { force: true },
        );
      } finally {
        kernel.current?.setState({ sourceContactDiscoveryBrandId: null });
      }
    },
  },

  // The right bar's "Run analysis again", as a command. It bills according to analysis cost, so
  // it stays on the always-propose list and Auto cannot fire it without a card.
  RERUN_BRAND_ANALYSIS: {
    action_type: 'RERUN_BRAND_ANALYSIS',
    traceLabels: { active: 're-analysing', done: 're-analysed', unit: 'brand' },
    targetEntityType: 'SourcingCampaign',
    description:
      "Re-run the FULL research and judging pass on one or more named brands of the open shortlist: the Detective gathers fresh evidence per criterion and the Judge re-scores from it. This is the only way to change a score that is wrong because the EVIDENCE was wrong, re-applying the rubric only recomputes from evidence already gathered, so it cannot fix a brand that was judged on fabricated stores or a stale website. COSTS CREDITS: actual spend varies with analysis cost, so name only the brands that need it and never the whole list as a shortcut. Use it after correcting a brand's data (a verified store purge, a fixed website, a cleaned contact list) so the score finally reflects the truth. IT IS ALSO HOW A RUN THAT RAN OUT OF BUDGET IS FINISHED: the brands a truncated pass never reached are leads left ABORTED with no score, and naming them here analyses them for the first time, on the same rubric and inside the same list. EXTEND_SHORTLIST cannot do it - it only adds brands the list has never carried, and those rows are already on it. Defaults to the shortlist open in Source.",
    parameters: [
      { name: 'brandIds', type: 'string[]', description: 'The brands to re-analyse. At least one.', required: true },
      { name: 'campaignId', type: 'string', description: 'Optional campaign id. Defaults to the shortlist open in Source.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      assertUserStartedThisChain(kernel, 'RERUN_BRAND_ANALYSIS');
      const raw = params.brandIds;
      const brandIds = Array.from(
        new Set(
          (Array.isArray(raw) ? raw : [raw])
            .map((id) => (typeof id === 'string' ? id.trim() : ''))
            .filter(Boolean),
        ),
      );
      if (brandIds.length === 0) {
        throw new Error('RERUN_BRAND_ANALYSIS: "brandIds" must contain at least one brand id.');
      }
      const campaignId = resolveShortlistTarget(params, kernel, 'RERUN_BRAND_ANALYSIS').campaignId;
      await kernel.current?.rerunSourcingCampaignBrand(campaignId, brandIds);
      params.result = { campaignId, brandIds, queued: brandIds.length };
      void options;
    },
  },

  RECORD_BRAND_FINANCIALS: {
    action_type: 'RECORD_BRAND_FINANCIALS',
    traceLabels: { active: 'recording the filings', done: 'recorded the filings', unit: 'year' },
    targetEntityType: 'Brand',
    description:
      "Persist the OFFICIAL revenue series you RESEARCHED for a brand whose accounts no integrated register can read (a Polish group in the KRS, a German group in the Bundesanzeiger, audited accounts on the group's own site). Each record needs year, revenue, currency (ISO code), source (name a human recognises) and sourceUrl (the http(s) page the figure is actually on - a record without a real citation is REFUSED, all-or-nothing). The rows land as DECLARED claims: shown with their citations, drawn as a DASHED curve when two or more years share source and currency, never promoted to verified, never entering sorts or scoring. Record the FULL published series (3+ years when the filings exist), not just the latest: one year is a number, a curve needs a history. Never record estimates, forecasts, or figures you computed yourself - filed or officially published figures only, in their original currency.",
    parameters: [
      { name: 'brandId', type: 'string', description: 'The brand. Defaults to the brand open in Source.', required: false },
      { name: 'records', type: 'object[]', description: 'The series: [{ year, revenue, currency, source, sourceUrl }], 1 to 12 rows.', required: true },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const explicit = typeof params.brandId === 'string' ? params.brandId.trim() : '';
      const brandId = explicit || resolveSourceFocusedBrandId(kernel.current?.getState?.());
      if (!brandId) throw new Error('RECORD_BRAND_FINANCIALS: "brandId" is required unless a brand is open in Source.');
      const records = Array.isArray(params.records) ? params.records : [];
      // Recording is brand-data writing: it rides the same in-flight counter as enrichBrand so
      // the blue slide covers it, and the finally below can never leave the counter stuck.
      kernel.current?.setState((state: any) => ({ sourceEnrichInFlight: (state.sourceEnrichInFlight ?? 0) + 1 }));
      markEnriching(kernel.current, brandId, ['financials'], true);
      try {
      const response = await fetch(`/api/inventory/brands/${encodeURIComponent(brandId)}/financials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ records }),
        signal: options?.abortSignal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error((payload as { message?: string })?.message || `RECORD_BRAND_FINANCIALS failed (${response.status}).`);
      await kernel.current?.syncEntity('Brand', brandId);
      params.result = { brandId, ...payload };
      } finally {
        markEnriching(kernel.current, brandId, ['financials'], false);
        kernel.current?.setState((state: any) => ({ sourceEnrichInFlight: Math.max(0, (state.sourceEnrichInFlight ?? 0) - 1) }));
      }
    },
  },

  UPDATE_SHORTLIST_DOCTRINE: {
    action_type: 'UPDATE_SHORTLIST_DOCTRINE',
    traceLabels: { active: 'writing the doctrine', done: 'wrote the doctrine', unit: 'file' },
    targetEntityType: 'SourcingCampaign',
    description:
      "Rewrite the scoring INSTRUCTIONS of a sourcing shortlist. Use this whenever the user tells you how the evidence should be READ on this list (\"an announced opening only counts once the lease is signed\", \"I don't care about franchises here\"): turn what they said into a standing rule. An offhand remark is a valid trigger, it does not have to be a formal request. The content REPLACES the whole document and is injected into every later scoring pass, so write finished markdown in the user's language: short imperative rules, not conversation. Protocol: (1) READ_SHORTLIST_DOCTRINE first and fold the existing text into your rewrite, never silently drop it; (2) write once; (3) tell the user what changed. If the remark is about how many POINTS a criterion is worth, that is SET_RUN_WEIGHTS; criteria are never added or removed. Instructions decide how evidence is read; the rubric decides what the points are. A remark that carries both a priority and a standard of proof (\"stop giving me tiny franchises\") takes both commands in the same script.",
    parameters: [
      {
        name: 'content',
        type: 'string',
        description: 'The full new instruction document (markdown). Replaces the existing one entirely.',
        required: true,
      },
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional sourcing campaign id. Defaults to the shortlist open in Source.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const { campaignId } = resolveShortlistTarget(params, kernel, 'UPDATE_SHORTLIST_DOCTRINE');
      const content = typeof params?.content === 'string' ? params.content : '';
      if (!content.trim()) {
        throw new Error('UPDATE_SHORTLIST_DOCTRINE: "content" must be a non-empty string.');
      }
      if (content.length > SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH) {
        throw new Error(
          `UPDATE_SHORTLIST_DOCTRINE: the document is ${content.length} characters, the maximum is ${SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH}. Shorten it; nothing was saved.`,
        );
      }
      const key = `campaign:${campaignId}`;
      const response = await fetch('/api/doctrine', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, content }),
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        throw new Error(
          response.status === 403
            ? `UPDATE_SHORTLIST_DOCTRINE: the user's role cannot edit this shortlist's doctrine. Do not retry; tell the user.`
            : `UPDATE_SHORTLIST_DOCTRINE: request failed (${response.status}).`,
        );
      }
      const payload = await response.json().catch(() => null);
      params.result = { campaignId, version: payload?.document?.version ?? null, saved: true };
      // A DoctrineDocument has no socket stream, so this token is what makes the Source right bar
      // (and an open doctrine workspace) show the new text without the user refreshing anything.
      const k = kernel.current;
      k?.setState?.({ sourceDoctrineRefreshToken: (k.getState?.()?.sourceDoctrineRefreshToken ?? 0) + 1 });
      // Same signal the Doctrine view already listens to, so an open doctrine page reloads too.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('doctrine:updated', { detail: { key } }));
      }
      k?.logUserAction?.('Agent Shortlist Doctrine Updated', { campaignId });
    },
  },

  // The user's REUSABLE scoring, the pair above lifted off one asset and parked on the person.
  // A shortlist's scoring is two objects (instructions + rubric); a preset is a copy of both, stored
  // as a DoctrineDocument on the existing USER layer, agent SOURCE, key user:tool:SOURCE. No new
  // table, no new Prisma column: see lib/leasing/sourcingPreset.ts for the storage contract and for
  // why the rubric is serialized into the document as a fenced json block.
  //
  // Same modes and same lock as the doctrine pair and REAPPLY_RUBRIC: both ends of a preset are one
  // open asset shortlist, and a campaign id is not something the agent can invent.
  // THE APP'S LANGUAGE, CHANGED BY ASKING. Backing the "/language" entry in the Source composer
  // menu, and any plain request for it ("parle-moi en francais", "switch to English").
  //
  // It writes ONE thing: the user's own `language` preference, through
  // `updateAndSaveUserPreference` - the same setter the settings modal uses, which already handles
  // the optimistic update and the save. Kernel.tsx derives `state.language` from that preference
  // (falling back to the organisation's favourite), and every surface already reads that, so there
  // is nothing else to notify: the UI strings, the agent's own replies (resolveAgentLanguage above)
  // and the documents it writes all follow from the one value.
  // SAVE / LOAD THE LAUNCH PARAMETERS, the agent's half of the right panel's two controls.
  //
  // The form's values are mirrored into `sourceDraftParams` by SourceSetupPanel, and applied back
  // through `sourcePendingParams`, so these two read and write ONE store value each. They do not
  // reach into the panel, and the panel remains the only thing that owns the form.
  //
  // Both write to the SAME document the panel writes: the user's preset on their own doctrine
  // layer. "My usual parameters" means one thing whether it was said to the agent or clicked.
  SAVE_LAUNCH_PARAMS: {
    action_type: 'SAVE_LAUNCH_PARAMS',
    targetEntityType: 'ABSOLUTE',
    traceLabels: { active: 'saving the parameters', done: 'saved the parameters', unit: 'parameter set' },
    description: `Save the launch parameters currently set in the Source panel as this user's saved set.
    Trigger it on "save these params", "keep this setup", "enregistre ces parametres".
    It REPLACES the saved set entirely; there is no history. If they already have one, say so before saving.
    It saves what is ON SCREEN in the setup panel - it does not read a campaign, and it does not launch anything.
    The result lands on params.result: "saved" (how many parameters were stored). Report that.
    TERMINAL: confirm and stop, autoRecall false.`,
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'What to call this set, as the user said it ("ESQ params 1"). Defaults to their existing single set.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel) => {
      const k = kernel.current;
      if (!k) throw new Error('SAVE_LAUNCH_PARAMS: Kernel not available.');
      const draft = k.getState?.()?.sourceDraftParams ?? null;
      if (!draft) {
        throw new Error(
          'SAVE_LAUNCH_PARAMS: no sourcing setup form is open, so there are no parameters to save. Do not retry; ask the user to open a campaign draft first.',
        );
      }
      const existing = parseSourcingPreset(await fetchSourcingPresetContent());
      const name = String(params?.name ?? '').trim() || DEFAULT_PARAMS_SET_NAME;
      // Replace the set of that name, keep every other one: saving "ESQ params 1" must not delete
      // "big-box setup". normalizeSourcingPresetParamSets is last-write-wins on a duplicate name.
      const paramSets = [...(existing.paramSets ?? []).filter((set) => set.name !== name), { name, params: draft }];
      const content = serializeSourcingPreset({
        instructions: existing.instructions,
        rubric: existing.rubric,
        paramSets,
        lang: resolveAgentLanguage(kernel),
      });
      const response = await fetch('/api/doctrine', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: SOURCING_PRESET_DOCTRINE_KEY, content }),
      });
      if (!response.ok) throw new Error(`SAVE_LAUNCH_PARAMS: request failed (${response.status}).`);
      params.result = { saved: Object.keys(draft).length, name };
    },
  },

  LOAD_LAUNCH_PARAMS: {
    action_type: 'LOAD_LAUNCH_PARAMS',
    targetEntityType: 'ABSOLUTE',
    traceLabels: { active: 'loading the parameters', done: 'loaded the parameters', unit: 'parameter set' },
    description: `Fill the Source setup panel with this user's saved launch parameters.
    Trigger it on "use my usual parameters", "load my params", "charge mes parametres", "use ESQ params 1".
    Pass the name when the user says one. With no name, and several saved sets, ASK which - do not pick for them.
    It FILLS THE FORM AND STOPS. It never launches - launching is LAUNCH_SOURCING_CAMPAIGN and needs the user to ask for it.
    If the user has no saved set, say so plainly rather than inventing defaults.
    The result lands on params.result: "loaded" (how many parameters were applied). Report that.
    TERMINAL: confirm and stop, autoRecall false. The user is mid-setup; carrying on into an analysis interrupts them.`,
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Which saved set to load. Omit only when the user has exactly one.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel) => {
      const k = kernel.current;
      if (!k) throw new Error('LOAD_LAUNCH_PARAMS: Kernel not available.');
      const preset = parseSourcingPreset(await fetchSourcingPresetContent());
      const sets = preset.paramSets ?? [];
      if (sets.length === 0) {
        throw new Error(
          'LOAD_LAUNCH_PARAMS: this user has no saved parameters yet. Do not retry; tell them, and offer to save the current ones.',
        );
      }
      const asked = String(params?.name ?? '').trim();
      const chosen = asked
        ? sets.find((set) => set.name.toLowerCase() === asked.toLowerCase())
        : sets.length === 1 ? sets[0] : undefined;
      if (!chosen) {
        throw new Error(
          asked
            ? `LOAD_LAUNCH_PARAMS: no saved set called "${asked}". They have: ${sets.map((set) => set.name).join(', ')}. Ask which one; do not guess.`
            : `LOAD_LAUNCH_PARAMS: they have several saved sets (${sets.map((set) => set.name).join(', ')}) and named none. Ask which one.`,
        );
      }
      k.setState({ sourcePendingParams: chosen.params });
      params.result = { loaded: Object.keys(chosen.params).length, name: chosen.name };
    },
  },

  SET_LANGUAGE: {
    action_type: 'SET_LANGUAGE',
    // Not scoped to any record: it changes a preference on the caller.
    targetEntityType: 'ABSOLUTE',
    traceLabels: { active: 'changing the language', done: 'changed the language', unit: 'language' },
    description: `Change the interface and reply language for THIS user, between English and French.
    Trigger it on any request to change language: "/language", "passe en francais", "answer me in English", "switch the app to French".
    It is a preference on the user, not on the organisation and not on a shortlist: it changes nothing anyone else sees.
    Apply it to your own next reply too - a user who asks for French and gets an English confirmation has been told the command failed.
    The result lands on params.result: "language" (the code now in force). Report the language, do not claim success in the abstract.
    TERMINAL: this is the whole turn. Confirm in one sentence with autoRecall false and stop - do not continue into a shortlist, a campaign or anything else the user did not ask for.`,
    parameters: [
      {
        name: 'language',
        type: 'string',
        description: "The language to switch to: 'fr' or 'en'. Also accepts 'french'/'francais'/'english'.",
        required: true,
      },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel) => {
      const k = kernel.current;
      if (!k) throw new Error('SET_LANGUAGE: Kernel not available.');
      const asked = String(params?.language ?? '').trim().toLowerCase();
      // Tolerant on the way in, exact on the way out: the model will say "French" as readily as
      // "fr", and refusing that would make the command fail for the phrasing users actually use.
      const isFrench = asked.startsWith('fr');
      const isEnglish = asked.startsWith('en');
      if (!isFrench && !isEnglish) {
        throw new Error(
          `SET_LANGUAGE: "${asked || '(empty)'}" is not a language this app has. Only 'fr' and 'en' exist; ask the user which of the two they want.`,
        );
      }
      await k.updateAndSaveUserPreference('language', isFrench ? 'FRENCH' : 'ENGLISH');
      params.result = { language: isFrench ? 'fr' : 'en' };
    },
  },

  SAVE_SOURCING_PRESET: {
    action_type: 'SAVE_SOURCING_PRESET',
    traceLabels: { active: 'saving the preset', done: 'saved the preset', unit: 'preset' },
    targetEntityType: 'SourcingCampaign',
    description: `Save the scoring of the shortlist the user has open as THEIR default, reusable on any other sourcing. Copies the shortlist's INSTRUCTIONS; the scoring grid is the same for every run, so there is nothing else to copy.
    Trigger it when the user says the scoring on this list is how they want to work from now on: "save this scoring as my default", "keep this for my other centres", "this is my usual scoring". Do not offer it on a list they are still arguing with; a preset is worth saving once they are happy with the ranking.
    It REPLACES the preset they had, entirely. If they already have one, say so before saving and let them confirm; there is no history to fall back on.
    It changes nothing on this shortlist and nothing on any other: saving only stores. Applying is APPLY_SOURCING_PRESET, on the shortlist they want it on.
    The result lands on params.result: "hasInstructions". Report it, do not claim success in the abstract.`,
    parameters: [
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional sourcing campaign id to copy from. Defaults to the asset shortlist open in Source.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const campaignId = resolveShortlistTarget(params, kernel, 'SAVE_SOURCING_PRESET').campaignId;
      const k = kernel.current;
      if (!k) throw new Error('SAVE_SOURCING_PRESET: Kernel not available.');

      const instructions = await fetchShortlistInstructions(campaignId, 'SAVE_SOURCING_PRESET', options?.abortSignal);
      if (!instructions.trim()) {
        throw new Error(
          'SAVE_SOURCING_PRESET: this shortlist has no instructions, so there is nothing to save. Do not retry; tell the user.',
        );
      }

      const content = serializeSourcingPreset({
        instructions,
        rubric: [],
        lang: resolveAgentLanguage(kernel),
      });
      // Same ceiling as a shortlist doctrine: it is the same column, and a preset that cannot be
      // written is worse than one that refuses early with a reason.
      if (content.length > SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH) {
        throw new Error(
          `SAVE_SOURCING_PRESET: the preset is ${content.length} characters, the maximum is ${SOURCING_CAMPAIGN_DOCTRINE_MAX_LENGTH}. Shorten this shortlist's instructions first; nothing was saved.`,
        );
      }

      const response = await fetch('/api/doctrine', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: SOURCING_PRESET_DOCTRINE_KEY, content }),
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        throw new Error(
          response.status === 403
            ? 'SAVE_SOURCING_PRESET: this user cannot write their own preset. Do not retry; tell the user.'
            : `SAVE_SOURCING_PRESET: request failed (${response.status}).`,
        );
      }
      const payload = await response.json().catch(() => null);
      params.result = {
        savedFromCampaignId: campaignId,
        hasInstructions: true,
        version: payload?.document?.version ?? null,
        saved: true,
      };
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('doctrine:updated', { detail: { key: SOURCING_PRESET_DOCTRINE_KEY } }),
        );
      }
      k.logUserAction?.('Agent Saved Sourcing Preset', { campaignId });
    },
  },

  APPLY_SOURCING_PRESET: {
    action_type: 'APPLY_SOURCING_PRESET',
    traceLabels: { active: 'applying the preset', done: 'applied the preset', unit: 'preset' },
    targetEntityType: 'SourcingCampaign',
    description: `Write the user's saved sourcing preset onto the shortlist they have open: its INSTRUCTIONS become this shortlist's instructions (the scoring grid is the same for every run). Trigger it on "apply my usual scoring here", "score this one like the others", "use my default".
    IT REPLACES, IT DOES NOT MERGE. READ what is there first (READ_SHORTLIST_DOCTRINE) and tell the user which asset-specific instruction lines go away.
    Instructions apply on the next pass: nothing already scored moves.
    If no preset exists yet the command fails with that message: do not retry, tell the user to save one first with SAVE_SOURCING_PRESET from a shortlist they are happy with.`,
    parameters: [
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional sourcing campaign id to apply onto. Defaults to the asset shortlist open in Source.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const campaignId = resolveShortlistTarget(params, kernel, 'APPLY_SOURCING_PRESET').campaignId;
      const k = kernel.current;
      if (!k) throw new Error('APPLY_SOURCING_PRESET: Kernel not available.');

      const presetResponse = await fetch(
        `/api/doctrine?key=${encodeURIComponent(SOURCING_PRESET_DOCTRINE_KEY)}`,
        { cache: 'no-store', signal: options?.abortSignal },
      );
      if (!presetResponse.ok) {
        throw new Error(`APPLY_SOURCING_PRESET: reading the preset failed (${presetResponse.status}).`);
      }
      const presetPayload = await presetResponse.json().catch(() => null);
      const preset = parseSourcingPreset(presetPayload?.document?.content);
      if (!preset.instructions.trim()) {
        throw new Error(
          'APPLY_SOURCING_PRESET: this user has no saved sourcing preset yet. Do not retry; tell them to open a shortlist they are happy with and run SAVE_SOURCING_PRESET first.',
        );
      }

      params.campaignId = campaignId;
      params.presetInstructions = preset.instructions;
      await applySourcingPresetToShortlist(k, { campaignId, instructions: preset.instructions });
      params.result = { campaignId, instructionsReplaced: true, applied: true };
      k.logUserAction?.('Agent Applied Sourcing Preset', { campaignId });
    },
  },

  RECORD_MEMORY: {
    action_type: 'RECORD_MEMORY',
    traceLabels: { active: 'remembering', done: 'remembered', unit: 'memory', pluralUnit: 'memories' },
    targetEntityType: 'ABSOLUTE',
    description:
      "Record a durable memory about THIS USER that should persist across every future session and every agent. Use it in two cases: (1) the user explicitly tells you to remember something (\"remember that…\", \"from now on…\", \"my preference is…\"); (2) you learn a stable, reusable fact about how this user works that would improve future turns, a standing constraint, a correction they gave you, a lasting preference. Do NOT record transient task details, one-off values, entity/CRM data (use MUTATE for those), or anything already stated in your instructions. Keep each memory ONE self-contained sentence. It rides into every future prompt under USER MEMORY, so be selective and high-signal.",
    parameters: [
      {
        name: 'content',
        type: 'string',
        description: 'The single-sentence memory to store, phrased so it is useful out of context.',
        required: true,
        example: 'This user always wants prices in EUR and floor areas in m², and prefers concise answers.',
      },
      {
        name: 'importance',
        type: 'number',
        description: 'Optional 1–10 priority; higher entries survive budget trimming. Defaults to 5.',
        required: false,
        example: 8,
      },
    ],
    availableInModes: ['commander', 'director', 'analyst', 'master', 'doctrine', 'slides'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      if (!kernel.current) {
        throw new Error('RECORD_MEMORY: Kernel is not available.');
      }
      const content = typeof params?.content === 'string' ? params.content.trim() : '';
      if (!content) throw new Error('RECORD_MEMORY: "content" must be a non-empty string.');
      const importance = typeof params?.importance === 'number' ? params.importance : undefined;

      const response = await fetch('/api/doctrine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, importance, origin: 'AGENT_VOLUNTEERED' }),
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        let message = 'Failed to record the memory.';
        try {
          const payload = await response.json();
          if (typeof payload?.message === 'string' && payload.message.length > 0) message = payload.message;
        } catch {
          // keep default message
        }
        throw new Error(`RECORD_MEMORY: ${message}`);
      }
      const payload = await response.json().catch(() => null);
      // autoRecall feeds this back so the agent can confirm to the user.
      params.result = { recorded: true, id: payload?.entry?.id ?? null };
      kernel.current.logUserAction?.('Agent Memory Recorded', {});
    },
  },

  CREATE_SUPPORT_TICKET: {
    action_type: 'CREATE_SUPPORT_TICKET',
    traceLabels: { active: 'escalating', done: 'escalated', unit: 'ticket' },
    targetEntityType: 'ABSOLUTE',
    description:
      'Escalates an unresolved problem to the Fater team as a support ticket. Use ONLY after the Runtime Error Protocol is exhausted: you could not fix the error with your commands AND there is no user-side fix (or it keeps recurring after your fixes). The created ticket number lands on params.result, tell the user the number and that the Fater team will intervene. Never ticket an issue already listed under KNOWN UNRESOLVED ISSUES.',
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Short, plain-language summary of the problem.',
        required: true,
        example: 'Image upscale fails with a configuration error',
      },
      {
        name: 'description',
        type: 'string',
        description: 'What happened, what you attempted, and the impact on the user.',
        required: true,
        example:
          'Magnific upscale fails with "missing API key" on every attempt. I retried and switched models without success. The user cannot upscale outputs.',
      },
      {
        name: 'errorId',
        type: 'string',
        description: 'Optional. The errorId of the observed runtime error being escalated (from RUNTIME ERRORS OBSERVED).',
        required: false,
        example: '3f6c1d2e-8a90-4a3b-9f1c-0d2e4b6a8c10',
      },
    ],
    availableInModes: ['commander', 'director', 'analyst'],
    handler: async (params, kernel, options) => {
      if (!kernel.current) {
        throw new Error('CREATE_SUPPORT_TICKET: Kernel is not available.');
      }
      const title = typeof params?.title === 'string' ? params.title.trim() : '';
      const description = typeof params?.description === 'string' ? params.description.trim() : '';
      if (!title) throw new Error('CREATE_SUPPORT_TICKET: "title" must be a non-empty string.');
      if (!description) throw new Error('CREATE_SUPPORT_TICKET: "description" must be a non-empty string.');
      const errorId = typeof params?.errorId === 'string' ? params.errorId.trim() : '';
      const ledger = kernel.current.getAgentErrorLedger?.() ?? [];
      const observedError = errorId ? ledger.find((entry) => entry.id === errorId) ?? null : null;

      const response = await fetch('/api/support-tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          description,
          errorContext: observedError ?? undefined,
        }),
        signal: options?.abortSignal,
      });
      if (!response.ok) {
        let message = 'Failed to create the support ticket.';
        try {
          const payload = await response.json();
          if (typeof payload?.message === 'string' && payload.message.length > 0) message = payload.message;
        } catch {
          // keep default message
        }
        throw new Error(`CREATE_SUPPORT_TICKET: ${message}`);
      }
      const payload = await response.json().catch(() => null);
      // autoRecall feeds this back to the agent so it can tell the user the number.
      params.result = { displayNumber: payload?.displayNumber ?? 'FTR-????' };

      // The escalated error stops riding as an open runtime error, from now on
      // it returns as a KNOWN UNRESOLVED ISSUE (served from the ticket table).
      if (observedError) {
        kernel.current.setAgentErrorLedger?.((prev: AgentObservedError[]) =>
          prev.map((entry) =>
            entry.id === observedError.id ? { ...entry, status: 'ESCALATED' as const } : entry
          )
        );
      }
      kernel.current.logUserAction?.('Agent Support Ticket Created', {
        has_error_context: Boolean(observedError),
      });
    },
  },

  SET_PLAN: {
    action_type: 'SET_PLAN',
    traceLabels: { active: 'setting', done: 'set', unit: 'plan' },
    targetEntityType: 'ABSOLUTE',
    description: 'Updates or clears the multi-turn `strategic_plan` variable. This is your primary tool for maintaining long-term strategy. Use an empty string to clear the plan upon task completion.',
    parameters: [
      {
        name: 'plan',
        type: 'string',
        description: 'The new strategic plan text. An empty string clears the plan.',
        required: true
      }
    ],
    availableInModes: ['director'],
    handler: async (params, kernel, options) => {
      const plan = params.plan as string;
      if (typeof plan !== 'string') {
        throw new Error('SET_PLAN: "plan" parameter must be a string.');
      }
      if (!kernel.current) return;
      const { sessionId: activeId, session: activeSession, projectId: activeProjectId } =
        resolveSessionProjectContext(kernel, options.sessionId);
      const planUpdatedAt = new Date().toISOString();
      if (activeId) {
        const updatedAt = planUpdatedAt;
        const s = kernel.current?.getInventory().ChatSession.registry[activeId];
        if (s) {
          const updated = {
            ...s,
            strategicPlan: plan || null,
            updatedAt,
            isDirty: true,
            saveStatus: s.saveStatus === 'saving' ? 'saving' : 'idle',
          };
          kernel.current?.mergeEntityKernelState('ChatSession', activeId, updated);
          kernel.current?.markChatSessionDirty?.(activeId);
        }
      }

      if (typeof window !== 'undefined') {
        try {
          const referencedGenerationTaskIds = (() => {
            const projectId = typeof activeProjectId === 'string' ? activeProjectId.trim() : '';
            const tasks = projectId ? (kernel.current?.getGenerationTasks?.(projectId) ?? []) : [];
            const ids = tasks
              .map(task => task.id)
              .filter((id): id is string => typeof id === 'string' && id.length > 0);
            return Array.from(new Set(ids));
          })();

          const recentMessages =
            (activeId ? (kernel.current?.getChatSessionMessages(activeId) ?? []) : [])
              .slice(-5)
              .map((message: any) => ({
                role: message.role,
                content: message.content,
                timestamp: message.timestamp,
              }));

          const lastUserMessage =
            [...(activeId ? (kernel.current?.getChatSessionMessages(activeId) ?? []) : [])]
              .reverse()
              .find((message: any) => message.role === 'user');

          const planningPayload: PlanningTracePayload = {
            planText: plan,
            agentPlan: {
              text: plan,
              updatedAt: planUpdatedAt,
              source: 'SET_PLAN',
            },
            userPrompt: lastUserMessage?.content ?? null,
            chatSessionId: activeId ?? null,
            sessionType: activeSession
              ? (resolveProjectIdFromChatSessionContext(activeSession) ? 'project-linked' : 'standalone')
              : null,
            contextSnapshot: {
              projectId: activeProjectId,
              projectName: activeProjectId
                ? (
                  (() => {
                    const projectRow = kernel.current.getInventory().Project.registry[activeProjectId];
                    if (!projectRow) return null;
                    return projectRow?.name ?? null;
                  })()
                  ?? null
                )
                : null,
              workspaceId: options.workspaceId,
              agentMode:
                isEntityFocusSubject(activeSession?.activeSubject ?? null) &&
                activeSession?.activeSubject?.entityType === 'Project'
                  ? 'director'
                  : 'commander',
              messageCount: activeId ? (kernel.current?.getChatSessionMessages(activeId)?.length ?? 0) : 0,
              recentMessages,
            },
            referencedGenerationTaskIds,
            outcomeStatus: 'PENDING',
            source: 'SET_PLAN',
          };

          await fetch('/api/planning-traces', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(planningPayload),
          });
        } catch (error) {
          console.error('[SET_PLAN] Failed to record planning trace', error);
        }
      }
    }
  },

  GENERATE_DOCUMENT: {
    action_type: 'GENERATE_DOCUMENT',
    traceLabels: { active: 'generating', done: 'generated', unit: 'document' },
    targetEntityType: 'Deal',
    description: 'Generates a PDF document for a specific Deal using a specific Document Template. The resulting document is saved to the deal history. You should usually query for available templates (DocumentTemplate) first to choose the right templateId.',
    prerequisites: [
      { entityType: 'Deal', instruction: 'Use QUERY to find and anchor the target Deal.' },
      { entityType: 'DocumentTemplate', instruction: 'Use QUERY to find and anchor the desired DocumentTemplate.' }
    ],
    parameters: [
      {
        name: 'dealId',
        type: 'string',
        description: 'The ID of the Deal to render.',
        required: true,
      },
      {
        name: 'templateId',
        type: 'string',
        description: 'Template ID to use for generation. Optional only for INCENTIVE docs (fallback resolves deal incentive template or global default).',
        required: false,
      },
      {
        name: 'documentType',
        type: 'string',
        description: 'Optional document classification. Allowed values: "INCENTIVE" or "LOI". If omitted, type is inferred from template when templateId is provided.',
        required: false,
      }
    ],
    availableInModes: ['commander'],
    handler: async (params, kernel, options) => {
      const { dealId, templateId, documentType } = params;

      if (!dealId || typeof dealId !== 'string') throw new Error('GENERATE_DOCUMENT: "dealId" is required.');
      if (templateId !== undefined && typeof templateId !== 'string') throw new Error('GENERATE_DOCUMENT: "templateId" must be a string when provided.');
      if (documentType !== undefined && documentType !== 'LOI' && documentType !== 'INCENTIVE') {
        throw new Error('GENERATE_DOCUMENT: "documentType" must be "LOI" or "INCENTIVE".');
      }
      if (!templateId && documentType === 'LOI') {
        throw new Error('GENERATE_DOCUMENT: "templateId" is required for LOI documents.');
      }

      const targetSessionId = options.sessionId || kernel.current?.getActiveChatSessionId?.() || null;
      const plannedDocumentType = resolveDocumentTypeForGeneration(kernel, documentType, templateId);
      const plannedActionType = resolveDocumentActionType(plannedDocumentType);
      if (targetSessionId && plannedActionType && kernel.current && !options.isHeadless) {
        kernel.current.setActiveSubject(targetSessionId, {
          entityType: 'Deal',
          entityId: dealId,
          focusActionType: plannedActionType,
        });
      }

      // Notify user process is starting
      toast.info('Generating document...');

      const result = await kernel.current?.generateDocument(dealId, templateId, documentType);

      const resolvedDocumentType = coerceGeneratedDocumentType(result?.document?.type) ?? plannedDocumentType;
      const resolvedActionType = resolveDocumentActionType(resolvedDocumentType);
      if (targetSessionId && resolvedActionType && kernel.current && !options.isHeadless) {
        kernel.current.setActiveSubject(targetSessionId, {
          entityType: 'Deal',
          entityId: dealId,
          focusActionType: resolvedActionType,
        });
      }
      
      toast.success('Document generated successfully.');
      
      kernel.current?.logUserAction?.('Document Generated', {
        dealId,
        templateId,
        documentType: resolvedDocumentType ?? documentType ?? null,
        docId: result?.document?.id,
      });
      
      if (result?.chat_message && typeof result.chat_message === 'string') {
        await kernel.current?.addAssistantMessage(result.chat_message, undefined, 'Fater');
      }
    }
  },

  EXPORT_SHORTLIST: {
    action_type: 'EXPORT_SHORTLIST',
    traceLabels: { active: 'exporting', done: 'exported', unit: 'shortlist' },
    targetEntityType: 'SourcingCampaign',
    description: `Export the open shortlist as a PDF the user can send to someone who has no account: the ranked brands with their scores and the reason behind each one, the evidence as clickable sources, and the scoring rubric the whole list was judged on. It downloads in the user's browser immediately. Free, instant, and it changes nothing: it reads what is already scored, never launches a pass and never enriches.
    Defaults to the shortlist open in Source; pass "campaignId" to target another one. It covers ASSET runs, the ones that produce a shortlist of brands; a portfolio-wide pass is refused out loud.
    WHAT IT LEAVES OUT, say so if the user asks why a brand is missing: only settled, kept results travel (SUCCESS and INTERESTING). Rows a person removed, rows the Judge answered "not a fit" (DISMISSED), rows whose analysis failed and rows from a run that was stopped are all absent, though the brands the run really scored are still counted on the cover.
    NARROW IT WHEN THE USER DOES. "just the ones worth calling" is "highlightedOnly": true (the rows marked INTERESTING); "the top ten" is "limit": 10. Default is the top 25, and the file states on its own cover how many rows it holds out of how many qualified, so you never have to guess at those numbers - do not invent them.
    IT IS TERMINAL: export, say in one sentence that the file has downloaded and what you narrowed it to if the user asked for something specific, set autoRecall: false and STOP. Do not re-rank, re-read or improve the list first unless they asked: an export is a snapshot of what the user is looking at, and a list that silently changed on the way to the PDF is not the one they approved.`,
    parameters: [
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional sourcing campaign id. Defaults to the asset shortlist open in Source.',
        required: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'How many brands to include, best first. Default 25, maximum 100.',
        required: false,
      },
      {
        name: 'highlightedOnly',
        type: 'boolean',
        description: 'Include only the rows marked INTERESTING. Default false.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    // The file lands through a browser download, so a headless pass has nowhere to put it.
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      if (!k) throw new Error('EXPORT_SHORTLIST: Kernel not available.');
      const { campaignId } = resolveShortlistTarget(params, kernel, 'EXPORT_SHORTLIST');

      const query = new URLSearchParams({ lang: k.getState().language === 'fr' ? 'fr' : 'en' });
      const limit = Number(params.limit);
      if (Number.isFinite(limit) && limit > 0) query.set('limit', String(Math.floor(limit)));
      if (params.highlightedOnly === true) query.set('highlighted', '1');

      // Building the document takes a moment, and without this the screen does not move at all
      // until the browser's download appears.
      toast.info('Building the shortlist PDF...');
      const response = await fetch(
        `/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/export?${query.toString()}`,
        { cache: 'no-store', signal: options?.abortSignal },
      );
      if (!response.ok) {
        const message = await response.text().catch(() => '');
        throw new Error(
          `EXPORT_SHORTLIST: the export failed (${response.status}). ${message.slice(0, 200)}`,
        );
      }

      const disposition = response.headers.get('Content-Disposition') ?? '';
      const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'shortlist.pdf';
      const blob = await response.blob();
      // Checked AFTER the body: aborting mid-download leaves fetch resolved but the user has said
      // stop, and a file landing in their Downloads folder from a cancelled run is not recoverable.
      if (options?.abortSignal?.aborted) return;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);

      toast.success('Shortlist exported.');
      // A LINK IN THE CHAT, not only a file in the Downloads folder. The download above is a copy at
      // one moment; the link is the document itself, re-rendered from the shortlist as it stands
      // whenever anyone opens it, and it needs no storage, no second copy and no expiry - it is the
      // same route, the same permission check, with `inline` so a click SHOWS the PDF instead of
      // saving it again. Asked for on 2026-08-16: "when we export a pdf, it should be an openable
      // link on the chat".
      const openUrl = `/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/export?${query.toString()}&inline=1`;
      // POSTED BY THE COMMAND, NOT LEFT TO THE MODEL. The first cut put the link in the result and
      // told the agent to end its reply with it. Measured on 2026-08-16: the export turn produced an
      // assistant message of ZERO characters, so the link existed and nobody ever saw it. A file the
      // user asked for is not something to make conditional on the model choosing to speak.
      // Precedent for a command writing into the conversation: kernel/useCommands.ts.
      k.addAssistantMessage?.(`[${fileName}](${openUrl})`);
      params.result = {
        campaignId,
        fileName,
        openUrl,
        linkAlreadyPosted: true,
      };
      k.logUserAction?.('Agent Exported Shortlist', { campaignId });
    },
  },

  REAPPLY_RUBRIC: {
    action_type: 'REAPPLY_RUBRIC',
    traceLabels: { active: 'reranking', done: 'reranked', unit: 'shortlist' },
    targetEntityType: 'SourcingCampaign',
    description: `Re-rank a sourcing shortlist against the CURRENT scoring rubric, for free and in seconds. It recomputes every lead's fit score from the per-criterion evidence the run already stored, so the list visibly re-orders on screen without spending a credit or re-running anything. Defaults to the shortlist the user has open in Source; pass "campaignId" to target another one.
    CHAIN IT AFTER A WEIGHT CHANGE. Put it in the same script, straight after SET_RUN_WEIGHTS, so the user sees the consequence of their remark in the same turn.
    It re-weighs the run against the one scoring grid with THAT RUN'S weights, so chain it on the shortlist whose weights you just set.
    ITS ONE LIMITATION, be honest about it: it RESCALES points that already exist. Changing a criterion's WEIGHT re-ranks for free, because the same evidence is simply worth more or less. A shortlist scored before the one grid (2026-10-07) carries other criteria: the server refuses it with a 409 naming them. That is a definitive answer, NOT a transient failure: do not retry, do not try another campaignId. Tell the user, in their language, that this list was scored on an older grid and only a relaunch can score it on the current one. Then stop and let them decide, because a relaunch costs credits.
    A 409 also comes back while the campaign is still PENDING/WAITING/RUNNING. Same rule: do not retry, tell the user to wait for the run to finish.
    REPORT THE NUMBERS. The result lands on params.result: "updatedLeads" (how many rows moved), "dismissedAfterReapply", "resurrectedFromDiscard", "skippedNoCriteria", "skippedRubricMismatch". Say how many rows actually moved instead of claiming success in the abstract. "resurrectedFromDiscard" counts rows that came BACK. A non-zero count is normal and is the point of a re-rank, not an anomaly: a brand the Judge dismissed for failing an eliminatory minimum returns once the new weights no longer put it under that floor. Report the number plainly as part of the outcome. What never returns is a lead a PERSON removed by hand (INVALIDATED), that is curation, not a score, and no change of weights undoes it. If an invalidated row ever reappears, THAT is worth flagging.`,
    parameters: [
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional sourcing campaign id. Defaults to the asset shortlist open in Source.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    headlessCompatible: true,
    handler: async (params, kernel) => {
      const k = kernel.current;
      if (!k) throw new Error('REAPPLY_RUBRIC: Kernel not available.');

      // Re-ranking exists for a portfolio pass too, and the kernel has had the call all along with
      // nothing able to reach it. "Re-rank this" means the same thing whichever list is open, so
      // the id decides the route rather than the user being asked which engine produced their list.
      const explicitRubricId = typeof params.campaignId === 'string' ? params.campaignId.trim() : '';
      const openRubricId = k.getState().sourceSelectedCampaignId;

      const { campaignId } = resolveShortlistTarget(params, kernel, 'REAPPLY_RUBRIC');

      try {
        const { summary, rubricMismatch } = await k.reapplySourcingCampaignRubric(campaignId);
        // A re-rank changes the answer, so the verdict that came before it is now stale. Said at
        // the point of action, not only in the doctrine, because this is the moment it applies.
        params.result = { campaignId, ...(summary ?? {}), rubricMismatch, ...shortlistVerdictResult(kernel.current, campaignId) };
      } catch (error) {
        const status = (error as { status?: number } | null)?.status;
        const message = (error as Error | null)?.message || 'Failed to reapply rubric.';
        // 409 is the server's considered refusal (criteria added/removed, or the campaign is
        // still running). Surfacing it as "do not retry" is what stops the agent looping on it.
        throw new Error(
          status === 409
            ? `REAPPLY_RUBRIC: ${message} Do not retry this command; explain the situation to the user and let them decide.`
            : `REAPPLY_RUBRIC: ${message}`,
        );
      }
      k.logUserAction?.('Agent Reapplied Shortlist Rubric', { campaignId });
    },
  },

  // THE RUN'S WEIGHTS (lib/leasing/runWeights.ts): the criteria are fixed, the weights follow the
  // run, and the user changes them by saying so, during the run or after it.
  SET_RUN_WEIGHTS: {
    action_type: 'SET_RUN_WEIGHTS',
    traceLabels: { active: 'reweighing', done: 'reweighed', unit: 'shortlist' },
    targetEntityType: 'SourcingCampaign',
    description: `Change what each scoring criterion is WORTH for the open run, while it is scoring or after it, and re-rank it in the same call. The criteria never change, only their weights: every run is weighed for its unit, brief and centre when it starts, and the user may say "rent matters more here" or "less weight on traffic" at any time. Pass only the criteria that change, by id, with their new weight; the others keep theirs and the set is brought back to the grid's total. Brands already scored are re-ranked for free; a brand still being judged is weighed on the new set when it is saved.
    The criteria themselves are one grid for every run and cannot be added, removed or rewritten. The criterion ids are those of the open shortlist's scoring grid in the LIVE WORKSPACE CONTEXT. REPORT what moved: the result carries the new weights and the re-rank counts.`,
    parameters: [
      { name: 'campaignId', type: 'string', description: 'Optional. Defaults to the shortlist open in Source.', required: false },
      { name: 'weights', type: 'object[]', description: 'The criteria that change: [{ id: criterionId, weight: number }], positive weights.', required: true },
      { name: 'reason', type: 'string', description: "One sentence on why, in the user's words.", required: false },
    ],
    availableInModes: ['commander'],
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    headlessCompatible: true,
    handler: async (params, kernel) => {
      const k = kernel.current;
      if (!k) throw new Error('SET_RUN_WEIGHTS: Kernel not available.');
      const { campaignId } = resolveShortlistTarget(params, kernel, 'SET_RUN_WEIGHTS');
      const weights = Object.fromEntries((Array.isArray(params.weights) ? params.weights : [])
        .filter((w: unknown): w is { id: string; weight: number } => !!w && typeof (w as { id?: unknown }).id === 'string')
        .map((w: { id: string; weight: number }) => [w.id, Number(w.weight)]));
      const response = await fetch(`/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/weights`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ weights, reason: params.reason }),
      });
      const body = await response.json().catch(() => ({})) as { message?: string; weights?: Record<string, number>; criteria?: unknown };
      if (!response.ok) throw new Error(`SET_RUN_WEIGHTS: ${body.message ?? response.status}${response.status < 500 ? ' Do not retry as is; tell the user.' : ''}`);
      // Nothing scored yet is not a failure: the weights stand and every brand is scored on them.
      const reranked = await k.reapplySourcingCampaignRubric(campaignId).then((r) => r.summary).catch((error: Error) => ({ notYet: error.message }));
      params.result = { campaignId, weights: body.criteria, reranked, ...shortlistVerdictResult(kernel.current, campaignId) };
      k.logUserAction?.('Agent Set Run Weights', { campaignId, weights: body.weights });
    },
  },

  // The other end of LAUNCH_SOURCING_CAMPAIGN. A run that is scoring the wrong brands, or that was
  // launched by mistake, has to be stoppable by SAYING SO, the stop button lives in the campaign
  // view, which is exactly where you are not when you realise. Free, and the server treats a user
  // stop as a graceful terminal path (ABORTED), so nothing is corrupted by using it.
  DIAGNOSE_ASSET_FOR_SOURCING: {
    action_type: 'DIAGNOSE_ASSET_FOR_SOURCING',
    traceLabels: { active: 'reading', done: 'read', unit: 'asset' },
    targetEntityType: 'Property',
    description: `Read, in one call, what a sourcing on this asset has to answer: its empty units by size band (available and under offer, with each surface), the tenants on lease by market positioning and CNCC family, how many brands already trade on site, and the last runs on the asset with the filters they used and what they got (scored, top score, how many reached 60, how many were dismissed).
    ALWAYS the first step when the user states a sourcing need, and again before any relaunch: the parameters come from these facts, never from the form's defaults. Read-only, costs nothing.
    FOR ONE UNIT, pass "unitId" as well: the answer then carries the unit brief, what an asset read averages away: the unit's surface, frontage, floor, asking rent, its neighbours read off the plan with their tenants, its last tenants, the runs already scored for it, and the surface band its filters should open on. A sourcing for a unit starts from that brief and from a one-line hypothesis (what belongs there and why) that you draft from it and the user confirms.`,
    parameters: [
      { name: 'propertyId', type: 'string', description: 'The asset. Defaults to the asset in focus, or to the unit\'s asset when unitId is given.', required: false },
      { name: 'unitId', type: 'string', description: 'One unit of the asset, when the sourcing is for that unit. Defaults to the unit in focus, if any.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      const unitId = (typeof params.unitId === 'string' && params.unitId.trim()) || resolveUnitIdFromSessionContext(kernel, sessionId);
      const unit = unitId
        ? await fetch(`/api/inventory/units/${encodeURIComponent(unitId)}/sourcing-brief`).then(async (response) => {
            if (!response.ok) throw new Error(`DIAGNOSE_ASSET_FOR_SOURCING: unit ${response.status} ${(await response.text()).slice(0, 200)}`);
            return response.json() as Promise<{ asset: { id: string } }>;
          })
        : null;
      const propertyId = (typeof params.propertyId === 'string' && params.propertyId.trim()) || unit?.asset.id || resolvePropertyIdFromSessionContext(kernel, sessionId);
      if (!propertyId) throw new Error('DIAGNOSE_ASSET_FOR_SOURCING: name the asset (propertyId); none is in focus.');
      const response = await fetch(`/api/inventory/properties/${encodeURIComponent(propertyId)}/sourcing-diagnosis`);
      if (!response.ok) throw new Error(`DIAGNOSE_ASSET_FOR_SOURCING: ${response.status} ${(await response.text()).slice(0, 200)}`);
      params.result = { ...(await response.json()), ...(unit ? { unit } : {}) };
    },
  },

  PROPOSE_SOURCING_BUDGET: {
    action_type: 'PROPOSE_SOURCING_BUDGET',
    traceLabels: { active: 'preparing', done: 'proposed', unit: 'proposal' },
    targetEntityType: 'SourcingCampaign',
    description: 'Create a proposal to continue an existing completed campaign: more credits, a WIDENED BRIEF (surface range, store minimum), or both. Does not spend, launch, or grant a mandate. The card shows the change, how many new brands the widened brief opens (measured when proposed), the ceiling, goal and target score. Only the human Allow once control in the existing approval interface applies it and continues the same shortlist on the wider brief. Do not promise a first batch or cost before continuation is reserved. A chat agreement alone cannot accept the proposal.\n    DO EVERYTHING YOU CAN TO REACH THE TARGET (owner, 2026-10-14): when a run ends short of its target because no brand that fits the brief is left ("Aucune nouvelle marque qualifiée", or a mandate saying every brand that fits is in the shortlist), propose the gentlest widening yourself, without waiting to be asked: the surface range by about a fifth each side first, then a lower store minimum, then wider. Keep the unit in mind: a widening a unit cannot take is no proposal. A widening that opens nothing is refused: try another.',
    parameters: [
      { name: 'campaignId', type: 'string', description: 'Campaign to continue; defaults to the open Source shortlist.', required: false },
      { name: 'additionalCredits', type: 'number', description: 'Positive whole number of EXTRA credits; the resulting total ceiling is capped at 3000. Omit when only widening the brief and the ceiling left still pays for brands.', required: false },
      { name: 'surfaceMinSqm', type: 'number', description: 'Widened brief: the new smallest usual store surface, m2.', required: false },
      { name: 'surfaceMaxSqm', type: 'number', description: 'Widened brief: the new largest usual store surface, m2.', required: false },
      { name: 'minimumNetworkSize', type: 'number', description: 'Widened brief: the new minimum number of stores a brand runs.', required: false },
      { name: 'goal', type: 'string', description: 'The user\'s sourcing goal, required if the campaign has no saved goal. A saved mandate goal is preserved.', required: false },
      { name: 'targetScore', type: 'number', description: 'Target score (50–100) for a campaign without a mandate; otherwise its existing target is preserved.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: false,
    evaluateLockState: (context) => openShortlistLock(context.kernel),
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      const sessionId = options.sessionId || k?.getActiveChatSessionId();
      const campaignId = String(params.campaignId || k?.getState().sourceSelectedCampaignId || '').trim();
      if (!k || !sessionId || !campaignId) throw new Error('Open the campaign in Source before proposing more credits.');
      const brief = Object.fromEntries((['surfaceMinSqm', 'surfaceMaxSqm', 'minimumNetworkSize'] as const).filter((key) => typeof params[key] === 'number').map((key) => [key, params[key] as number]));
      params.result = await k.sourceBudgetProposal(campaignId, {
        action: 'propose', chatSessionId: sessionId, ...(typeof params.additionalCredits === 'number' ? { additionalCredits: params.additionalCredits } : {}),
        ...(Object.keys(brief).length ? { brief } : {}),
        goal: typeof params.goal === 'string' ? params.goal : undefined,
        targetScore: typeof params.targetScore === 'number' ? params.targetScore : undefined,
      });
    },
  },

  SET_SOURCING_MANDATE: {
    action_type: 'SET_SOURCING_MANDATE',
    traceLabels: { active: 'recording', done: 'recorded', unit: 'mandate' },
    targetEntityType: 'ABSOLUTE',
    description: `Record the initial mandate for a NEW sourcing plan: the user's goal in their words, the asset(s), a credit ceiling and the target score a champion must reach (default 90; set it when the user names one). This command neither raises an existing campaign's server-held ceiling nor renews its continuation. Within the initial mandate you may launch, extend and relaunch ON YOUR OWN, including in the turn that wakes you when a run lands, until the committed budgets reach the ceiling (each launch or extension counts its whole budget), the mandate expires (6 hours) or the user presses stop.
    Only in a turn where the user agreed to go ("go", "vas-y", "launch it", "lance"): a mandate is spending authority, and you may not grant it to yourself. Propose the plan and its total cost first, then record the mandate in the turn they accept it. Without a mandate, every billed step waits for them to ask, as before.
    Say the ceiling plainly when you record it, and report what is committed and what is left after every launch or extension.`,
    parameters: [
      { name: 'goal', type: 'string', description: "The user's need, in their words.", required: true },
      { name: 'propertyIds', type: 'string', description: 'Comma-separated asset ids the mandate covers.', required: true },
      { name: 'creditCeiling', type: 'number', description: 'Total credits the mandate may commit, launches and extensions together (max 3000).', required: true },
      { name: 'targetScore', type: 'number', description: 'The score (50-100) a champion must reach before you stop on your own. Default 90.', required: false },
    ],
    availableInModes: ['commander'],
    handler: async (params, kernel, options) => {
      assertUserAskedToLaunch(kernel, 'SET_SOURCING_MANDATE');
      const k = kernel.current;
      const sessionId = options.sessionId || k?.getActiveChatSessionId() || null;
      if (!k || !sessionId) throw new Error('SET_SOURCING_MANDATE: no active conversation.');
      const made = createMandate({
        goal: String(params.goal ?? ''),
        propertyIds: String(params.propertyIds ?? '').split(','),
        creditCeiling: Number(params.creditCeiling),
        targetScore: params.targetScore == null || params.targetScore === '' ? null : Number(params.targetScore),
      });
      if (!made.ok) throw new Error(`SET_SOURCING_MANDATE: refused, ${made.reason}.`);
      k.setState({ sourceMandates: { ...(k.getState().sourceMandates ?? {}), [sessionId]: made.mandate } });
      k.logUserAction?.('Sourcing mandate recorded', { sessionId, creditCeiling: made.mandate.creditCeiling, propertyIds: made.mandate.propertyIds });
      params.result = { mandate: made.mandate };
    },
  },

  END_SOURCING_MANDATE: {
    action_type: 'END_SOURCING_MANDATE',
    traceLabels: { active: 'closing', done: 'closed', unit: 'mandate' },
    targetEntityType: 'ABSOLUTE',
    description: 'Close the mandate once the shortlist is coherent, or when the user says stop. After it, every billed step waits for the user again.',
    parameters: [],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      const sessionId = options.sessionId || k?.getActiveChatSessionId() || null;
      if (!k || !sessionId) return;
      const { [sessionId]: closed, ...rest } = k.getState().sourceMandates ?? {};
      k.setState({ sourceMandates: rest });
      params.result = { closed: Boolean(closed), committedCredits: closed?.committedCredits ?? 0 };
    },
  },

  STOP_SOURCING_CAMPAIGN: {
    action_type: 'STOP_SOURCING_CAMPAIGN',
    traceLabels: { active: 'stopping', done: 'stopped', unit: 'campaign' },
    targetEntityType: 'SourcingCampaign',
    description: `Stop a RUNNING or QUEUED sourcing campaign, at either granularity: an asset run (brands scored against the whole asset) or a unit run (brand x unit pairs). Use it the moment the user says to stop, cancel, halt or kill a run ("stop it", "arrête", "cancel that campaign"), this is never something to ask permission for, because stopping spends nothing and a run left going does. Stops a PORTFOLIO-WIDE pass too, which is the same word to the user; it reads which family the campaign belongs to and calls the right route. Defaults to the shortlist or pass the user has open in Source; give "campaignId" to target another one.
    The campaign settles as ABORTED, which is a normal terminal state and not a failure: brands already scored KEEP their scores and stay in the shortlist, and the rows that never ran are released so a later pass can pick them up. Credits already consumed by completed analyses are NOT refunded, say so plainly rather than implying the stop undoes the spend.
    After stopping, tell the user how many rows were scored before it stopped, so they know what they got for the credits spent.`,
    parameters: [
      {
        name: 'campaignId',
        type: 'string',
        description: 'Optional campaign id, asset run or unit run. Defaults to the shortlist open in Source.',
        required: false,
      },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel) => {
      // Stopping is the one shortlist operation that exists at BOTH granularities, so it routes
      // by family instead of refusing a unit run: the button in the unit campaign view calls the
      // same unit-fit stop route, and "stop it" has to work from the chat, which is exactly where
      // the user is when they realise.
      const k = kernel.current;
      if (!k) throw new Error('STOP_SOURCING_CAMPAIGN: Kernel not available.');

      // ONE FAMILY, ONE STOP ROUTE. This command used to dispatch between an asset run and a
      // portfolio pass by looking the id up in a registry; the portfolio engine was deleted on
      // 2026-08-18 and only asset runs can be started now.
      const { campaignId } = resolveShortlistTarget(params, kernel, 'STOP_SOURCING_CAMPAIGN');
      await k.stopSourcingCampaign(campaignId);
      params.result = { campaignId, scope: 'asset', stopped: true };
      k.logUserAction?.('Agent Stopped Sourcing Campaign', { campaignId, scope: 'asset' });
    },
  },

  CONFIGURE_PROPERTY_SOURCING_STRATEGY: {
    action_type: 'CONFIGURE_PROPERTY_SOURCING_STRATEGY',
    traceLabels: { active: 'configuring', done: 'configured', unit: 'strategy' },
    targetEntityType: 'Property',
    description: `Set the asset-level sourcing strategy filters an asset campaign reads at launch:
    whether to exclude the property's existing tenants and/or brands scored in the last 6 months. Applies immediately (cheap, reversible config, no credits).
    It also sets the rest of the asset brief: the accepted retail sectors, the unit surface envelope
    a brand must be able to occupy. Together these are every asset filter the right bar exposes.
    Only the fields you pass are changed; omitted fields keep their current value. Pair this with
    SET_RUN_WEIGHTS (weights) and LAUNCH_SOURCING_CAMPAIGN (budget + country) for full launch granularity.`,
    parameters: [
      { name: 'propertyId', type: 'string', description: 'Target property ID. Optional if a Property/Unit/Deal/Campaign tied to a property is focused.', required: false },
      { name: 'excludeExistingTenants', type: 'boolean', description: 'Exclude brands already tenants at this property.', required: false },
      { name: 'excludeRecentlyScored', type: 'boolean', description: 'Exclude brands scored for this property in the last 6 months.', required: false },
      { name: 'acceptedCnccActivities', type: 'string[]', description: 'Restrict candidates to these retail sectors (CNCC activity labels, as shown in the right bar). Pass [] to stop filtering by sector.', required: false },
      { name: 'surfaceMinSqm', type: 'number', description: 'Smallest unit surface the brand must be able to occupy, m2. 0 = no lower bound. Brands whose declared range cannot fit are excluded; brands with unknown surface are kept.', required: false },
      { name: 'surfaceMaxSqm', type: 'number', description: 'Largest unit surface, m2. 0 = no upper bound.', required: false },
      { name: 'acceptedFormats', type: 'string[]', description: 'Store formats the unit takes, any of: STANDARD (a shop), KIOSK, CORNER, FLAGSHIP, DRIVE, MOBILE. A brand whose French network rarely uses them is capped on surface & format (measured on its stores\' photos). Pass [] for any format.', required: false },
      { name: 'maxBudgetCredits', type: 'number', description: 'Spending ceiling in credits for this asset\'s next launch. Actual brands analysed vary with analysis cost; the observed reference is about 29 brands per 50 credits. Saved as the asset\'s launch budget and shown in the right bar; it does NOT launch anything or spend anything on its own.', required: false }
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      const propertyId =
        (typeof params.propertyId === 'string' && params.propertyId.trim().length > 0
          ? params.propertyId.trim()
          : null) || resolvePropertyIdFromSessionContext(kernel, sessionId);
      if (!propertyId) {
        throw new Error('CONFIGURE_PROPERTY_SOURCING_STRATEGY: "propertyId" is required unless a Property/Unit/Deal/Campaign is currently focused.');
      }

      // The merge rules (partial update, [] clears, 0 = no bound, "true" is true) live in
      // lib/leasing/sourcingStrategyParams and are unit-tested there. This file cannot be imported
      // in jest, so anything kept here could only ever be grepped for, never run - which is how a
      // switch that never reached the database still passed its checks.
      const currentFilters =
        (kernel.current?.getInventory().Property.registry[propertyId] as
          | { sourcingCategoryFilters?: Record<string, unknown> | null }
          | undefined)?.sourcingCategoryFilters ?? {};

      let nextFilters: Record<string, unknown>;
      let excludeExistingTenants: boolean | undefined;
      let excludeRecentlyScored: boolean | undefined;
      try {
        nextFilters = mergeSourcingCategoryFilters(currentFilters as Record<string, unknown>, {
          acceptedCnccActivities: params.acceptedCnccActivities,
          surfaceMinSqm: params.surfaceMinSqm,
          surfaceMaxSqm: params.surfaceMaxSqm,
          acceptedFormats: params.acceptedFormats,
        });
        excludeExistingTenants = readFlag(params.excludeExistingTenants, 'excludeExistingTenants');
        excludeRecentlyScored = readFlag(params.excludeRecentlyScored, 'excludeRecentlyScored');
      } catch (error) {
        throw new Error(`CONFIGURE_PROPERTY_SOURCING_STRATEGY: ${error instanceof Error ? error.message : String(error)}`);
      }

      const payload: Record<string, unknown> = { sourcingCategoryFilters: nextFilters };
      if (excludeExistingTenants !== undefined) {
        payload.sourcingExclusionTenants = excludeExistingTenants;
      }
      if (excludeRecentlyScored !== undefined) {
        payload.sourcingExclusionRecentLeads = excludeRecentlyScored;
      }

      params.propertyId = propertyId;

      // THE BUDGET HAS NO COLUMN, so it cannot ride the mutate above. It lives in the asset's
      // runtime draft, which is exactly where LAUNCH_SOURCING_CAMPAIGN reads it from and where the
      // right bar now shows it. Before this, asked to "apply 10 to the budget", the agent had
      // nowhere to put it: it wrote into the filters JSON, the write schema silently dropped the
      // key, and the agent reported success for a number that never existed.
      if (params.maxBudgetCredits !== undefined && params.maxBudgetCredits !== null) {
        const parsed = Number(params.maxBudgetCredits);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          throw new Error('CONFIGURE_PROPERTY_SOURCING_STRATEGY: "maxBudgetCredits" must be a positive number of credits.');
        }
        kernel.current?.patchEntityRuntimeState('Property', propertyId, {
          sourcingBudgetCreditsDraft: Math.max(1, Math.floor(parsed)),
        });
      }

      await kernel.current?.mutateInventory(
        [{ action: 'UPDATE', entity: 'Property', id: propertyId, payload }],
        'Dealer updated property sourcing strategy',
      );
    }
  },

  ASSET_MODEL_EDIT: {
    action_type: 'ASSET_MODEL_EDIT',
    traceLabels: { active: 'updating', done: 'updated', unit: 'assumption', pluralUnit: 'assumptions' },
    targetEntityType: 'Property',
    description: `Edits the focused asset's repositioning business-plan model (the in-app Asset spreadsheet) and recomputes returns live. Applies directly, it is a reversible what-if, no database write. Provide ONLY the levers to change; omit everything else (omitted levers are left unchanged).
    Editable levers (all optional numbers unless noted): capexEnvelopeEur (euros, e.g. 18000000), rentUpliftPct (ERV reversion vs passing, 0-80), timelineMonths (works duration, 6-48), tenantMix ('retail'|'mixed'|'office'|'leisure'), sustainability ('baseline'|'silver'|'gold'|'net-zero'), holdYears (3-10), rentGrowthPct (ERV growth %/yr), opexRatioPct (% of gross rent), exitCapRatePct (3-12), acqCostPct, sellingCostPct, ltvPct (senior loan-to-cost, 0-85), interestRatePct, arrangementFeePct, mezzLtvPct (mezzanine loan-to-cost, 0-30; 0 = no mezz), mezzRatePct, prefRatePct (LP preferred hurdle), promotePct (GP carry). propertyId is optional when a Property/Unit/Deal tied to a property is focused.`,
    evaluateLockState: (context) => {
      const permissionLock = assetManagementPermissionLock(context.kernel.current);
      if (permissionLock) return permissionLock;
      const { lockHint } = resolveFocusedPropertyId(context);
      if (lockHint) return { isLocked: true, unlockHint: lockHint };
      return { isLocked: false, unlockHint: '' };
    },
    parameters: [
      { name: 'propertyId', type: 'string', description: 'Target asset (Property) ID. Optional if a Property/Unit/Deal tied to a property is focused.', required: false },
      { name: 'capexEnvelopeEur', type: 'number', description: 'Capex envelope in euros (e.g. 18000000).', required: false },
      { name: 'rentUpliftPct', type: 'number', description: 'Target ERV reversion uplift vs passing rent, % (0-80).', required: false },
      { name: 'timelineMonths', type: 'number', description: 'Works duration to practical completion, months (6-48).', required: false },
      { name: 'tenantMix', type: 'string', description: "Repositioning mix: 'retail' | 'mixed' | 'office' | 'leisure'.", required: false },
      { name: 'sustainability', type: 'string', description: "ESG target: 'baseline' | 'silver' | 'gold' | 'net-zero'.", required: false },
      { name: 'holdYears', type: 'number', description: 'Hold period to exit, years (3-10).', required: false },
      { name: 'rentGrowthPct', type: 'number', description: 'ERV growth per year, % (-5 to 10).', required: false },
      { name: 'opexRatioPct', type: 'number', description: 'Non-recoverable opex as % of gross rent (0-60).', required: false },
      { name: 'exitCapRatePct', type: 'number', description: 'Exit cap rate applied to stabilised NOI, % (3-12).', required: false },
      { name: 'acqCostPct', type: 'number', description: 'Acquisition costs, % of purchase price (0-8).', required: false },
      { name: 'sellingCostPct', type: 'number', description: 'Disposal costs, % of gross sale (0-5).', required: false },
      { name: 'ltvPct', type: 'number', description: 'Senior leverage as % of total cost / LTC (0-85).', required: false },
      { name: 'interestRatePct', type: 'number', description: 'Senior all-in interest rate, % (0-20).', required: false },
      { name: 'arrangementFeePct', type: 'number', description: 'Debt arrangement fee, % of loan (0-5).', required: false },
      { name: 'mezzLtvPct', type: 'number', description: 'Mezzanine as % of total cost (0-30; 0 disables mezz).', required: false },
      { name: 'mezzRatePct', type: 'number', description: 'Mezzanine rate, % (0-25).', required: false },
      { name: 'prefRatePct', type: 'number', description: 'LP preferred return / hurdle, % (0-30).', required: false },
      { name: 'promotePct', type: 'number', description: 'GP promote / carry above the hurdle, % (0-50).', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      let propertyId =
        typeof params.propertyId === 'string' && params.propertyId.trim().length > 0
          ? params.propertyId.trim()
          : null;
      if (!propertyId) propertyId = resolvePropertyIdFromSessionContext(kernel, sessionId);
      if (!propertyId) {
        throw new Error('ASSET_MODEL_EDIT: a Property must be focused, or pass "propertyId".');
      }

      const clampNum = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
      const asNum = (v: unknown): number | undefined => {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string') {
          const n = parseFloat(v.replace(/[^0-9.+-]/g, ''));
          return Number.isFinite(n) ? n : undefined;
        }
        return undefined;
      };
      const MIXES: TenantMix[] = ['retail', 'mixed', 'office', 'leisure'];
      const SUS: Sustainability[] = ['baseline', 'silver', 'gold', 'net-zero'];

      const patch: Partial<ScenarioParams> = {};
      const setIf = (key: keyof ScenarioParams, raw: unknown, lo: number, hi: number, round = false) => {
        const n = asNum(raw);
        if (n === undefined) return;
        const c = clampNum(n, lo, hi);
        (patch as Record<string, number>)[key] = round ? Math.round(c) : c;
      };
      setIf('capexEnvelope', params.capexEnvelopeEur, 500_000, 80_000_000, true);
      setIf('rentUpliftPct', params.rentUpliftPct, 0, 80, true);
      setIf('timelineMonths', params.timelineMonths, 6, 48, true);
      setIf('holdYears', params.holdYears, 3, 10, true);
      setIf('rentGrowthPct', params.rentGrowthPct, -5, 10);
      setIf('opexRatioPct', params.opexRatioPct, 0, 60, true);
      setIf('exitCapRatePct', params.exitCapRatePct, 3, 12);
      setIf('acqCostPct', params.acqCostPct, 0, 8);
      setIf('sellingCostPct', params.sellingCostPct, 0, 5);
      setIf('ltvPct', params.ltvPct, 0, 85, true);
      setIf('interestRatePct', params.interestRatePct, 0, 20);
      setIf('arrangementFeePct', params.arrangementFeePct, 0, 5);
      setIf('mezzLtvPct', params.mezzLtvPct, 0, 30, true);
      setIf('mezzRatePct', params.mezzRatePct, 0, 25);
      setIf('prefRatePct', params.prefRatePct, 0, 30);
      setIf('promotePct', params.promotePct, 0, 50, true);
      if (typeof params.tenantMix === 'string' && MIXES.includes(params.tenantMix as TenantMix)) {
        patch.tenantMix = params.tenantMix as TenantMix;
      }
      if (typeof params.sustainability === 'string' && SUS.includes(params.sustainability as Sustainability)) {
        patch.sustainability = params.sustainability as Sustainability;
      }
      if (Object.keys(patch).length === 0) {
        throw new Error('ASSET_MODEL_EDIT: provide at least one assumption to change (e.g. ltvPct, exitCapRatePct, rentUpliftPct).');
      }

      // Hydrate the asset's units so the recompute reflects the full rent roll.
      const property = kernel.current?.getInventory()?.Property?.registry?.[propertyId] ?? null;
      const unitIds: string[] = Array.isArray(property?.unitIds) ? property.unitIds : [];
      if (unitIds.length > 0) {
        try {
          await kernel.current?.ensureInventoryIds('Unit', unitIds);
        } catch {
          /* best-effort hydration */
        }
      }
      const unitRegistry = kernel.current?.getInventory()?.Unit?.registry ?? {};
      const units = unitIds.map((id) => unitRegistry[id]);
      const baseline = deriveAssetBaseline(property, units);

      // Seed defaults if the model hasn't been opened yet, then apply the patch.
      if (!getAssetModelParams(kernel.current, propertyId)) {
        setAssetModelParams(kernel.current, propertyId, deriveDefaultParams(baseline));
      }
      patchAssetModelParams(kernel.current, propertyId, patch);

      // Immediate feedback: recompute and surface the new headline metrics.
      const merged = getAssetModelParams(kernel.current, propertyId);
      if (merged) {
        const o = deriveScenarioOutcomes(baseline, merged);
        toast.success(
          `Model updated, Levered IRR ${o.leveredIrrPct.toFixed(1)}%, EM ${o.equityMultiple.toFixed(2)}×, profit ${fmtEuroM(o.profitEur)}`,
        );
      }
    },
  },

  ASSET_MODEL_RENT_ROLL: {
    action_type: 'ASSET_MODEL_RENT_ROLL',
    traceLabels: { active: 'updating', done: 'updated', unit: 'tenancy', pluralUnit: 'tenancies' },
    targetEntityType: 'Property',
    description: `Edits the focused asset's rent roll lease-by-lease, then recomputes the WHOLE model live (NOI, value, returns, WAULT, reversion, occupancy). Reversible what-if, no database write. Use to change a tenancy's rent / area / ERV / lease expiry / status, re-let a vacant unit, vacate a let one, or add & remove tenancies.
    'edits' = changes to EXISTING tenancies; identify each by 'unit' = the rent-roll Unit label (e.g. "0-53") or the unit id. Per edit (only 'unit' required): passingRentEur (€/yr), areaSqm, ervPerSqm (€/m²/yr), leaseExpiryYear (years from now), status ('LEASED' | 'EXPIRING_SOON' | 'REPLACEMENT_UNDER_OFFER' | 'AVAILABLE'), tenant (name), remove (true drops the tenancy).
    'add' = NEW tenancies, each { label, areaSqm, passingRentEur?, ervPerSqm?, leaseExpiryYear?, status?, tenant? }.
    A unit set to 'AVAILABLE' (or with no passing rent) is treated as vacant. propertyId optional when a Property is focused.`,
    evaluateLockState: (context) => {
      const permissionLock = assetManagementPermissionLock(context.kernel.current);
      if (permissionLock) return permissionLock;
      const { lockHint } = resolveFocusedPropertyId(context);
      if (lockHint) return { isLocked: true, unlockHint: lockHint };
      return { isLocked: false, unlockHint: '' };
    },
    parameters: [
      { name: 'edits', type: 'object[]', description: 'Changes to existing tenancies. Each: { unit (label or id, required), passingRentEur?, areaSqm?, ervPerSqm?, leaseExpiryYear?, status?, tenant?, remove? }.', required: false },
      { name: 'add', type: 'object[]', description: 'New tenancies. Each: { label (required), areaSqm (required), passingRentEur?, ervPerSqm?, leaseExpiryYear?, status?, tenant? }.', required: false },
      { name: 'propertyId', type: 'string', description: 'Target asset (Property) ID. Optional if a Property is focused.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      let propertyId =
        typeof params.propertyId === 'string' && params.propertyId.trim().length > 0
          ? params.propertyId.trim()
          : null;
      if (!propertyId) propertyId = resolvePropertyIdFromSessionContext(kernel, sessionId);
      if (!propertyId) throw new Error('ASSET_MODEL_RENT_ROLL: a Property must be focused, or pass "propertyId".');

      // Hydrate units so we can resolve unit references + recompute on the full roll.
      const property = kernel.current?.getInventory()?.Property?.registry?.[propertyId] ?? null;
      const unitIds: string[] = Array.isArray(property?.unitIds) ? property.unitIds : [];
      if (unitIds.length > 0) {
        try {
          await kernel.current?.ensureInventoryIds('Unit', unitIds);
        } catch {
          /* best-effort hydration */
        }
      }
      const unitRegistry = kernel.current?.getInventory()?.Unit?.registry ?? {};
      const units = unitIds.map((id) => unitRegistry[id]);
      const baseline = deriveAssetBaseline(property, units);
      const roll = baseline.rentRoll;

      const asNum = (v: unknown): number | undefined => {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string') {
          const n = parseFloat(v.replace(/[^0-9.+-]/g, ''));
          return Number.isFinite(n) ? n : undefined;
        }
        return undefined;
      };
      const STATUSES = ['LEASED', 'EXPIRING_SOON', 'REPLACEMENT_UNDER_OFFER', 'AVAILABLE'];
      const findKey = (ref: unknown): string | null => {
        const s = String(ref ?? '').trim();
        if (!s) return null;
        const byId = roll.find((l) => l.unitId === s);
        if (byId) return byId.unitId;
        const byLabel = roll.find((l) => l.label.toLowerCase() === s.toLowerCase());
        return byLabel ? byLabel.unitId : null;
      };

      const editsMap: Record<string, RentRollEdit> = {};
      const editsArr = Array.isArray(params.edits) ? params.edits : [];
      for (const raw of editsArr) {
        if (!raw || typeof raw !== 'object') continue;
        const e = raw as Record<string, unknown>;
        const key = findKey(e.unit);
        if (!key) continue; // unknown tenancy → skip
        const edit: RentRollEdit = {};
        if (e.remove === true) edit.removed = true;
        const pr = asNum(e.passingRentEur);
        if (pr !== undefined) edit.passingRent = Math.max(0, Math.round(pr));
        const ar = asNum(e.areaSqm);
        if (ar !== undefined) edit.areaSqm = Math.max(0, Math.round(ar));
        const erv = asNum(e.ervPerSqm);
        if (erv !== undefined) edit.ervPerSqm = Math.max(0, Math.round(erv));
        const exp = asNum(e.leaseExpiryYear);
        if (exp !== undefined) edit.leaseExpiryYear = Math.max(0, Math.min(30, exp));
        if (typeof e.status === 'string' && STATUSES.includes(e.status)) edit.status = e.status;
        if (typeof e.tenant === 'string' && e.tenant.trim()) edit.tenant = e.tenant.trim();
        if (Object.keys(edit).length > 0) editsMap[key] = edit;
      }

      const addArr = Array.isArray(params.add) ? params.add : [];
      let addSeq = Object.keys(getAssetRentRollEdits(kernel.current, propertyId)).filter((k) => k.startsWith('add:')).length;
      for (const raw of addArr) {
        if (!raw || typeof raw !== 'object') continue;
        const a = raw as Record<string, unknown>;
        const label = typeof a.label === 'string' && a.label.trim() ? a.label.trim() : null;
        const area = asNum(a.areaSqm);
        if (!label || area === undefined) continue; // need label + area
        addSeq += 1;
        const status = typeof a.status === 'string' && STATUSES.includes(a.status) ? a.status : 'LEASED';
        const edit: RentRollEdit = { added: true, label, areaSqm: Math.max(0, Math.round(area)), status };
        const pr = asNum(a.passingRentEur);
        if (pr !== undefined) edit.passingRent = Math.max(0, Math.round(pr));
        const erv = asNum(a.ervPerSqm);
        if (erv !== undefined) edit.ervPerSqm = Math.max(0, Math.round(erv));
        const exp = asNum(a.leaseExpiryYear);
        if (exp !== undefined) edit.leaseExpiryYear = Math.max(0, Math.min(30, exp));
        if (typeof a.tenant === 'string' && a.tenant.trim()) edit.tenant = a.tenant.trim();
        editsMap[`add:${addSeq}`] = edit;
      }

      if (Object.keys(editsMap).length === 0) {
        throw new Error('ASSET_MODEL_RENT_ROLL: provide at least one valid edit (by unit label/id) or addition (with label + areaSqm).');
      }
      applyAssetRentRollEdits(kernel.current, propertyId, editsMap);

      // Recompute on the full edited roll for immediate feedback.
      const merged = mergeRentRollEdits(baseline, getAssetRentRollEdits(kernel.current, propertyId));
      const modelParams = getAssetModelParams(kernel.current, propertyId) ?? deriveDefaultParams(merged);
      const o = deriveScenarioOutcomes(merged, modelParams);
      toast.success(
        `Rent roll updated, passing ${fmtEuroM(merged.passingRentRoll)}, NOI ${fmtEuroM(merged.currentNoi)}, levered IRR ${o.leveredIrrPct.toFixed(1)}%`,
      );
    },
  },

  ASSET_MODEL_ADD_TAB: {
    action_type: 'ASSET_MODEL_ADD_TAB',
    traceLabels: { active: 'building', done: 'built', unit: 'tab' },
    targetEntityType: 'Property',
    description: `Adds (or updates) a custom tab/sheet on the focused asset's business-plan model, filled with a table you author. Use this to build ANY analysis the six core sheets don't cover, a scenario comparison, a tenant/lease analysis, a custom sensitivity, an exec summary, working notes, etc. The tab appears immediately and stays freely editable by the user.
    PREFER LIVE FORMULAS over hardcoded numbers: any cell whose value starts with "=" is evaluated live, e.g. "=B2*C2", "=SUM(B2:B6)", "=B3*(1-B4)", "=IRR(B10:H10)", "=NPV(0.08,B10:H10)". Formulas reference other cells WITHIN THIS TAB (A1-style: column letter + 1-based row). Lay out the input values in cells and COMPUTE the rest with formulas so the tab stays live, do not paste a static result you could derive. Standard Excel functions plus IRR and NPV are available.
    Provide 'name' (the tab label) and 'rows' (a 2D array of strings = the table, row-major; first row usually headers). mode 'replace' (default) overwrites a same-named tab; 'append' adds rows beneath. You cannot use the reserved core sheet names (Summary, Assumptions, Rent Roll, Cashflow, Debt & Returns, Sensitivity).`,
    evaluateLockState: (context) => {
      const permissionLock = assetManagementPermissionLock(context.kernel.current);
      if (permissionLock) return permissionLock;
      const { lockHint } = resolveFocusedPropertyId(context);
      if (lockHint) return { isLocked: true, unlockHint: lockHint };
      return { isLocked: false, unlockHint: '' };
    },
    parameters: [
      { name: 'name', type: 'string', description: 'Tab label (e.g. "Scenario comparison"). Not a reserved core sheet name.', required: true },
      { name: 'rows', type: 'string[][]', description: 'The table as a row-major 2D array of strings, e.g. [["Scenario","IRR","EM"],["Base","17.8%","2.08x"],["Upside","22%","2.4x"]]. First row = headers.', required: true },
      { name: 'mode', type: 'string', description: "'replace' (default) overwrites a same-named tab, 'append' adds rows beneath it.", required: false, options: ['replace', 'append'] },
      { name: 'propertyId', type: 'string', description: 'Target asset (Property) ID. Optional if a Property/Unit/Deal tied to a property is focused.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      let propertyId =
        typeof params.propertyId === 'string' && params.propertyId.trim().length > 0
          ? params.propertyId.trim()
          : null;
      if (!propertyId) propertyId = resolvePropertyIdFromSessionContext(kernel, sessionId);
      if (!propertyId) throw new Error('ASSET_MODEL_ADD_TAB: a Property must be focused, or pass "propertyId".');

      const name = typeof params.name === 'string' ? params.name.trim() : '';
      if (!name) throw new Error('ASSET_MODEL_ADD_TAB: "name" is required.');
      const RESERVED = ['summary', 'assumptions', 'rent roll', 'cashflow', 'debt & returns', 'sensitivity'];
      if (RESERVED.includes(name.toLowerCase())) {
        throw new Error(`ASSET_MODEL_ADD_TAB: "${name}" is a reserved core sheet, pick a different tab name.`);
      }

      const toRows = (raw: unknown): string[][] => {
        if (!Array.isArray(raw)) return [];
        return raw.map((row) => {
          if (Array.isArray(row)) return row.map((c) => (c === null || c === undefined ? '' : String(c)));
          if (row === null || row === undefined) return [];
          if (typeof row === 'object') return Object.values(row as Record<string, unknown>).map((c) => (c === null || c === undefined ? '' : String(c)));
          return [String(row)];
        });
      };
      const rows = toRows(params.rows);
      if (rows.length === 0) {
        throw new Error('ASSET_MODEL_ADD_TAB: "rows" must be a non-empty 2D array of strings.');
      }
      const mode = params.mode === 'append' ? 'append' : 'replace';
      upsertAssetCustomSheet(kernel.current, propertyId, name, rows, mode);
      toast.success(`Added tab "${name}" (${rows.length} row${rows.length === 1 ? '' : 's'})`);
    },
  },

  ASSET_MODEL_WRITE_CELLS: {
    action_type: 'ASSET_MODEL_WRITE_CELLS',
    traceLabels: { active: 'writing', done: 'wrote', unit: 'cell', pluralUnit: 'cells' },
    targetEntityType: 'Property',
    description: `Writes or edits specific cells, values OR live formulas, on a custom tab of the focused asset's model (creates the tab if missing). Use to set or fix individual cells/formulas precisely without resending the whole table (ASSET_MODEL_ADD_TAB rewrites a tab; this patches cells, so it's how you EDIT an existing formula).
    A cell value starting with "=" is a live formula referencing other cells in the SAME tab, e.g. "=B2*C2", "=SUM(B2:B6)", "=IRR(B10:H10)", "=NPV(0.08,B10:H10)". Standard Excel functions plus IRR and NPV are available.
    Targets CUSTOM tabs only; the six core sheets are computed and not directly writable (edit those via ASSET_MODEL_EDIT for assumptions or ASSET_MODEL_RENT_ROLL for tenancies).`,
    evaluateLockState: (context) => {
      const permissionLock = assetManagementPermissionLock(context.kernel.current);
      if (permissionLock) return permissionLock;
      const { lockHint } = resolveFocusedPropertyId(context);
      if (lockHint) return { isLocked: true, unlockHint: lockHint };
      return { isLocked: false, unlockHint: '' };
    },
    parameters: [
      { name: 'name', type: 'string', description: 'Custom tab to write into (created if missing). Not a reserved core sheet name.', required: true },
      { name: 'cells', type: 'object[]', description: 'Cells to set. Each: { cell: A1-ref e.g. "B3", value: string }. A value starting with "=" is a live formula. e.g. [{"cell":"A1","value":"NOI"},{"cell":"B1","value":"=B2*(1-B3)"}].', required: true },
      { name: 'propertyId', type: 'string', description: 'Target asset (Property) ID. Optional if a Property is focused.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      let propertyId =
        typeof params.propertyId === 'string' && params.propertyId.trim().length > 0
          ? params.propertyId.trim()
          : null;
      if (!propertyId) propertyId = resolvePropertyIdFromSessionContext(kernel, sessionId);
      if (!propertyId) throw new Error('ASSET_MODEL_WRITE_CELLS: a Property must be focused, or pass "propertyId".');

      const name = typeof params.name === 'string' ? params.name.trim() : '';
      if (!name) throw new Error('ASSET_MODEL_WRITE_CELLS: "name" is required.');
      const RESERVED = ['summary', 'assumptions', 'rent roll', 'cashflow', 'debt & returns', 'sensitivity'];
      if (RESERVED.includes(name.toLowerCase())) {
        throw new Error(`ASSET_MODEL_WRITE_CELLS: "${name}" is a reserved core sheet, write to a custom tab instead.`);
      }

      const { cellMap, rejected } = parseCellPatch(params.cells);
      const written = Object.keys(cellMap).length;
      if (written === 0) {
        throw new Error(
          `ASSET_MODEL_WRITE_CELLS: provide at least one cell as { cell: "B3", value: "..." }.${describeRejectedCells(rejected)}`,
        );
      }
      writeAssetCustomSheetCells(kernel.current, propertyId, name, cellMap, true);
      toast.success(`Wrote ${written} cell${written === 1 ? '' : 's'} to "${name}"`);
      if (rejected.length > 0) {
        throw new Error(
          `ASSET_MODEL_WRITE_CELLS: wrote ${written} cell${written === 1 ? '' : 's'} to "${name}".${describeRejectedCells(rejected)}`,
        );
      }
    },
  },

  ASSET_MODEL_REMOVE_TAB: {
    action_type: 'ASSET_MODEL_REMOVE_TAB',
    traceLabels: { active: 'removing', done: 'removed', unit: 'tab' },
    targetEntityType: 'Property',
    description: `Removes a custom tab/sheet (one previously created with ASSET_MODEL_ADD_TAB) from the focused asset's model. Cannot remove the six core sheets.`,
    evaluateLockState: (context) => {
      const permissionLock = assetManagementPermissionLock(context.kernel.current);
      if (permissionLock) return permissionLock;
      const { lockHint } = resolveFocusedPropertyId(context);
      if (lockHint) return { isLocked: true, unlockHint: lockHint };
      return { isLocked: false, unlockHint: '' };
    },
    parameters: [
      { name: 'name', type: 'string', description: 'Exact label of the custom tab to remove.', required: true },
      { name: 'propertyId', type: 'string', description: 'Target asset (Property) ID. Optional if a Property is focused.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      let propertyId =
        typeof params.propertyId === 'string' && params.propertyId.trim().length > 0
          ? params.propertyId.trim()
          : null;
      if (!propertyId) propertyId = resolvePropertyIdFromSessionContext(kernel, sessionId);
      if (!propertyId) throw new Error('ASSET_MODEL_REMOVE_TAB: a Property must be focused, or pass "propertyId".');
      const name = typeof params.name === 'string' ? params.name.trim() : '';
      if (!name) throw new Error('ASSET_MODEL_REMOVE_TAB: "name" is required.');
      removeAssetCustomSheet(kernel.current, propertyId, name);
      toast.success(`Removed tab "${name}"`);
    },
  },

  ANALYST_WRITE_CELLS: {
    action_type: 'ANALYST_WRITE_CELLS',
    traceLabels: { active: 'writing', done: 'wrote', unit: 'cell', pluralUnit: 'cells' },
    targetEntityType: 'ABSOLUTE',
    description: `Writes or edits specific cells, values OR live formulas, on a sheet of the table the user has OPEN in the Analyst tool (creates the sheet if missing). Use to set or fix individual cells/formulas precisely without resending the whole table.
    A cell value starting with "=" is a live formula referencing other cells, within the sheet (A1-style, e.g. "=B2*C2", "=SUM(B2:B6)", "=IRR(B2:B8)", "=NPV(0.08,B2:B8)") or across sheets (e.g. "='Inputs'!B2"). Standard Excel functions plus IRR and NPV are available. Lay out inputs as values and COMPUTE the rest with formulas so the table stays live.`,
    evaluateLockState: (context) => analystLock(context.kernel.current),
    parameters: [
      { name: 'sheet', type: 'string', description: 'Sheet to write into (created if missing). Defaults to the first sheet if omitted.', required: false },
      { name: 'cells', type: 'object[]', description: 'Cells to set. Each: { cell: A1-ref e.g. "B3", value: string, why?: short reason }. A value starting with "=" is a live formula. The optional "why" (a brief rationale) is shown to the user on hover over the changed cell, include it to "show your work". e.g. [{"cell":"B1","value":"=SUM(B2:B6)","why":"total revenue"}].', required: true },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const active = resolveAgentTable(kernel.current, options);
      if (!active) throw new Error('ANALYST_WRITE_CELLS: open a table in the Analyst tool first.');
      const st = useTablesStore.getState();
      const doc = st.docs[active.id];
      if (!doc) throw new Error('ANALYST_WRITE_CELLS: the open table is no longer available.');
      if (!canEditTable(doc)) throw new Error('This shared asset workbook is read only for your account.');
      const sheetName =
        typeof params.sheet === 'string' && params.sheet.trim() ? params.sheet.trim() : doc.sheets[0]?.name ?? 'Sheet 1';

      const target = doc.sheets.find((sh) => sh.name.toLowerCase() === sheetName.toLowerCase());
      if (target && isStructuralSheet(doc, target.id)) throw new Error('ANALYST_WRITE_CELLS: Rent Roll and Lease Dates show the leases on file and are not typed into. Change a lease in Source’s rent roll or on its deal, an assumption with ANALYST_SET_ASSUMPTION, or write your analysis in another sheet.');
      const { cellMap, whyByKey, rejected } = parseCellPatch(params.cells);
      const written = Object.keys(cellMap).length;
      if (written === 0) {
        throw new Error(
          `ANALYST_WRITE_CELLS: provide at least one cell as { cell: "B3", value: "..." }.${describeRejectedCells(rejected)}`,
        );
      }
      st.writeSheetCells(active.id, sheetName, cellMap);
      if (kernel.current) await saveSharedTable(kernel.current, active.id);
      // Record the edit so the grid can highlight the changed cells (+ show the "why" on hover).
      st.setAgentEdits({
        docId: active.id,
        sheetName,
        keys: Object.keys(cellMap),
        whyByKey: Object.keys(whyByKey).length ? whyByKey : undefined,
        at: Date.now(),
      });
      st.requestAnalystView();
      st.markAgentActive(); // keep the "agent working" glow alive while it edits
      toast.success(`Wrote ${written} cell${written === 1 ? '' : 's'} to "${sheetName}"`);
      if (rejected.length > 0) {
        throw new Error(
          `ANALYST_WRITE_CELLS: wrote ${written} cell${written === 1 ? '' : 's'} to "${sheetName}".${describeRejectedCells(rejected)}`,
        );
      }
    },
  },

  ANALYST_SET_ASSUMPTION: {
    action_type: 'ANALYST_SET_ASSUMPTION',
    traceLabels: { active: 'setting', done: 'set', unit: 'assumption' },
    targetEntityType: 'ABSOLUTE',
    description: `Changes one of the open asset's financial assumptions, the single set that values the asset in Analyst and prices every Merch scenario (ASSET MODEL block). Owners only. Give the basis whenever you set the yield or the reference rent, and keep "mockup" true while the figure is not the landlord's own.`,
    evaluateLockState: (context) => analystLock(context.kernel.current),
    parameters: [
      { name: 'field', type: 'string', description: 'yieldPct (2-15), referenceRent (EUR/m²/yr, 1-5000), worksPerSqm (EUR/m²), rentFreeMonths (0-36), indexationPct (%/yr), or mockup (true/false).', required: true },
      { name: 'value', type: 'string', description: 'The new value: a number, "null" to unset, or true/false for mockup.', required: true },
      { name: 'basis', type: 'string', description: 'Where the figure comes from (required for yieldPct and referenceRent).', required: false },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      const active = resolveAgentTable(k, options);
      const propertyId = active ? useTablesStore.getState().docs[active.id]?.propertyId : null;
      if (!k || !propertyId) throw new Error('ANALYST_SET_ASSUMPTION: open the asset’s shared workbook first.');
      const property = await k.syncEntity('Property', propertyId, { force: true, requireFresh: true });
      const current = property?.analystAssumptions;
      if (!current) throw new Error('ANALYST_SET_ASSUMPTION: the asset’s assumptions could not be read. Retry.');
      const field = String(params.field ?? '');
      const raw = String(params.value ?? '').trim();
      const basis = typeof params.basis === 'string' ? params.basis.trim().slice(0, 300) : '';
      const numeric = ['yieldPct', 'referenceRent', 'worksPerSqm', 'rentFreeMonths', 'indexationPct'];
      if (field !== 'mockup' && !numeric.includes(field)) throw new Error(`ANALYST_SET_ASSUMPTION: unknown field "${field}".`);
      const value = field === 'mockup' ? raw === 'true' : raw === 'null' ? null : Number(raw);
      if (typeof value === 'number' && !Number.isFinite(value)) throw new Error('ANALYST_SET_ASSUMPTION: "value" must be a number, "null", or true/false for mockup.');
      if ((field === 'yieldPct' || field === 'referenceRent') && value !== null && !basis) throw new Error(`ANALYST_SET_ASSUMPTION: give the basis of the ${field === 'yieldPct' ? 'yield' : 'reference rent'}.`);
      const { updatedAt: _at, updatedBy: _by, ...next } = current;
      const payload = { ...next, [field]: value, ...(field === 'yieldPct' && basis ? { yieldBasis: basis } : {}), ...(field === 'referenceRent' && basis ? { referenceRentBasis: basis } : {}) };
      await k.mutateInventory([{ entity: 'Property', action: 'UPDATE', id: propertyId, payload: { analystAssumptions: payload } }], `Set the asset’s ${field}`);
      await k.syncEntity('Property', propertyId, { force: true, requireFresh: true });
      kernel.current?.logUserAction('Analyst Assumption Set', { propertyId, field });
    },
  },
  ANALYST_ADD_SHEET: {
    action_type: 'ANALYST_ADD_SHEET',
    traceLabels: { active: 'building', done: 'built', unit: 'sheet' },
    targetEntityType: 'ABSOLUTE',
    description: `Authors a whole sheet (table) on the table the user has OPEN in the Analyst tool, filled with a 2D array of rows you provide. Use to build ANY table, a model, a comparison, a schedule, an exec summary. PREFER LIVE FORMULAS over hardcoded numbers: any cell starting with "=" is evaluated live (e.g. "=B2*C2", "=SUM(B2:B6)", "=IRR(B2:B8)") and may reference other sheets ("='Inputs'!B2"). Lay out inputs as values and COMPUTE the rest with formulas.
    Provide 'name' (the sheet label) and 'rows' (a row-major 2D array of strings; first row usually headers). mode 'replace' (default) overwrites a same-named sheet; 'append' adds rows beneath it.`,
    evaluateLockState: (context) => analystLock(context.kernel.current),
    parameters: [
      { name: 'name', type: 'string', description: 'Sheet label (e.g. "P&L").', required: true },
      { name: 'rows', type: 'string[][]', description: 'The table as a row-major 2D array of strings, e.g. [["Item","2024","2025"],["Revenue","1000","=B2*1.1"]]. First row = headers.', required: true },
      { name: 'mode', type: 'string', description: "'replace' (default) overwrites a same-named sheet, 'append' adds rows beneath it.", required: false, options: ['replace', 'append'] },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const active = resolveAgentTable(kernel.current, options);
      if (!active) throw new Error('ANALYST_ADD_SHEET: open a table in the Analyst tool first.');
      const name = typeof params.name === 'string' ? params.name.trim() : '';
      if (!name) throw new Error('ANALYST_ADD_SHEET: "name" is required.');
      const rows = toRows2D(params.rows);
      if (rows.length === 0) throw new Error('ANALYST_ADD_SHEET: "rows" must be a non-empty 2D array of strings.');
      const mode = params.mode === 'append' ? 'append' : 'replace';
      const doc = useTablesStore.getState().docs[active.id];
      if (!canEditTable(doc)) throw new Error('This shared asset workbook is read only for your account.');
      const existing = doc.sheets.find((sheet) => sheet.name.toLowerCase() === name.toLowerCase());
      if (existing && isStructuralSheet(doc, existing.id) && mode === 'replace') throw new Error('Use ANALYST_WRITE_CELLS for targeted corrections to permanent shared sheets; do not rebuild them.');
      useTablesStore.getState().upsertSheet(active.id, name, rows, mode);
      if (kernel.current) await saveSharedTable(kernel.current, active.id);
      useTablesStore.getState().requestAnalystView();
      useTablesStore.getState().markAgentActive(); // keep the "agent working" glow alive
      toast.success(`${mode === 'append' ? 'Appended to' : 'Built'} sheet "${name}" (${rows.length} row${rows.length === 1 ? '' : 's'})`);
    },
  },

  ANALYST_REMOVE_SHEET: {
    action_type: 'ANALYST_REMOVE_SHEET',
    traceLabels: { active: 'removing', done: 'removed', unit: 'sheet' },
    targetEntityType: 'ABSOLUTE',
    description: `Removes a sheet (by name) from the table the user has OPEN in the Analyst tool. A table always keeps at least one sheet.`,
    evaluateLockState: (context) => analystLock(context.kernel.current),
    parameters: [
      { name: 'name', type: 'string', description: 'Exact label of the sheet to remove.', required: true },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const active = resolveAgentTable(kernel.current, options);
      if (!active) throw new Error('ANALYST_REMOVE_SHEET: open a table in the Analyst tool first.');
      const name = typeof params.name === 'string' ? params.name.trim() : '';
      if (!name) throw new Error('ANALYST_REMOVE_SHEET: "name" is required.');
      const st = useTablesStore.getState();
      const doc = st.docs[active.id];
      const sheet = doc?.sheets.find((sh) => sh.name.toLowerCase() === name.toLowerCase()) ?? null;
      if (!sheet) throw new Error(`ANALYST_REMOVE_SHEET: no sheet named "${name}".`);
      if (!canEditTable(doc) || isStructuralSheet(doc, sheet.id)) throw new Error('This sheet cannot be removed: shared readers cannot edit, and Rent Roll / Lease Dates are permanent.');
      st.removeSheet(active.id, sheet.id);
      if (kernel.current) await saveSharedTable(kernel.current, active.id);
      toast.success(`Removed sheet "${sheet.name}"`);
    },
  },

  ANALYST_CREATE_TABLE: {
    action_type: 'ANALYST_CREATE_TABLE',
    traceLabels: { active: 'creating', done: 'created', unit: 'table' },
    targetEntityType: 'ABSOLUTE',
    description: `Creates a new personal Analyst table, optionally seeded with rows. Exception: when propertyId is supplied, opens that asset's canonical shared Rent Roll / Lease Dates workbook, initializing it from recorded data only if absent; never creates a personal copy or overwrites saved edits. Shared initialization does not accept rows.`,
    evaluateLockState: (context) => {
      const permissionLock = analystPermissionLock(context.kernel.current);
      if (permissionLock) return permissionLock;
      return { isLocked: false, unlockHint: '' };
    },
    parameters: [
      { name: 'name', type: 'string', description: 'Table name (e.g. "FY25 budget").', required: false },
      { name: 'propertyId', type: 'string', description: 'Only for the canonical shared asset rent roll. Reuses the asset workbook; never duplicates it.', required: false },
      { name: 'rows', type: 'string[][]', description: 'Optional initial rows for the first sheet (row-major 2D string array). First row usually headers.', required: false },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      if (typeof params.propertyId === 'string' && params.propertyId.trim()) {
        if (toRows2D(params.rows).length) throw new Error('Shared rent rolls initialize from recorded asset data, not supplied rows.');
        if (!kernel.current) throw new Error('Analyst is unavailable.');
        await loadSharedTables(kernel.current);
        const store = useTablesStore.getState();
        const shared = Object.values(store.docs).find((doc) => doc.propertyId === params.propertyId);
        if (!shared) throw new Error('Shared asset not found or access denied.');
        store.openDoc(shared.id);
        bindMasterDocIfMasterTurn(kernel.current, options, shared.id);
        store.requestAnalystView();
        return;
      }
      const st = useTablesStore.getState();
      const name = typeof params.name === 'string' && params.name.trim() ? params.name.trim() : undefined;
      const id = st.createDoc(name);
      const rows = toRows2D(params.rows);
      if (rows.length > 0) {
        const first = useTablesStore.getState().docs[id]?.sheets[0]?.name ?? 'Sheet 1';
        useTablesStore.getState().upsertSheet(id, first, rows, 'replace');
      }
      // On a master turn, bind this new table to the session so this master chat
      // (and only it) targets it on later turns, never the global active doc.
      bindMasterDocIfMasterTurn(kernel.current, options, id);
      useTablesStore.getState().requestAnalystView();
      toast.success(`Created table "${useTablesStore.getState().docs[id]?.name ?? name ?? 'Untitled table'}"`);
    },
  },

  ANALYST_OPEN_TABLE: {
    action_type: 'ANALYST_OPEN_TABLE',
    traceLabels: { active: 'opening', done: 'opened', unit: 'table' },
    targetEntityType: 'ABSOLUTE',
    description: `Opens an existing personal table or canonical shared asset workbook by ID, asset ID, name, or sheet name. Loads accessible shared projects before resolving. Shared asset rent-roll build/rebuild requests reuse this canonical workbook; they never create personal copies. Ordinary new personal models use ANALYST_CREATE_TABLE.`,
    evaluateLockState: (context) => {
      const permissionLock = analystPermissionLock(context.kernel.current);
      if (permissionLock) return permissionLock;
      return { isLocked: false, unlockHint: '' };
    },
    parameters: [
      { name: 'table', type: 'string', description: 'What to open, a table name/id, OR the name of a sheet inside a table (as listed under "ANALYST TABLES"). Sheet names open their containing table and switch to that sheet.', required: true },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const q = typeof params.table === 'string' ? params.table.trim() : '';
      if (!q) throw new Error('ANALYST_OPEN_TABLE: "table" (a table name/id or sheet name) is required.');
      const existingPersonal = Object.values(useTablesStore.getState().docs).find((doc) => !doc.propertyId && (doc.id === q || doc.name.toLowerCase().includes(q.toLowerCase()) || doc.sheets.some((sheet) => sheet.name.toLowerCase().includes(q.toLowerCase()))));
      if (!existingPersonal && kernel.current) await loadSharedTables(kernel.current);
      const st = useTablesStore.getState();
      const lower = q.toLowerCase();
      // 1) Match a table by id, then exact name, then partial name.
      let id: string | null = st.docs[q] ? q : Object.values(st.docs).find((doc) => doc.propertyId === q)?.id ?? null;
      if (!id) {
        const exact = Object.values(st.docs).find((d) => d.name.toLowerCase() === lower);
        const partial = exact ?? Object.values(st.docs).find((d) => d.name.toLowerCase().includes(lower));
        id = partial?.id ?? null;
      }
      // 2) Fall back to a SHEET name inside any table (e.g. a rent roll added via
      //    ANALYST_ADD_SHEET into an existing table); open that table + focus the sheet.
      let focusSheet: string | null = null;
      if (!id) {
        for (const d of Object.values(st.docs)) {
          const sheet = d.sheets.find((s) => s.name.toLowerCase() === lower)
            ?? d.sheets.find((s) => s.name.toLowerCase().includes(lower));
          if (sheet) { id = d.id; focusSheet = sheet.name; break; }
        }
      }
      if (!id) {
        const existing = Object.values(st.docs).map((d) => `"${d.name}"`).join(', ') || '(no tables exist yet)';
        throw new Error(
          `ANALYST_OPEN_TABLE: no table or sheet named "${q}" exists. ` +
            `The Analyst tables that DO exist are: ${existing}. ` +
            `Do NOT call ANALYST_OPEN_TABLE for "${q}" again, it will keep failing. ` +
            `Next step: either ANALYST_OPEN_TABLE one of the existing names above, or call ANALYST_CREATE_TABLE once ` +
            `(name "${q}", with the rows) to create it, ANALYST_CREATE_TABLE both creates AND opens the table, so do not follow it with another ANALYST_OPEN_TABLE.`,
        );
      }
      st.openDoc(id);
      if (focusSheet) useTablesStore.setState({ focusSheetName: focusSheet });
      // On a master turn, bind the opened table to the session (see ANALYST_CREATE_TABLE).
      bindMasterDocIfMasterTurn(kernel.current, options, id);
      st.requestAnalystView();
      const docName = st.docs[id]?.name ?? q;
      toast.success(focusSheet ? `Opened "${docName}" → sheet "${focusSheet}"` : `Opened table "${docName}"`);
    },
  },

  ANALYST_READ_RANGE: {
    action_type: 'ANALYST_READ_RANGE',
    traceLabels: { active: 'reading', done: 'read', unit: 'range' },
    targetEntityType: 'ABSOLUTE',
    description: `Reads the CURRENT COMPUTED VALUES (and errors) of a cell range from the table open in the Analyst tool, use to inspect exact results before answering, or precedent values when debugging a formula. The values appear in your context on the NEXT step as a "LAST READ" block (this tool does not return them inline). Reads the full range even beyond what the always-on table snapshot shows.`,
    evaluateLockState: (context) => analystLock(context.kernel.current),
    parameters: [
      { name: 'range', type: 'string', description: 'A1 range to read, e.g. "B2:D20", a column "B2:B40", or a single cell "B5".', required: true },
      { name: 'sheet', type: 'string', description: 'Sheet to read from. Defaults to the sheet currently in view.', required: false },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const active = resolveAgentTable(kernel.current, options);
      if (!active) throw new Error('ANALYST_READ_RANGE: open a table in the Analyst tool first.');
      const st = useTablesStore.getState();
      const doc = st.docs[active.id];
      if (!doc) throw new Error('ANALYST_READ_RANGE: the open table is no longer available.');
      const rangeStr = typeof params.range === 'string' ? params.range.trim() : '';
      const rng = parseA1Range(rangeStr);
      if (!rng) throw new Error('ANALYST_READ_RANGE: "range" must be an A1 reference like "B2:D20" or "B5".');
      const wanted = typeof params.sheet === 'string' ? params.sheet.trim().toLowerCase() : '';
      const sheet =
        (wanted ? doc.sheets.find((s) => s.name.toLowerCase() === wanted) : null) ??
        doc.sheets.find((s) => s.id === st.activeSheetId) ??
        doc.sheets[0];
      if (!sheet) throw new Error('ANALYST_READ_RANGE: no sheet to read.');
      let wb: Record<string, Record<string, unknown>> = {};
      try {
        wb = computeWorkbook(doc.sheets.map((s) => ({ name: s.name, cells: s.cells }))) as unknown as Record<string, Record<string, unknown>>;
      } catch {
        /* best-effort: leave wb empty, fall back to raw cell text below */
      }
      const computed = wb[sheet.name.toLowerCase()] ?? {};
      const MAX_READ = 400;
      const lines: string[] = [];
      let count = 0;
      for (let r = rng.r0; r <= rng.r1 && count < MAX_READ; r += 1) {
        const rowCells: string[] = [];
        for (let c = rng.c0; c <= rng.c1 && count < MAX_READ; c += 1) {
          const k = `${r},${c}`;
          const cv = computed[k];
          const raw = sheet.cells[k];
          if ((cv === undefined || cv === null || cv === '') && (raw === undefined || raw === '')) continue;
          const v = cv !== undefined && cv !== null && cv !== '' ? cv : raw;
          const valStr = typeof v === 'number' ? v.toLocaleString('en-US', { maximumFractionDigits: 4 }) : String(v);
          rowCells.push(`${keyToA1(k)}=${valStr.length > 60 ? `${valStr.slice(0, 60)}…` : valStr}`);
          count += 1;
        }
        if (rowCells.length) lines.push(rowCells.join(', '));
      }
      const text = lines.length ? lines.join('\n') + (count >= MAX_READ ? '\n…(truncated)' : '') : '(all cells in range are empty)';
      st.setLastRead({ sheet: sheet.name, range: rangeStr, text });
      st.requestAnalystView();
      toast.success(`Read ${sheet.name}!${rangeStr}`);
    },
  },

  ANALYST_FORMAT_CELLS: {
    action_type: 'ANALYST_FORMAT_CELLS',
    traceLabels: { active: 'formatting', done: 'formatted', unit: 'cell', pluralUnit: 'cells' },
    targetEntityType: 'ABSOLUTE',
    description: `Formats cells on the table open in the Analyst tool, bold, italic, underline, text colour, background/highlight colour, alignment, and Excel number format. Use to style headers, highlight totals/inputs, or apply a number format (e.g. "#,##0", "0.0%", "€#,##0"). Provide A1 cells or ranges plus the style to apply. This changes presentation only; it never alters values or formulas.`,
    evaluateLockState: (context) => analystLock(context.kernel.current),
    parameters: [
      { name: 'sheet', type: 'string', description: 'Sheet to format. Defaults to the sheet currently in view / first sheet.', required: false },
      { name: 'cells', type: 'object[]', description: 'Targets + styles. Each: { range: "A1" or "A1:D1", bold?: bool, italic?: bool, underline?: bool, color?: "#RRGGBB", background?: "#RRGGBB", align?: "left"|"center"|"right", numFmt?: Excel code e.g. "#,##0" | "0.0%" | "€#,##0" }. e.g. [{"range":"A1:D1","bold":true,"background":"#1F3864","color":"#FFFFFF"},{"range":"B2:B20","numFmt":"#,##0"}].', required: true },
    ],
    availableInModes: ['commander', 'master'],
    headlessCompatible: true,
    handler: async (params, kernel, options) => {
      const active = resolveAgentTable(kernel.current, options);
      if (!active) throw new Error('ANALYST_FORMAT_CELLS: open a table in the Analyst tool first.');
      const st = useTablesStore.getState();
      const doc = st.docs[active.id];
      if (!doc) throw new Error('ANALYST_FORMAT_CELLS: the open table is no longer available.');
      if (!canEditTable(doc)) throw new Error('This shared asset workbook is read only for your account.');
      const wanted = typeof params.sheet === 'string' ? params.sheet.trim().toLowerCase() : '';
      const sheet =
        (wanted ? doc.sheets.find((s) => s.name.toLowerCase() === wanted) : null) ??
        doc.sheets.find((s) => s.id === st.activeSheetId) ??
        doc.sheets[0];
      if (!sheet) throw new Error('ANALYST_FORMAT_CELLS: no sheet to format.');
      const specs = Array.isArray(params.cells) ? params.cells : [];
      const allKeys: string[] = [];
      const MAX_FORMAT_CELLS = 8000; // guard: a blanket range (e.g. B2:B100000) would otherwise
                                     // materialize millions of style entries, freezing the tab and
                                     // blowing the localStorage quota. Models never need this many.
      for (const raw of specs) {
        if (!raw || typeof raw !== 'object') continue;
        const c = raw as Record<string, unknown>;
        const ref = typeof c.range === 'string' ? c.range : typeof c.cell === 'string' ? c.cell : '';
        const rng = parseA1Range(ref);
        if (!rng) continue;
        const patch: Partial<CellStyle> = {};
        if (typeof c.bold === 'boolean') patch.bold = c.bold;
        if (typeof c.italic === 'boolean') patch.italic = c.italic;
        if (typeof c.underline === 'boolean') patch.underline = c.underline;
        if (typeof c.color === 'string') patch.color = c.color;
        if (typeof c.background === 'string') patch.background = c.background;
        if (c.align === 'left' || c.align === 'center' || c.align === 'right') patch.align = c.align;
        if (typeof c.numFmt === 'string') patch.numFmt = c.numFmt;
        if (Object.keys(patch).length === 0) continue;
        const cells = (rng.r1 - rng.r0 + 1) * (rng.c1 - rng.c0 + 1);
        if (allKeys.length + cells > MAX_FORMAT_CELLS) {
          throw new Error(`ANALYST_FORMAT_CELLS: range "${ref}" is too large (${allKeys.length + cells} cells > ${MAX_FORMAT_CELLS}). Format a tighter range, a blanket range freezes the grid.`);
        }
        const keys: string[] = [];
        for (let r = rng.r0; r <= rng.r1; r += 1) for (let col = rng.c0; col <= rng.c1; col += 1) keys.push(`${r},${col}`);
        st.setCellStyles(active.id, sheet.id, keys, patch);
        allKeys.push(...keys);
      }
      if (allKeys.length === 0) {
        throw new Error('ANALYST_FORMAT_CELLS: provide at least one { range, ...style } (e.g. {"range":"A1:D1","bold":true}).');
      }
      st.setAgentEdits({ docId: active.id, sheetName: sheet.name, keys: allKeys, at: Date.now() });
      if (kernel.current) await saveSharedTable(kernel.current, active.id);
      // setCellStyles intentionally doesn't bump rev; nudge it so the grid remounts and repaints.
      useTablesStore.setState((s) => ({ rev: s.rev + 1 }));
      st.requestAnalystView();
      toast.success(`Formatted ${allKeys.length} cell${allKeys.length === 1 ? '' : 's'} on "${sheet.name}"`);
    },
  },

  // MASTER-only: surface a SLICE of the model the Master built as a clean table
  // right in the master chat, so the user sees the relevant numbers without
  // opening the Analyst tool. Values are the LIVE computed results (computeWorkbook)
  // with their number formats, rendered as a GFM markdown table that the chat
  // renders + styles. Delivered as a DURABLE persisted message so it survives
  // reload (like the Master's image cards). This is the "show parts of the table
  // on the chat" capability.
  ANALYST_SHOW_IN_CHAT: {
    action_type: 'ANALYST_SHOW_IN_CHAT',
    traceLabels: { active: 'showing', done: 'showed', unit: 'table' },
    targetEntityType: 'ABSOLUTE',
    description: `Shows a SLICE of the model you built as a clean table right here in the chat, so the user sees the relevant numbers without opening the Analyst tool. Use it when part of the model is worth seeing: the headline returns once you finish, a summary block, or a sheet the user asked for. Provide the A1 'range' to show (e.g. "A1:F14", include the header row) and, optionally, the 'sheet' and a short 'title'. The values shown are the LIVE computed results with their number formats. Keep each slice focused (a screenful); call it again for another slice. The full model always stays available in the Analyst workspace.`,
    evaluateLockState: (context) => analystLock(context.kernel.current),
    parameters: [
      { name: 'range', type: 'string', description: 'A1 range to show, e.g. "A1:F14". Include the header row (its first row becomes the table header). If omitted, shows the sheet\'s used range (capped to a screenful).', required: false },
      { name: 'sheet', type: 'string', description: 'Sheet to show. Defaults to the sheet in view / first sheet.', required: false },
      { name: 'title', type: 'string', description: 'Optional short caption shown above the table (e.g. "Returns & Valuation").', required: false },
    ],
    availableInModes: ['master'],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      if (!k) throw new Error('ANALYST_SHOW_IN_CHAT: Kernel not available.');
      const masterSessionId =
        typeof options?.sessionId === 'string' && options.sessionId.trim().length > 0 ? options.sessionId.trim() : '';
      if (!masterSessionId) throw new Error('ANALYST_SHOW_IN_CHAT: a master session id is required.');
      const active = resolveAgentTable(k, options);
      if (!active) throw new Error('ANALYST_SHOW_IN_CHAT: build or open a table first (ANALYST_CREATE_TABLE).');
      const st = useTablesStore.getState();
      const doc = st.docs[active.id];
      if (!doc) throw new Error('ANALYST_SHOW_IN_CHAT: the table is no longer available.');
      const wanted = typeof params.sheet === 'string' ? params.sheet.trim().toLowerCase() : '';
      const sheet =
        (wanted ? doc.sheets.find((s) => s.name.toLowerCase() === wanted) : null) ??
        doc.sheets.find((s) => s.id === st.activeSheetId) ??
        doc.sheets[0];
      if (!sheet) throw new Error('ANALYST_SHOW_IN_CHAT: no sheet to show.');

      // Resolve the range: an explicit A1 range, else the sheet's used range. Cap
      // it so a slice stays a screenful, the point is PART of the table, not a dump.
      const MAX_ROWS = 40;
      const MAX_COLS = 16;
      let r0 = 0;
      let c0 = 0;
      let r1 = 0;
      let c1 = 0;
      const rangeStr = typeof params.range === 'string' ? params.range.trim() : '';
      const parsed = rangeStr ? parseA1Range(rangeStr) : null;
      if (parsed) {
        r0 = parsed.r0;
        c0 = parsed.c0;
        r1 = parsed.r1;
        c1 = parsed.c1;
      } else {
        let maxR = 0;
        let maxC = 0;
        for (const key of Object.keys(sheet.cells)) {
          const parts = key.split(',');
          const rr = Number(parts[0]);
          const cc = Number(parts[1]);
          if (Number.isFinite(rr) && rr > maxR) maxR = rr;
          if (Number.isFinite(cc) && cc > maxC) maxC = cc;
        }
        r1 = maxR;
        c1 = maxC;
      }
      r1 = Math.min(r1, r0 + MAX_ROWS - 1);
      c1 = Math.min(c1, c0 + MAX_COLS - 1);
      if (r1 < r0 || c1 < c0) throw new Error('ANALYST_SHOW_IN_CHAT: nothing to show in that range.');

      // Emit a LIVE table marker, not a static snapshot: a fenced
      // ```fater-analyst-table block holding the doc / sheet / range. The chat
      // renders it as a ChatGPT-style card bound to the live Analyst doc, so it
      // stays in sync with edits and can be opened in the Analyst editor.
      const rangeA1 = `${keyToA1(`${r0},${c0}`)}:${keyToA1(`${r1},${c1}`)}`;
      const titleRaw = typeof params.title === 'string' ? params.title.trim() : '';
      // Embed a STATIC snapshot of the formatted slice in the marker. The chat
      // message is server-durable, so this snapshot lets the card render the table
      // on any device even when the client-only Analyst doc isn't present there;
      // the card still prefers the live doc (and its Open-in-editor link) when it
      // is available locally.
      const ssf = await loadSsf();
      const data = computeTableSlice(doc, sheet.name, rangeA1, ssf);
      const marker = JSON.stringify({ docId: active.id, sheet: sheet.name, range: rangeA1, title: titleRaw, data });
      const content = `\`\`\`fater-analyst-table\n${marker}\n\`\`\``;

      // Deliver as a DURABLE persisted message tagged to the master session so it
      // survives navigation / reload, exactly like the Master's image cards.
      await k.addPersistedAssistantMessage?.(content, 'Master', masterSessionId);
      return JSON.stringify({ sheet: sheet.name, range: rangeA1, docId: active.id });
    },
  },

  // MASTER-only: surface SOURCE data (a campaign shortlist, the campaign list, a
  // brand's contacts, or a brand fiche) as a clean read-only table right in the
  // master chat, so the user sees it without opening the Source tool. Rows are read
  // LIVE from the kernel inventory; a static snapshot is embedded in the durable
  // marker so the card survives reload / renders on any device. This is the Source
  // analog of ANALYST_SHOW_IN_CHAT.
  SOURCE_SHOW_IN_CHAT: {
    action_type: 'SOURCE_SHOW_IN_CHAT',
    traceLabels: { active: 'showing', done: 'showed', unit: 'table' },
    targetEntityType: 'ABSOLUTE',
    description: `Shows SOURCE data as a clean table right here in the chat, so the user sees it without opening the Source tool. Set 'kind' to: 'shortlist' (a campaign's scored brand fits), 'campaigns' (the campaign list), 'contacts' (a brand's decision-maker contacts), or 'brand' (a brand fiche). For shortlist/campaigns also set 'scope' to 'portfolio', 'asset', or 'unit' and pass the id you have (campaignId / portfolioId / propertyId). For contacts and brand pass 'brandId'. Rows come live from the inventory; the ids come from your source context. Keep each view focused; call again for another slice.`,
    parameters: [
      { name: 'kind', type: 'string', description: "What to show: 'shortlist', 'campaigns', 'contacts', or 'brand'.", required: true },
      { name: 'scope', type: 'string', description: "For shortlist/campaigns: 'portfolio', 'asset', or 'unit'. Defaults to 'asset'.", required: false },
      { name: 'campaignId', type: 'string', description: 'Campaign whose fits to show (kind=shortlist).', required: false },
      { name: 'portfolioId', type: 'string', description: 'Portfolio id (portfolio shortlist / campaigns).', required: false },
      { name: 'propertyId', type: 'string', description: 'Property id (asset shortlist / campaigns).', required: false },
      { name: 'brandId', type: 'string', description: 'Brand id (required for kind=contacts and kind=brand).', required: false },
      { name: 'statusFilter', type: 'string', description: "Optional status to keep for shortlist rows, e.g. 'INTERESTING'.", required: false },
      { name: 'columns', type: 'string', description: "Optional comma-separated columns to keep/drop (default = all). Shortlist: brand, fit, status, signal, evaluated, contacted, channel, response, comment, and the scope column (portfolio | property | unit). Contacts: name, title, seniority, email, phone, linkedin.", required: false },
      { name: 'limit', type: 'number', description: 'Optional cap on rows shown. By DEFAULT every matching row is shown (all the brands) in one scrollable card (query ceiling 999); only pass this to intentionally show a smaller, focused set. The card offers "See more in Source" for anything beyond the ceiling.', required: false },
      { name: 'title', type: 'string', description: 'Optional short caption shown above the table.', required: false },
    ],
    availableInModes: ['master'],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      if (!k) throw new Error('SOURCE_SHOW_IN_CHAT: Kernel not available.');
      const masterSessionId =
        typeof options?.sessionId === 'string' && options.sessionId.trim().length > 0 ? options.sessionId.trim() : '';
      if (!masterSessionId) throw new Error('SOURCE_SHOW_IN_CHAT: a master session id is required.');

      const kind = (typeof params.kind === 'string' ? params.kind.trim().toLowerCase() : '') as SourceCardKind;
      if (!['shortlist', 'campaigns', 'contacts', 'brand'].includes(kind)) {
        throw new Error("SOURCE_SHOW_IN_CHAT: 'kind' must be one of shortlist, campaigns, contacts, brand.");
      }
      const scopeRaw = typeof params.scope === 'string' ? params.scope.trim().toLowerCase() : '';
      const scope: SourceCardScope = scopeRaw === 'portfolio' ? 'portfolio' : scopeRaw === 'unit' ? 'unit' : 'asset';
      const campaignId = typeof params.campaignId === 'string' ? params.campaignId.trim() : '';
      const portfolioId = typeof params.portfolioId === 'string' ? params.portfolioId.trim() : '';
      const propertyId = typeof params.propertyId === 'string' ? params.propertyId.trim() : '';
      const brandId = typeof params.brandId === 'string' ? params.brandId.trim() : '';
      const statusFilter = typeof params.statusFilter === 'string' ? params.statusFilter.trim().toUpperCase() : '';
      const titleRaw = typeof params.title === 'string' ? params.title.trim() : '';
      // Rows shown in the scrollable card window. Default shows ALL matching rows: the
      // user wants to see every brand, not a top slice, and the card is a scrollable
      // window so a long list is fine. The query ceiling is 999; the "See more in Source"
      // footer covers anything beyond that. An explicit `limit` intentionally narrows it.
      const limit = Math.min(Math.max(1, Number(params.limit) || 999), 999);
      const columns = Array.isArray(params.columns)
        ? (params.columns as unknown[]).map((c) => String(c)).filter(Boolean)
        : typeof params.columns === 'string' && params.columns.trim()
          ? params.columns.split(',').map((s) => s.trim()).filter(Boolean)
          : undefined;

      const asRec = (x: unknown): Record<string, unknown> => (x !== null && typeof x === 'object' ? (x as Record<string, unknown>) : {});
      const readRows = (et: InventoryEntityType, ids: string[]): unknown[] => {
        const reg = k.getInventory()[et].registry as Record<string, unknown>;
        return ids.map((id) => reg[id]).filter(Boolean);
      };

      let entityType: InventoryEntityType;
      let rows: unknown[] = [];
      let rowIds: string[] = [];
      let total = 0; // full server-side count (for the "See more in Source" affordance)

      if (kind === 'shortlist') {
        if (scope === 'portfolio') {
          entityType = 'PortfolioFit';
          const res = await k.queryInventory('PortfolioFit', {
            filters: { ...(campaignId ? { campaignId } : {}), ...(portfolioId ? { portfolioId } : {}), sortBy: 'score', sortDir: 'desc' },
            limit,
            force: true,
            ttl: 0,
          });
          rowIds = res.ids;
          total = res.pagination?.total ?? res.ids.length;
        } else {
          entityType = 'Lead';
          const res = await k.queryInventory('Lead', {
            filters: { ...(propertyId ? { propertyId } : {}), ...(campaignId ? { campaignId } : {}), sortBy: 'fitScore', sortDir: 'desc' },
            limit,
            force: true,
            ttl: 0,
          });
          rowIds = res.ids;
          total = res.pagination?.total ?? res.ids.length;
        }
        rows = readRows(entityType, rowIds);
        if (statusFilter) {
          rows = rows.filter((r) => String(asRec(r).status ?? '').toUpperCase() === statusFilter);
          rowIds = rows.map((r) => String(asRec(r).id ?? '')).filter(Boolean);
        }
      } else if (kind === 'campaigns') {
        // ONE list at every granularity (app/api/inventory/campaigns): the scope is a `targetKind`
        // FILTER on it, not a choice of endpoint. The route also matches an asset against a
        // multi-asset run's declared coverage, not just the column, which the per-table routes
        // could not do and which used to be re-done here on whatever rows came back.
        entityType = 'Campaign';
        const targetKind = scope === 'portfolio' ? 'PORTFOLIO' : 'ASSET';
        const res = await k.queryInventory('Campaign', {
          filters: {
            targetKind,
            ...(portfolioId ? { portfolioId } : {}),
            ...(propertyId ? { propertyId } : {}),
          },
          limit,
          force: true,
          ttl: 0,
        });
        rowIds = res.ids.slice(0, limit);
        total = res.pagination?.total ?? res.ids.length;
        rows = readRows('Campaign', rowIds);
      } else if (kind === 'contacts') {
        if (!brandId) throw new Error('SOURCE_SHOW_IN_CHAT: contacts need a brandId.');
        entityType = 'Contact';
        const res = await k.queryInventory('Contact', { filters: { parentId: brandId }, limit, force: true, ttl: 0 });
        rowIds = res.ids;
        rows = readRows('Contact', rowIds);
      } else {
        // brand fiche
        if (!brandId) throw new Error('SOURCE_SHOW_IN_CHAT: a brand fiche needs a brandId.');
        entityType = 'Brand';
        if (!k.getInventory().Brand.registry[brandId]) await k.syncEntity?.('Brand', brandId);
        const b = k.getInventory().Brand.registry[brandId];
        if (!b) throw new Error('SOURCE_SHOW_IN_CHAT: brand not found.');
        rowIds = [brandId];
        rows = [b];
      }

      if (rows.length === 0) throw new Error(`SOURCE_SHOW_IN_CHAT: no ${kind} found for that selection.`);

      const data = buildSourceSlice(kind, scope, rows, columns);
      if (!data || (data.rows.length === 0 && kind !== 'brand')) {
        throw new Error(`SOURCE_SHOW_IN_CHAT: nothing to show for ${kind}.`);
      }
      // The full list opens in the Source tool: portfolio shortlist -> the Portfolio,
      // asset/unit shortlist -> the Property. A unit query only ever needs a campaignId (the unit
      // rows carry no property), so the asset is resolved from the campaign itself when the caller
      // did not pass one, which is the normal case: without this a unit card had no "open in
      // Source" target at all.
      const shortlistPropertyId =
        propertyId ||
        (kind === 'shortlist' && scope !== 'portfolio' && campaignId
          ? (resolveOpenSourceShortlist(k, campaignId)?.propertyId ?? '')
          : '');
      const navEntityType =
        kind === 'shortlist' && scope === 'portfolio' && portfolioId
          ? 'Portfolio'
          : kind === 'shortlist' && (scope === 'asset' || scope === 'unit') && shortlistPropertyId
            ? 'Property'
            : undefined;
      const navEntityId = navEntityType === 'Portfolio' ? portfolioId : navEntityType === 'Property' ? shortlistPropertyId : '';
      const marker = JSON.stringify({
        kind,
        scope,
        entityType,
        rowIds,
        columns,
        total: total || rows.length,
        navEntityType,
        navEntityId,
        title: titleRaw,
        data,
      });
      const content = `\`\`\`fater-source-table\n${marker}\n\`\`\``;
      await k.addPersistedAssistantMessage?.(content, 'Master', masterSessionId);
      return JSON.stringify({ kind, scope, shown: rows.length, total: total || rows.length });
    },
  },

  ADD_BRAND_STORES: {
    action_type: 'ADD_BRAND_STORES',
    traceLabels: { active: 'adding', done: 'added', unit: 'store', pluralUnit: 'stores' },
    targetEntityType: 'Brand',
    // NOT LOCKED ON FOCUS. A Brand-targeted command is stripped from the model's list unless a
    // brand is the subject or anchored, and /fill-stores is typed in a CAMPAIGN conversation where
    // the subject is the shortlist - so the command was invisible exactly where it is asked for
    // (reported 2026-08-23). The brand is resolved at execution time instead (explicit brandId,
    // else the brand open in Source), and the handler throws a sentence the agent can read.
    evaluateLockState: () => ({ isLocked: false, unlockHint: '' }),
    description:
      "Writes the stores a brand is MISSING into its store records (BrandLocation), read by YOU from the brand's own store locator or Google Maps via WEB_SEARCH. ADDITIVE ONLY: the server dedupes against the rows already held (by coordinates, then by address+city), never touches an existing row and never deletes; cleaning is /verify-stores. Pass the stores you extracted (address, city and ISO-2 country required; postalCode, latitude/longitude, name when the source gives them) and \"sourceUrl\", the page you read them from - it is stored on every row and the store reviewer verifies them from there, so the rows are UNVERIFIED until it passes and you say so in one clause. Batch up to 300 per call. This spends nothing. It fills the STORE ROWS, not the declared network size: when the locator total disagrees with declaredNetworkSize, also correct that field (MUTATE), and keep the two numbers labelled apart.",
    parameters: [
      { name: 'brandId', type: 'string', description: 'Id of the brand to fill, from QUERY BRAND. Always pass it: in a campaign conversation no brand is open, and a brand name is not accepted.', required: true },
      { name: 'sourceUrl', type: 'string', description: 'The URL the store list was read from (the locator page). Stored on every row.', required: true },
      { name: 'stores', type: 'object[]', description: 'The stores extracted from the source. Each: { address (required), city (required), country (ISO-2, required), postalCode?, name?, latitude?, longitude? }.', required: true },
    ],
    availableInModes: ['commander'],
    headlessCompatible: false,
    handler: async (params, kernel) => {
      assertUserStartedThisChain(kernel, 'ADD_BRAND_STORES');
      const k = kernel.current;
      if (!k) throw new Error('ADD_BRAND_STORES: Kernel not available.');
      const explicitBrandId = typeof params.brandId === 'string' ? params.brandId.trim() : '';
      const brandId = explicitBrandId || resolveSourceFocusedBrandId(k.getState?.());
      if (!brandId) {
        throw new Error('ADD_BRAND_STORES: no brand in scope. Pass "brandId", or open the brand in Source first.');
      }
      if (!Array.isArray(params.stores) || params.stores.length === 0) {
        throw new Error('ADD_BRAND_STORES: "stores" must be a non-empty array of extracted stores.');
      }
      markEnriching(k, brandId, ['stores'], true);
      const response = await fetch(`/api/inventory/brands/${brandId}/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceUrl: params.sourceUrl, stores: params.stores }),
      }).finally(() => markEnriching(k, brandId, ['stores'], false));
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(`ADD_BRAND_STORES failed: ${result?.message ?? response.status}${result?.errors ? ' ' + JSON.stringify(result.errors) : ''}`);
      }
      // The store map and counts on screen redraw from the registry, so pull the brand fresh.
      void k.syncEntity('Brand', brandId);
      return {
        ...result,
        tellTheUser: `${result.created} new store(s) recorded, ${result.skippedAsDuplicates} already held; the brand now carries ${result.held} store rows, the new ones pending verification.`,
      };
    },
  },

  DISCOVER_BRAND_CONTACTS: {
    action_type: 'DISCOVER_BRAND_CONTACTS',
    traceLabels: { active: 'discovering', done: 'discovered', unit: 'contact', pluralUnit: 'contacts' },
    targetEntityType: 'Brand',
    description:
      'Finds decision-maker contacts (Expansion, Real Estate, Country Manager, CEO) for a BRAND, via Lusha. BRAND-SCOPED: use it whenever the user asks who to contact at a brand. It defaults to the brand open in Source, so "get me the contacts for X" needs no id at all; pass "brandId" to target another, or "dealId" to take the brand from a deal. It BILLS per enriched contact, so run it ONCE and read the result rather than retrying. If it comes back empty, the usual cause is that the brand\'s stored website is a country site rather than the company: biotechusa.fr resolves to a 3-person shop while the parent has 2,000 staff. Say that plainly and suggest correcting the brand website; do not silently re-run.',
    // NOT LOCKED ON FOCUS, and this is the whole reason the command never fired. A locked command
    // does not merely warn: kernel/useAgents.ts rewrites its description to "[LOCKED] ..." and
    // strips its parameters to [], so the model cannot call it at all. This used to demand a
    // focused brand, and on a fresh page load nothing is focused - so the one tool that answers
    // "get me the contacts for X" was invisible at exactly the moment it was asked for, and the
    // turn came back with an empty script.
    //
    // The brand is resolved at EXECUTION time instead (explicit brandId, then a deal's brand, then
    // whatever is open in Source), and the handler throws a sentence the agent can read if none of
    // those work. An error the model can act on beats a tool it cannot see.
    parameters: [
      { name: 'brandId', type: 'string', description: 'Brand to find contacts for. Defaults to the brand open in Source.', required: false },
      { name: 'dealId', type: 'string', description: 'Optional. Take the brand from this deal instead.', required: false },
      { name: 'targetRoles', type: 'string[]', description: 'Optional roles to prioritise (e.g. ["Real Estate", "Expansion", "CEO"]).', required: false }
    ],
    availableInModes: ['commander'],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      const { dealId, targetRoles } = params;
      const k = kernel.current;
      if (!k) throw new Error('DISCOVER_BRAND_CONTACTS: Kernel not available.');
      // Before anything is resolved or opened: a refusal must cost nothing and must not move the
      // user's screen to a brand they never asked about.
      assertUserAskedForContacts(kernel, 'DISCOVER_BRAND_CONTACTS');

      // Same resolution order as the lock above, so a command that was OFFERED cannot then fail
      // on "which brand?". Explicit id wins, then a deal's brand, then whatever is open in Source.
      const explicitBrandId = typeof params.brandId === 'string' ? params.brandId.trim() : '';
      const brandFromDeal =
        typeof dealId === 'string' && dealId.trim()
          ? k.getInventory().Deal.registry[dealId.trim()]?.brandId || ''
          : '';
      const brandId = explicitBrandId || brandFromDeal || resolveSourceFocusedBrandId(k.getState?.());
      if (!brandId) {
        throw new Error(
          'DISCOVER_BRAND_CONTACTS: no brand in scope. Pass "brandId", or open the brand in Source first.',
        );
      }

      // OPEN THE THING BEFORE DOING THE WORK. The user asks, and the contacts tab of that brand
      // must come to the front immediately with its progress bar running, so the wait is visible
      // and obviously theirs. Doing this only on success meant the whole run - which takes the
      // better part of a minute - happened behind an unchanged screen, and the user reasonably
      // concluded nothing had happened at all.
      openBrandContactsView(kernel, brandId, true);

      toast.info('Discovering brand contacts...');

      try {
        const result = await k.discoverBrandContacts(brandId, {
          maxResults: 12,
          enrich: true,
          revealPersonalEmails: true,
          revealPhoneNumber: true,
          seniorities: ['director', 'head', 'vp', 'c_suite', 'founder', 'owner', 'partner'],
          // NO COUNTRY, deliberately. `person_locations` filters on where the PERSON lives, and
          // this was pinned to France: a brand worth sourcing is usually one that is NOT here yet,
          // so its expansion decision-makers are not here either. The server widens on its own
          // when a country search comes back empty; starting wide just saves a round trip.
          titles: Array.isArray(targetRoles) ? targetRoles : undefined
        });

        // SHOW WHAT WE JUST FOUND. Discovery writes Contact rows server-side, and the agent used
        // to stop there: the open Contacts tab kept rendering the list cached BEFORE the run
        // (usually empty), so the agent announced "14 contacts found" over "No results found" and
        // the user had to navigate away and back. These are the same three calls the Discover
        // button makes, in the same order: one refresh path, not two that drift. `force` matters
        // because the empty result is cached under this brand's signature and would be re-served.
        await k.syncEntity('Brand', brandId);
        await k.refreshInventory(
          'Contact',
          { parentId: brandId, parentType: 'brand' },
          undefined,
          { force: true },
        );

        toast.success('Contact discovery complete.');
        k.logUserAction?.('Dealer Discovered Contacts', { brandId, dealId: dealId ?? null });

        params.results = result;
      } catch (error: any) {
        throw new Error(`Contact discovery failed: ${error.message}`);
      } finally {
        // Clear in `finally`: a failed run must stop the spinner too, or the panel claims to be
        // working forever on a turn that already gave up. The tab selection stays put on purpose -
        // the user asked to see contacts, and that is still where they should be.
        k.setState({ sourceContactDiscoveryBrandId: null });
      }
    }
  },

  ENRICH_BRAND: {
    action_type: 'ENRICH_BRAND',
    traceLabels: { active: 'enriching', done: 'enriched', unit: 'brand' },
    targetEntityType: 'Brand',
    description: `Enrich a single brand's data on demand, fills gaps in website, network size, positioning, CNCC category, typical store surface, logo, financials, contacts, and legal entities from web research / the French SIREN registry / Apollo-Lusha, persisting live (no proposal gate; the user's request is the approval).
    Use this whenever the user asks you to enrich, look up, complete, or find data for a brand. Pass a single "dimension" to enrich just one aspect, or omit it to fill all gaps.
    The result reports the brand's post-enrich state so you can explain what filled and what came back empty (and why, see the Brand Data Enrichment knowledge).`,
    parameters: [
      { name: 'brandId', type: 'string', description: 'The brand to enrich. If omitted, the brand currently open in the Source editor is used.', required: false },
      { name: 'logoUrl', type: 'string', description: "With dimension 'logo': a DIRECT image URL you actually found (png/jpg/svg, e.g. off the brand's press kit). It is DOWNLOADED and stored, so a link that does not fetch is refused rather than written. Use it whenever you have found the real logo, including when the provider search came back empty. Do not pass a page that merely contains an image, and never compose a provider URL from a domain.", required: false },
      { name: 'siren', type: 'string', description: "With dimension 'financials': the SIREN of the brand's French head-of-network or operating company, when your research identified it and the footprint anchor has not (franchise networks especially). The server checks the register's OWN record ties it to the brand (declared enseigne or matching denomination) and reads the filed figures from the public structured sources itself; a SIREN the register does not corroborate writes nothing. If it answers 'no-public-ca', the company files with its revenue confidential in the public extracts - say that plainly instead of retrying.", required: false },
      { name: 'cik', type: 'string', description: "With dimension 'financials': the SEC CIK of the brand's LISTED parent, when your research found one (the brand's own investor-relations page names it). The server reads the audited 10-K revenue series from EDGAR's structured API itself and refuses a registrant whose name does not match the brand - never pass a CIK you have not seen tied to this brand. This is how a brand whose French arm files confidentially still earns a verified revenue chart.", required: false },
      { name: 'dimension', type: 'string', description: "Optional single aspect: website | network | positioning | category | surface | logo | contacts | financials | legal | stores. Omit to enrich all gaps. category = CNCC sector; surface = typical store surface (m²). 'stores' is the odd one out and does NOT fetch anything: it VERIFIES the stores already claimed for this brand against the brand's own store locator, Google Maps and OpenStreetMap, and DELETES the ones proven not to exist (snapshotted first, so a wrong call is recoverable). Many of those rows were written by a model with no existence check, so a store in our data is a claim until this runs. It returns { checked, notFound, deleted, skipped }. Use it whenever the user doubts a store or an address, and ALWAYS before using a brand's store network as an argument about proximity, cannibalisation or expansion.", required: false },
    ],
    availableInModes: ['commander'],
    handler: async (params, kernel, options) => {
      assertUserStartedThisChain(kernel, 'ENRICH_BRAND');
      const explicitBrandId =
        typeof params.brandId === 'string' && params.brandId.trim().length > 0 ? params.brandId.trim() : null;
      let resolvedBrandId = explicitBrandId;
      if (!resolvedBrandId) {
        try {
          // Was PortfolioFit-only, so on an ASSET shortlist, where the rows are Leads, the
          // common case, it found nothing and refused while a brand was plainly open. The
          // shared resolver checks all three registries, and it is the same one the composer's
          // "Selected: <brand>" chip reads, so the chip cannot promise a subject this refuses.
          resolvedBrandId = resolveSourceFocusedBrandId(kernel.current?.getState?.());
        } catch { /* fall through to the error below */ }
      }
      if (!resolvedBrandId) {
        throw new Error('ENRICH_BRAND: "brandId" is required unless a brand is open in the Source editor.');
      }
      const dimension =
        typeof params.dimension === 'string' && params.dimension.trim().length > 0 ? params.dimension.trim() : undefined;
      // Snapshot the before-state so the note shows what actually changed.
      let before: EnrichBrandLike | undefined;
      try {
        const state: any = (kernel.current as any)?.getState?.();
        before = state?.inventory?.Brand?.registry?.[resolvedBrandId];
      } catch { /* ignore */ }
      const sourceLogoUrl =
        typeof params.logoUrl === 'string' && params.logoUrl.trim().length > 0 ? params.logoUrl.trim() : undefined;
      const sourceCik =
        typeof params.cik === 'string' && params.cik.trim().length > 0 ? params.cik.trim() : undefined;
      const sourceSiren =
        typeof params.siren === 'string' && params.siren.trim().length > 0 ? params.siren.trim() : undefined;
      const result: any = await kernel.current?.enrichBrand(resolvedBrandId, dimension, sourceLogoUrl, sourceCik, sourceSiren);
      const after: EnrichBrandLike | undefined = result?.brand;
      // Document the outcome in the left chat, the same note the right-bar panel posts ,
      // so the agent (and user) can see and explain what filled and what came back empty.
      const dims = dimension ? [dimension] : [...ENRICH_DIMS];
      // The store verification's own numbers, which are the ANSWER to /verify-stores. Without
      // them the note fell back to diffing the store count, and an unchanged count reads as
      // "already up to date" - so the agent was told nothing had happened and reported that to
      // the user while ten stores had just been checked. Measured on Kiabi, 2026-08-19.
      // Returned to the agent as the command's result, never posted in the chat: the conversation
      // carries only what the agent itself says (owner, 2026-09-26).
      params.result = {
        summary: buildEnrichNote(after?.name || before?.name || 'Brand', before, after, dims, 'en', {
          stores: result?.stores,
          storesTotal: result?.storesTotal,
        }),
      };
    },
  },
  /**
   * THE PORTFOLIO-WIDE PASS, which the agent could not start at all.
   *
   * `LAUNCH_SOURCING_CAMPAIGN` is an ASSET run: it scores brands against one property's catchment
   * and rubric. A portfolio pass is a different engine (PortfolioFitCampaign) answering a different
   * question - which brands suit this landlord's estate at all - and it builds the reference
   * shortlist every later asset run narrows. The right bar could start one; chat could not, so
   * "run a portfolio-wide sourcing on Altarea, 80 credits" was answerable only with instructions
   * for clicking the button.
   */
  /**
   * DELETING A SHORTLIST IS THE ONE MANUAL ACTION THAT DESTROYS PAID WORK.
   *
   * The dashboard gates it behind a two-step menu that names the count: "Permanently delete this
   * campaign and its 80 results?". There is no equivalent gate for a command - the approval card
   * was removed and MUTATE now writes straight through - so the gate is built into the contract
   * instead: the campaign must be named (never "the open one"), and the caller must state how many
   * results it is destroying, which is checked against the real count before anything is deleted.
   *
   * That is not a formality. It means the agent has to READ the campaign before it can destroy it,
   * a number invented from context fails closed, and the user's own sentence ("delete the run with
   * the 80 brands") is what the check is made against.
   */
  DELETE_SOURCING_CAMPAIGN: {
    action_type: 'DELETE_SOURCING_CAMPAIGN',
    traceLabels: { active: 'deleting', done: 'deleted', unit: 'campaign' },
    targetEntityType: 'SourcingCampaign',
    description: `PERMANENTLY deletes a sourcing campaign AND every lead it produced. Irreversible,
    and the credits that paid for those analyses are gone with them - this is the delete in the
    dashboard's campaign menu, not an archive and not a stop. To halt a run that is still going use
    STOP_SOURCING_CAMPAIGN, which keeps everything already scored.
    ONLY on an explicit, unambiguous instruction to delete THAT campaign. "Clean this up", "I don't
    need these", "remove that from my list" are not it: ask which campaign and confirm they mean
    deletion rather than stopping or dismissing rows. Never delete to tidy up after yourself, never
    delete a campaign the user has not named, and never chain it after another command.
    You must pass "expectedLeadCount": the number of leads the campaign holds, which you have to
    READ first (QUERY the campaign, or its leadIds). If it does not match the real count the delete
    is refused - that guard exists because a wrong id destroys someone's work, so a number you
    inferred rather than read will and should fail.`,
    parameters: [
      { name: 'campaignId', type: 'string', description: 'The campaign to delete. REQUIRED and never defaulted: the shortlist that happens to be open is not consent to destroy it.', required: true },
      { name: 'expectedLeadCount', type: 'number', description: 'How many leads this campaign holds, read from the campaign before calling. Must match, or the delete is refused.', required: true },
    ],
    availableInModes: ['commander'],
    handler: async (params, kernel) => {
      const k = kernel.current;
      if (!k) throw new Error('DELETE_SOURCING_CAMPAIGN: Kernel not available.');

      const campaignId = typeof params.campaignId === 'string' ? params.campaignId.trim() : '';
      if (!campaignId) {
        throw new Error('DELETE_SOURCING_CAMPAIGN: "campaignId" is required. Name the campaign to delete; the one on screen is not consent.');
      }

      // Read the truth at the moment of deleting, rather than demanding the client happen to hold
      // it. Requiring a pre-loaded registry entry made the command unusable: the agent queried,
      // the entry still was not there the way this check wanted it, and the delete refused in a
      // loop. Cache state is not a safety property; the COUNT MATCHING REALITY is, and syncing
      // first makes that check stricter, not weaker.
      // Synced under the UNIFIED name, because that is the table, and read through the shared
      // reader. The count comes from the payload's own `totalResultCount` rather than the length of
      // a `leadIds` array, which only the legacy payload carried: keeping the array as the source
      // would have tied this guard to the older route surviving, and an absent array reads as zero,
      // so the guard would have started refusing every deletion the day hydration moved.
      await k.syncEntity('Campaign', campaignId);
      const campaign = readCampaignRow(k.getInventory(), campaignId) as
        | { leadIds?: unknown; totalResultCount?: unknown; resultCount?: unknown; name?: string }
        | undefined;
      if (!campaign) {
        throw new Error(`DELETE_SOURCING_CAMPAIGN: campaign "${campaignId}" does not exist, or is not visible to this account. Nothing was deleted.`);
      }

      const countedResults = [campaign.totalResultCount, campaign.resultCount]
        .find((value): value is number => typeof value === 'number' && Number.isFinite(value));
      const actualLeadCount = countedResults ?? (Array.isArray(campaign.leadIds) ? campaign.leadIds.length : 0);
      const claimed = Number(params.expectedLeadCount);
      if (!Number.isFinite(claimed)) {
        throw new Error('DELETE_SOURCING_CAMPAIGN: "expectedLeadCount" is required, and must be read from the campaign.');
      }
      if (Math.floor(claimed) !== actualLeadCount) {
        throw new Error(
          `DELETE_SOURCING_CAMPAIGN: refused. You said this campaign holds ${Math.floor(claimed)} leads; it holds ${actualLeadCount}. ` +
          'Re-read the campaign and confirm with the user that this is the one they mean before trying again.',
        );
      }

      await k.destroySourceCampaign('SourcingCampaign', campaignId);
      params.result = { campaignId, deletedLeadCount: actualLeadCount, irreversible: true };
      k.logUserAction?.('Agent Deleted Sourcing Campaign', { campaignId, deletedLeadCount: actualLeadCount });
    }
  },


  LAUNCH_SOURCING_CAMPAIGN: {
    action_type: 'LAUNCH_SOURCING_CAMPAIGN',
    traceLabels: { active: 'launching', done: 'launched', unit: 'campaign' },
    targetEntityType: 'Property',
    description: `Triggers a massive asset-level sourcing run based on the property's current strategy (rubric and exclusions).
    This creates a SourcingCampaign record and starts the agentic worker to find and score Leads.
    ONLY for a shortlist that does not exist yet. If the user is READING a shortlist and asks for more brands on it
    ("analyse 10 more brands", "add more candidates", "widen this list", "find me more"), that is EXTEND_SHORTLIST and
    NOT this: launching again creates a SECOND list, scored in its own pass, which leaves their existing rows untouched
    and cannot be compared with them. Do not stop the open campaign in order to relaunch it either - extend it.
    It consumes credits according to analysis cost; the actual number of brands varies.`,
    parameters: [
      { name: 'propertyId', type: 'string', description: 'The property to scan.', required: true },
      { name: 'maxBudgetCredits', type: 'number', description: 'Optional spending ceiling in credits; actual brands analysed vary with analysis cost (observed reference: about 29 brands per 50 credits).', required: false },
      { name: 'countryFilter', type: 'string', description: 'Optional ISO country code (e.g. "FR", "US", "GB") to restrict candidate brands to a single country. Omit to consider all countries.', required: false },
      { name: 'unitId', type: 'string', description: 'A UNIT FIT: score brands for this one unit of the asset (read its brief first with DIAGNOSE_ASSET_FOR_SOURCING unitId). The run then reads the unit, its neighbours and its history beside the asset. Defaults to the unit in focus, if any; omit for an asset-wide run.', required: false },
      { name: 'unitHypothesis', type: 'string', description: 'With unitId: one or two sentences on what belongs in this unit and why, drafted from its brief and confirmed by the user. Carried by the run and read by the Judge.', required: false },
    ],
    availableInModes: ['commander'],
    handler: async (params, kernel, options) => {
      // A run spends credits according to analysis cost. Refuse unless the USER asked for one this turn.
      const sessionId = options.sessionId || kernel.current?.getActiveChatSessionId() || null;
      const unitId = (typeof params.unitId === 'string' && params.unitId.trim()) || resolveUnitIdFromSessionContext(kernel, sessionId);
      const unitHypothesis = typeof params.unitHypothesis === 'string' && params.unitHypothesis.trim() ? params.unitHypothesis.trim() : null;
      const explicitPropertyId =
        typeof params.propertyId === 'string' && params.propertyId.trim().length > 0
          ? params.propertyId.trim()
          : null;
      const resolvedPropertyId = explicitPropertyId || resolvePropertyIdFromSessionContext(kernel, sessionId);

      if (!resolvedPropertyId) {
        throw new Error('LAUNCH_SOURCING_CAMPAIGN: "propertyId" is required unless a Property/Unit/Deal is currently focused.');
      }

      const rawMaxBudgetCredits = params.maxBudgetCredits;
      let resolvedMaxBudgetCredits: number | undefined;
      if (rawMaxBudgetCredits !== undefined && rawMaxBudgetCredits !== null) {
        const parsedBudget =
          typeof rawMaxBudgetCredits === 'number'
            ? rawMaxBudgetCredits
            : typeof rawMaxBudgetCredits === 'string' && rawMaxBudgetCredits.trim().length > 0
              ? Number(rawMaxBudgetCredits.trim())
              : NaN;

        if (!Number.isFinite(parsedBudget) || parsedBudget <= 0) {
          throw new Error('LAUNCH_SOURCING_CAMPAIGN: "maxBudgetCredits" must be a positive number when provided.');
        }
        resolvedMaxBudgetCredits = Math.max(1, Math.floor(parsedBudget));
      }

      // Asked for in this turn, or paid by the user's mandate: a launch under a mandate must name its
      // budget, since the budget is what the mandate counts.
      payFromMandateOr(kernel, sessionId, { propertyId: resolvedPropertyId, credits: resolvedMaxBudgetCredits ?? Number.NaN }, () =>
        assertUserAskedToLaunch(kernel, 'LAUNCH_SOURCING_CAMPAIGN'));

      // Optional single-country restriction (ISO code, e.g. "FR"). Mirrors the
      // Source Center asset launcher, which passes countryFilter through the launch
      // payload; the server sanitizes it (trim + non-empty) before use.
      const rawCountryFilter = params.countryFilter;
      const resolvedCountryFilter =
        typeof rawCountryFilter === 'string' && rawCountryFilter.trim().length > 0
          ? rawCountryFilter.trim().toUpperCase()
          : undefined;

      params.propertyId = resolvedPropertyId;
      if (resolvedMaxBudgetCredits !== undefined) {
        params.maxBudgetCredits = resolvedMaxBudgetCredits;
        kernel.current?.patchEntityRuntimeState('Property', resolvedPropertyId, {
          sourcingBudgetCreditsDraft: resolvedMaxBudgetCredits,
        });
      } else {
        delete params.maxBudgetCredits;
      }
      if (resolvedCountryFilter !== undefined) {
        params.countryFilter = resolvedCountryFilter;
      } else {
        delete params.countryFilter;
      }

      // Forward the resolved params explicitly rather than relying on the kernel
      // draft round-trip (which can be clobbered between the proposal and its
      // approval, silently letting the run scan the full candidate pool). When a
      // value was not requested we omit it, preserving server defaults.
      // The agent is woken when its own run lands, as it is for a launch from the form: that turn is
      // where it judges the list and, under a mandate, decides the next step (2026-09-28).
      const approvalPayload: Record<string, unknown> = { recallCommanderOnComplete: true, ...(unitId ? { unitId } : {}), ...(unitId && unitHypothesis ? { unitHypothesis } : {}) };
      if (resolvedMaxBudgetCredits !== undefined) approvalPayload.maxBudgetCredits = resolvedMaxBudgetCredits;
      if (resolvedCountryFilter !== undefined) approvalPayload.countryFilter = resolvedCountryFilter;
      // Under a mandate the SERVER drives every pass after this one, tab open or not: the mandate
      // travels with the launch (already committed for it) and the landing no longer wakes this tab.
      const mandate = sessionId ? kernel.current?.getState().sourceMandates?.[sessionId] : undefined;
      if (mandate && sessionId && Date.parse(mandate.expiresAt) > Date.now()) {
        approvalPayload.mandate = {
          goal: mandate.goal,
          targetScore: mandate.targetScore ?? MANDATE_DEFAULT_TARGET_SCORE,
          creditCeiling: mandate.creditCeiling,
          committedCredits: mandate.committedCredits,
          expiresAt: mandate.expiresAt,
          chatSessionId: sessionId,
        };
        approvalPayload.recallCommanderOnComplete = false;
      }
      await kernel.current?.launchSourcingCampaign(
        resolvedPropertyId,
        { payload: approvalPayload },
      );
      // The launch succeeded, so the server holds the mandate now; a second copy here would let
      // this tab act on the same landing as the driver.
      if (approvalPayload.mandate && sessionId) {
        const { [sessionId]: _handedOver, ...rest } = kernel.current?.getState().sourceMandates ?? {};
        kernel.current?.setState({ sourceMandates: rest });
        params.result = { ...(params.result as object | undefined), mandate: 'handed to the server: every landing is read and extended there, and its messages appear in this conversation' };
      }
    }
  },

  /**
   * ADD BRANDS TO THE LIST ON SCREEN, rather than starting a second one.
   *
   * "Analyse 10 more brands" is the single most natural follow-up to reading a shortlist, and the
   * system used to answer it by LAUNCHING A NEW CAMPAIGN: a second list, scored on its own pass,
   * that cannot be compared with the first and leaves the user's ten rows untouched. This is a
   * separate command rather than a flag on the launch because command SELECTION is where the agent
   * actually fails - a distinctly named tool is picked reliably, a boolean buried in another
   * command's parameters is not.
   */
  EXTEND_SHORTLIST: {
    action_type: 'EXTEND_SHORTLIST',
    // Unit is the SHORTLIST, not the brand: the trace counts commands, so 'brand' made a single
    // EXTEND read as "Added 1 brand" when it had just queued ten. `progressUnit` is the answer to
    // the same problem from the other side - while the run is alive the trace reads its real count
    // off the campaign ("extending 3 of 5 brands") and falls back to this static line once it ends.
    traceLabels: { active: 'extending', done: 'extended', unit: 'shortlist', pluralUnit: 'shortlists', progressUnit: 'brand' },
    targetEntityType: 'SourcingCampaign',
    description:
      'Adds MORE brands to the shortlist already on screen, scored by the SAME doctrine and the same rubric so every row stays comparable. Use this for "analyse 10 more brands", "add more candidates", "widen this list", "find me more" - anything that asks the CURRENT list to grow. It does NOT start a second shortlist: the new rows land in the same table under the same campaign. Never use LAUNCH_SOURCING_CAMPAIGN for a follow-up like that; a new campaign gives a separate list with its own scoring pass and is almost never what the user meant. It BILLS according to analysis cost, so pass the number the user asked for and no more. The shortlist must have finished running.',
    parameters: [
      { name: 'brandCount', type: 'number', description: 'How many NEW brands to add. Defaults to 10.', required: false },
      { name: 'campaignId', type: 'string', description: 'Shortlist to extend. Defaults to the one open in Source.', required: false },
      { name: 'sectors', type: 'string', description: 'CNCC sector letters to restrict the NEW brands to, comma separated, when the user names a category: A Alimentation, B Equipement de la personne, C Equipement de la maison, D Culture Cadeaux Loisirs, E Hygiene Beaute Sante, F Services, G Restauration. "F&B", "food", "restauration" is G (and A for grocery). Omit when they did not ask for a category. The rubric is unchanged either way; this only narrows which brands are considered.', required: false },
      { name: 'categories', type: 'string', description: 'Comma separated CNCC secteurs to restrict the NEW brands to, when the user names a CATEGORY rather than a famille ("fitness", "sneakers", "opticien"). These are the fine categories of the CNCC referential in your knowledge base; use their exact wording. Prefer this over "sectors" whenever the user names something narrower than a famille - asking for fitness and getting a hypermarket is what happens when only the famille can be expressed.', required: false },
      { name: 'reason', type: 'string', description: 'What the user is looking for in these new brands, in their own words ("one that could beat FitnessBoutique"). You are woken when the run lands and must answer THIS, so write it down now: the landing turn does not see the message that started the extend.', required: false },
      { name: 'brandIds', type: 'string', description: 'Comma separated Brand ids to add, when the user NAMES the brands they want ("add LCL", "put Normal and Action on it"). Resolve each name first with QUERY on BRAND using { "name": { "$contains": "...", "$mode": "insensitive" } } and pass the ids, never the names. These brands are analysed whatever the asset\'s standing filters and whatever was already evaluated, because naming a brand is a constraint and not a hint; without this the run picks from the pool instead and spends credits on a brand the user never asked for. The budget follows the list, while actual spend varies with analysis cost. If a name resolves to nothing, say so and add nothing: there is no substitute for the brand they asked for. A brand already on the shortlist is refused, since re-scoring a row that is there cannot make the list grow.', required: false },
    ],
    availableInModes: ['commander'],
    headlessCompatible: false,
    handler: async (params, kernel, options) => {
      const k = kernel.current;
      if (!k) throw new Error('EXTEND_SHORTLIST: Kernel not available.');

      const explicit = typeof params.campaignId === 'string' ? params.campaignId.trim() : '';
      const state = k.getState?.();
      const campaignId =
        explicit || (typeof state?.sourceSelectedCampaignId === 'string' ? state.sourceSelectedCampaignId.trim() : '');
      if (!campaignId) {
        throw new Error('EXTEND_SHORTLIST: no shortlist in scope. Open one in Source, or pass "campaignId".');
      }

      // The asset comes from the campaign itself, not from the session focus: the run must extend
      // the list the user is reading, whatever else they happen to have selected since.
      const campaignRow = readCampaignRow(k.getInventory(), campaignId);
      const propertyId = resolveCampaignPropertyId(campaignRow);
      if (!propertyId) {
        throw new Error(`EXTEND_SHORTLIST: shortlist ${campaignId} is not loaded. QUERY it first.`);
      }

      // A CATEGORY THE USER NAMED IS A CONSTRAINT, NOT A HINT. Extending reuses the campaign's
      // filters so the rows stay comparable, which left "add another one, F&B this time" with
      // nowhere to go: it was dropped in silence and a credit was spent on a brand from the wrong
      // sector. Reported 2026-08-16. Unknown letters are dropped HERE as well as at the route, so a
      // typo narrows nothing rather than narrowing to nothing.
      // Categories are free text from the referential, so they are validated at the route, not
      // guessed at here; empty entries are dropped so a trailing comma narrows nothing.
      const categories = String(params.categories ?? '')
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);

      const sectors = String(params.sectors ?? '')
        .split(',')
        .map((entry) => entry.trim().toUpperCase())
        .filter((entry): entry is CNCCSector => Object.keys(CNCC_SECTORS).includes(entry));

      // NAMED BRANDS ARE THE WHOLE REQUEST. "add LCL" used to arrive as "add one brand" and the run
      // took the first candidate of the pool, so the agent announced LCL and the list grew by a
      // brand nobody asked for (measured 2026-08-17). Ids only: names are resolved by the agent with
      // a QUERY, because matching a name to a record is a judgement (LCL the bank, not LCL Sport)
      // and doing it inside a launch would spend a credit on a guess.
      const brandIds = String(params.brandIds ?? '')
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);

      const requested = Number(params.brandCount);
      // The budget follows the named list when there is one: asking for two brands and defaulting to
      // ten would bill eight brands the user never mentioned.
      const brandCount =
        brandIds.length > 0
          ? brandIds.length
          : Number.isFinite(requested) && requested > 0
            ? Math.floor(requested)
            : 10;
      // Typed by the user in this chain, or paid by their mandate (lib/leasing/sourcingMandate.ts).
      const sessionId = options.sessionId || k.getActiveChatSessionId() || null;
      const approved = campaignRow?.config?.mandate as ServerSourcingMandate | undefined;
      const serverPays = !!approved?.budgetProposalId && approved.chatSessionId === sessionId
        && approved.userId === k.getUserContext().dbUserId;
      // A saved campaign approval survives reload. Its server reserves the pass atomically;
      // browser-local mandates must not debit this same extension a second time.
      if (!serverPays) payFromMandateOr(kernel, sessionId, { propertyId, credits: creditsForBrands(brandCount, true) }, () =>
        assertUserStartedThisChain(kernel, 'EXTEND_SHORTLIST'));
      // How many rows the list has right now, so the agent can quote the before and after.
      const leadRegistry = k.getInventory().Lead.registry as Record<
        string,
        { lastCampaignId?: string; campaignIds?: string[] }
      >;
      const existingCount = Object.values(leadRegistry).filter((lead) =>
        leadBelongsToCampaign(lead, campaignId),
      ).length;

      const reason = String(params.reason ?? '').trim();
      if (serverPays && !campaignRow?.propertyIds?.length) await k.syncEntity('Campaign', campaignId, { force: true });
      const approvedPropertyIds = serverPays ? readCampaignRow(k.getInventory(), campaignId)?.propertyIds : undefined;
      if (serverPays && !approvedPropertyIds?.length) throw new Error('Reload this campaign before using its approved allowance.');
      await k.launchSourcingCampaign(serverPays ? approvedPropertyIds! : propertyId, {
        // COME BACK AND SAY WHAT ARRIVED. Without this the extend ends at "Adding 1 brand" and the
        // user never hears whether the brand they asked for - "one that could beat FitnessBoutique"
        // - actually did. The flag is what the Source view reads to wake the agent when the run
        // lands, and it is the same one the launch button sets.
        payload: {
          extendCampaignId: campaignId,
          // A count of brands, not of credits: the route prices it (2026-09-27).
          brandCount,
          ...(reason ? { reason } : {}),
          recallCommanderOnComplete: !serverPays,
          ...(sectors.length > 0 ? { cnccSectorFilter: sectors } : {}),
          ...(categories.length > 0 ? { cnccActivityFilter: categories } : {}),
          ...(brandIds.length > 0 ? { targetBrandIds: brandIds } : {}),
        },
      });

      // Land the user back on the list that is about to grow.
      k.setState({ sourceSelectedCampaignId: campaignId });
      k.logUserAction?.('Dealer Extended Shortlist', { campaignId, brandCount });

      // WHAT THE AGENT HAS TO BE ABLE TO SAY. The rows do not all appear at once: the run scores
      // the new brands one by one and the table only reveals a row once it is fully scored, so for
      // a minute the list looks unchanged and the user reasonably concludes nothing happened. The
      // result therefore carries the count and the fact that arrival is progressive, so the agent
      // can promise it out loud instead of going quiet.
      // The names, so the landing turn can conclude about the brand the user asked for rather than
      // about "1 brand". The registry holds them because the agent had to QUERY the brand to get its
      // id in the first place; an id that is somehow not loaded rides along as itself, which still
      // beats saying nothing.
      const brandRegistry = k.getInventory().Brand.registry as Record<string, { name?: string }>;
      const namedBrands = brandIds.map((brandId) => brandRegistry[brandId]?.name?.trim() || brandId);

      params.result = {
        campaignId,
        brandsRequested: brandCount,
        shortlistWas: existingCount,
        shortlistWillBe: existingCount + brandCount,
        arrival: 'progressive',
        restrictedToSectors: sectors.length > 0 ? sectors : undefined,
        restrictedToCategories: categories.length > 0 ? categories : undefined,
        brandsNamed: namedBrands.length > 0 ? namedBrands : undefined,
        tellTheUser:
          namedBrands.length > 0
            ? `Adding ${namedBrands.join(', ')} to this shortlist. Scored on the same rubric as the rows already there, and the row appears once the analysis finishes, a minute or two.`
            : `Adding ${brandCount} brands to this shortlist (${existingCount} -> about ${existingCount + brandCount}). ` +
              'They are scored one at a time on the same rubric and each row appears as it finishes, so the list fills in over the next minute or two rather than all at once.',
        // Not now: the rows do not exist yet. When the run reports back, the list is a different
        // list and needs a fresh verdict - and specifically a verdict on WHAT WAS ADDED, because
        // that is what was asked for. A generic re-ranking answers a question nobody put.
        whyTheyWereAskedFor: typeof params.reason === 'string' ? params.reason : undefined,
        nextStep:
          'When the run lands you will be woken to speak. Name the brands that ARRIVED, with their '
          + 'scores, and say plainly whether they answer what was asked - if the user wanted one that '
          + 'could beat a named brand, compare the two and say yes or no. '
          // A NAMED BRAND ALWAYS GETS A CONCLUSION, AND A DISMISSAL IS ONE. Measured 2026-08-17:
          // "add Nike" analysed Nike, scored it 55, dismissed it, and the shortlist hides dismissed
          // rows - so the user saw an unchanged list, no word from the agent, and reasonably
          // concluded the feature was broken. The run had worked perfectly. What was missing was the
          // sentence saying so.
          + (namedBrands.length > 0
            ? `CONCLUDE ON ${namedBrands.join(', ')} BY NAME, whatever the verdict. QUERY the leads of `
              + 'this campaign to read the score and the status of each one, then say in one or two '
              + 'sentences what the analysis decided and why. A DISMISSED verdict is a real answer and '
              + 'must be said out loud, with the reason and the score: the row is hidden behind the '
              + '"show dismissed" control, so silence looks exactly like nothing having happened. If a '
              + 'brand is missing from the list entirely, say that instead of guessing. Then: '
            : 'Then: ')
          + SHORTLIST_VERDICT_NEXT_STEP,
      };
    }
  },

};
